import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import {
  AnchoredPanel,
  Badge,
  Breadcrumbs,
  BulkActionBar,
  Button,
  IconButton,
  Callout,
  Checkbox,
  ColorInput,
  CopyField,
  DateInput,
  DateTimeInput,
  EmptyState,
  FilterBar,
  Form,
  FormError,
  FormField,
  FormSubmit,
  Input,
  MediaFrame,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelect,
  OptionChip,
  OptionCard,
  OrderedListField,
  PinnableOrderList,
  RadioGroup,
  RangeInput,
  Rating,
  Select,
  SegmentedControl,
  Skeleton,
  Switch,
  Tabs,
  Table,
  PhotoInput,
  FileButton,
  ProgressMeter,
  ReportInlinePhotoGrid,
  ReportLetterhead,
  ReportPageBadge,
  ReportTableOfContents,
  SlotGridPicker,
  DirectionalPad,
  findAdjacentSlot,
  TaskCard,
  CompactRow,
  CompactRowList,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
  PlanTeaser,
  QuotaTeaser,
  CommandPalette,
  RouteDenied,
  DynamicFormField,
  useZodForm,
  PageHeader,
  RailPanel,
  OverflowMenu,
  MissingValue,
  DataList,
  Tooltip,
  VersionPanel,
  StatusChip,
  StatusStepper,
  SaveStateIndicator,
  AgentPlanCard,
  TeamTree,
  HeaderActions,
  CardActions,
  RowActions,
  ToolbarActions,
  FormActions,
  DialogActions,
  ChoiceGroup,
  Container,
  Stack,
  Inline,
  Cluster,
  Grid,
  Split,
  // Governed header components (story 2-147sb, P10 §2.6 header anatomy
  // kinds #3/#4/#5) — PageHeader (kind #2) is already imported above.
  WorkspaceHeader,
  ContextHeader,
  SectionHeader,
  // Executable page layouts (story 2-147sb, P10 §2.6) — replace 2-147f's
  // six documented-only recipes with real, importable components.
  DashboardLayout,
  CollectionLayout,
  WorkflowLayout,
  WorkspaceLayout,
  AdministrationLayout,
  ExternalLayout,
  // Aliased — this file already has its own pre-2-147p local `Section()`
  // gallery helper (line ~1726, the ad hoc `title`/`hint`/`children` shape
  // `GallerySection.tsx`'s own header comment describes formalizing but not
  // yet migrating, 57 call sites still use it) which collides on the bare
  // name. `LayoutSection` here is `components/ui/Layout.tsx`'s real
  // product-layout primitive — a different thing entirely.
  Section as LayoutSection,
  // Surface and card compositions (story 2-147ta, P10 §2.7 row 5) — see
  // this file's own "Surface and card compositions" GallerySection for the
  // full worked demonstration.
  Surface,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  MetricCard,
  EntityCard,
  // Collection compositions (story 2-147tb, P10 §2.7 row 7) — see this
  // file's own "Collection compositions" GallerySection for the full
  // worked demonstration.
  LoadMorePager,
} from '../components/ui';
import type { ActionDescriptor, FormActionDescriptor, PinnableOrderListItem, TeamTreeNode } from '../components/ui';
// Layout-tier shell primitive (story 2-127) — lives in components/layout
// (not components/ui) because it owns the shell's outer rail/canvas/panel
// structure, the same split AppShell.tsx already follows.
import { AppFrame } from '../components/layout/AppFrame';
// Real, presentational (no query/domain import) dashboard-family component —
// story 2-147f's Dashboard layout recipe demo reuses it rather than
// approximating a knockoff, same "real primitive, not a copy" rule the
// AppFrame demo already follows for RailPanel/PageHeader.
import { KpiCard } from '../components/dashboard/KpiCard';
// Gallery scaffolding (story 2-147p) — the gallery's OWN presentation
// layer, not a product primitive: lives in components/gallery/, not
// components/ui/ (see GalleryPage.tsx's own header comment for why).
import {
  GalleryPage,
  GallerySection,
  ExampleFrame,
  VariantGrid,
  StateMatrix,
  ResponsivePreview,
  LAYOUT_FAMILY_STATE_SET,
} from '../components/gallery';
import {
  PlusIcon,
  ArrowRightIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  ArchiveBoxIcon,
  ListBulletIcon,
  Squares2X2Icon,
  SparklesIcon,
  SquaresPlusIcon,
  XMarkIcon,
  Bars3Icon,
  PowerIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { CONTENT_STRESS_CASES, MISSING_VALUE_FIELDS } from '../lib/contentStressMatrix';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import { FlagGate } from '../components/access/FlagGate';
import { CreateFeatureRequestBodySchema } from '../lib/api/featureRequests.gen';
import { useVocab, resolveVocab, SITES_REGISTRY_LABEL, type VocabRegistry } from '../lib/vocab';
import { listProperties, propertiesKeys, PropertySchema, type Property } from '../lib/api/properties.gen';
import { useCursorList } from '../lib/query/useCursorList';
import { attributesSchemaFor, attributeDefaults } from '../lib/domainForm';
import type { DomainField } from '../lib/api/domains.gen';
// The reports module's public export (story 4-78) — same barrel seam the
// template editor uses, not a reach into `modules/reports/components/*`.
import { ReportRender } from '../modules/reports';
import type { ReportContent } from '../lib/api/reports.gen';
import { DEFAULT_LETTERHEAD, DEFAULT_REPORT_LAYOUT, DEFAULT_SECTION_PRESENTATION, REPORT_FONTS, SECTION_UNDERLINE_WIDTHS, SURFACE_RADII } from '../lib/agentShared/formSchema';
import type { LetterheadSlotKey } from '../lib/agentShared/formSchema';
import {
  LETTERHEAD_GRID_CELLS,
  LETTERHEAD_GRID_ROWS,
  LETTERHEAD_GRID_COLS,
  LETTERHEAD_SLOT_LABELS,
  relocateLetterheadSlot,
} from '../components/formBuilder/letterheadGrid';

// ------------------------------------------------------------------
// /app/design — the design-system gallery (D44, P10 §4).
// Every primitive in every state, plus the tokens they consume and the
// list of module-local components that are candidates for promotion.
// Doubles as the visual-regression surface later (Playwright per state).
// ------------------------------------------------------------------

const SEMANTIC_TOKENS = [
  '--background', '--foreground', '--muted', '--muted-foreground', '--border',
  '--primary', '--primary-foreground', '--secondary', '--accent',
  '--success', '--warning', '--destructive',
];

// Module-local components that follow the rule of three (P18 §6): reviewed
// at each wave boundary; promote into components/ui when a second/third
// consumer appears.
const PROMOTION_CANDIDATES = [
  { name: 'TypePickerPopover', home: 'formBuilder/editorParts.tsx', note: 'response-type picker; generalize to a searchable command list' },
  { name: 'ResponseSetModal', home: 'formBuilder/editorParts.tsx', note: 'sortable option rows; generalize to an editable list dialog' },
  { name: 'SliderModal', home: 'formBuilder/editorParts.tsx', note: 'numeric range editor with live preview' },
  { name: 'AddNewMenu / TriggerMenu', home: 'formBuilder/editorParts.tsx', note: 'icon+hint menu rows on AnchoredPanel; generalize to MenuList' },
  { name: 'AddressAutocomplete', home: 'properties/AddressAutocomplete.tsx', note: 'async combobox; generalize when a second async picker appears' },
  { name: 'agent-working-ring / agent-highlight', home: 'index.css', note: 'agent attention vocabulary; stays CSS until a component needs props' },
  { name: 'validation-highlight pattern', home: 'formBuilder (inline)', note: 'red ring + inline first-error; Input invalid prop covers the control half' },
];

// Story 4-78: the report render-model + style contract (contracts/reports.js
// `ReportContent`), placeholder-filled — the SAME shape `ReportRender`
// receives from a real submitted report, or from the template editor's own
// live preview (`components/formBuilder/reportPreviewContent.ts`). Three
// `sectionHeaderStyle` variants side by side because that field is the one a
// template author actually chooses in the "Report layout" panel.
const REPORT_RENDER_DEMO_BASE: Omit<ReportContent, 'style'> = {
  title: 'Sample inspection report',
  accentColor: '#6366f1',
  headerStyle: 'standard',
  templateName: 'Residential inspection',
  templateVersion: 3,
  subjectName: '12 Wharf Road',
  subjectAddress: '12 Wharf Road, Melbourne',
  typeName: 'Routine inspection',
  // Story 4-115 — sample values, same "gallery demo data" pattern as
  // `subjectName`/`subjectAddress` above.
  inspectionStatus: 'approved',
  inspectionDate: '2026-01-15T00:00:00.000Z',
  generatedAt: '2026-01-16T00:00:00.000Z',
  reportReference: 'RPT-A1B2C3D4',
  branding: null,
  // Story 4-107 — the typed layout policy; `ReportRender` doesn't read it
  // for any visual decision today (see that component's own header comment
  // on why page framing is a caller concern), so a static demo value is
  // enough here — real content gets this from `report.service.js`/
  // `reportPreviewContent.ts`, never hand-filled like this gallery page.
  layoutPolicy: {
    pageFormat: 'A4',
    // Story 4-114
    orientation: 'portrait',
    marginsMm: { top: 16, right: 14, bottom: 20, left: 14 },
    frontMatter: { enabled: false, countsTowardPageNumbers: false },
    letterhead: { mode: 'firstContentPage' },
  },
  // ── Real content blocks (story 4-80) — sample data, so the gallery shows
  // what a real submitted report's rollups look like rather than nulls.
  scoreSummary: { earned: 34, possible: 40, percentage: 85 },
  flaggedItems: {
    total: 2,
    bySeverity: { low: 0, medium: 1, high: 1, critical: 0 },
    items: [
      { observationText: 'Kitchen tap dripping.', recommendationText: 'Replace washer.', severityTag: 'medium', evidenceCount: 1 },
      { observationText: 'Smoke alarm battery flat.', recommendationText: 'Replace battery immediately.', severityTag: 'high', evidenceCount: 2 },
    ],
  },
  actions: {
    total: 2,
    items: [
      { observationText: 'Kitchen tap dripping.', recommendationText: 'Replace washer.', severityTag: 'medium' },
      { observationText: 'Smoke alarm battery flat.', recommendationText: 'Replace battery immediately.', severityTag: 'high' },
    ],
  },
  tableOfContents: [
    { key: 'overview', label: 'Overview' },
    { key: 'kitchen', label: 'Kitchen' },
  ],
  // Non-null here even though a real submitted report never has copy yet
  // (story 4-80's slot; a real source is 4-82's job) — this is the gallery's
  // proof that the `disclaimer` toggle DOES something once a value exists.
  disclaimer: 'This report reflects only the visible, accessible condition of the property at the time of inspection.',
  // Story 4-117 — same "gallery demo data" treatment as `disclaimer` above,
  // now a REAL persisted source (`ReportLayout.staticBlocks.methodology`)
  // rather than the permanently-null placeholder disclaimer used to be.
  methodology: 'This inspection followed a visual, non-invasive walkthrough of accessible areas only.',
  // Story 4-117 — resolved (never an unrealized value — see
  // `ReportFindingsGroupBy`'s own comment) grouping dimension for the
  // `flaggedItems`/`actions` rollups above.
  findingsGroupBy: 'severity',
  mediaSummary: {
    totalPhotos: 3,
    roomsWithPhotos: 1,
    // Story 4-119 — real `fieldLabel`/`sectionName`/`capturedAt` values (not
    // all-null) so the gallery's Media Tools demo below can actually show
    // grouping/timestamps doing something, not just round-tripping nulls.
    photos: [
      {
        roomName: 'Kitchen',
        thumbnailUrl: null,
        caption: 'Dripping tap',
        fieldLabel: 'Tap condition',
        sectionName: 'Kitchen',
        capturedAt: '2026-01-14T09:32:00.000Z',
      },
      {
        roomName: 'Kitchen',
        thumbnailUrl: null,
        caption: null,
        fieldLabel: 'Tap condition',
        sectionName: 'Kitchen',
        capturedAt: '2026-01-14T09:33:00.000Z',
      },
      {
        roomName: 'Smoke alarm',
        thumbnailUrl: null,
        caption: null,
        fieldLabel: 'Smoke alarm photo',
        sectionName: 'Safety',
        capturedAt: null,
      },
    ],
  },
  // Story 4-119 — resolved (never an unrealized value) presentation for the
  // media-summary block; `ReportRenderGalleryMediaTools` below flips these
  // one at a time to demo the new Media Tools controls.
  mediaSummaryPresentation: {
    layout: 'grid',
    imageFit: 'cover',
    showCaptions: true,
    showTimestamps: false,
    showLocation: false,
    showAnnotations: false,
    groupBy: 'none',
  },
  // All 11 Content-tab toggles ON — `ReportRenderGalleryToggles` below flips
  // them off one at a time to prove each one is a real, wired switch
  // (story 4-81), not the no-op it was before this story.
  contentToggles: {
    footer: true,
    tableOfContents: true,
    pageBreak: true,
    summary: true,
    flaggedItems: true,
    actions: true,
    disclaimer: true,
    inspectionResults: true,
    tables: true,
    mediaSummary: true,
    scores: true,
    // Story 4-117
    methodology: true,
    showQuestionLabels: true,
    showAnswers: true,
    showDescriptions: true,
    metadata: true,
    includeInternalNotes: true,
    evidenceCaptions: true,
  },
  sections: [
    {
      key: 'overview',
      heading: 'Overview',
      kind: 'section',
      pageBreak: 'auto',
      presentation: DEFAULT_SECTION_PRESENTATION,
      tocKey: 'overview',
      columns: null,
      columnKeys: null,
      tableRows: null,
      body: null,
      rows: [
        { label: 'Site', value: '12 Wharf Road', tone: 'neutral', kind: 'text', photos: null, chip: null, description: null },
        {
          label: 'Inspection status',
          value: 'completed',
          tone: 'positive',
          kind: 'text',
          photos: null,
          chip: null,
          description: null,
        },
        { label: 'Version', value: 'v3', tone: 'neutral', kind: 'text', photos: null, chip: null, description: null },
      ],
    },
    {
      key: 'kitchen',
      heading: 'Kitchen',
      kind: 'section',
      pageBreak: 'auto',
      presentation: DEFAULT_SECTION_PRESENTATION,
      tocKey: 'kitchen',
      columns: null,
      columnKeys: null,
      tableRows: null,
      body: 'Bench and cabinetry in good condition; one dripping tap noted.',
      rows: [
        { label: 'Condition', value: 'fair', tone: 'warning', kind: 'text', photos: null, chip: null, description: null },
        {
          label: 'Tap (dripping)',
          value: 'flagged for repair',
          tone: 'critical',
          kind: 'text',
          photos: null,
          chip: null,
          description: null,
        },
        { label: 'Photos', value: '3', tone: 'neutral', kind: 'text', photos: null, chip: null, description: null },
        // Story 4-83c — a photo/`image_gallery`-type answer's row: resolved
        // thumbnails instead of the raw evidence-id string the pre-4-83c bug
        // rendered. One real thumbnail (`thumbnailUrl` set) plus one
        // unresolvable item (`null`) side by side, so the gallery proves both
        // the real-image and "No preview" placeholder states in one place.
        {
          label: 'Tap photo',
          value: '',
          tone: 'neutral',
          kind: 'photo-grid',
          photos: [
            {
              thumbnailUrl:
                'data:image/svg+xml;utf8,' +
                encodeURIComponent(
                  '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="%23c7d2fe"/><circle cx="40" cy="40" r="18" fill="%234f46e5"/></svg>'
                ),
              caption: 'Dripping tap, close-up',
            },
            { thumbnailUrl: null, caption: null },
          ],
          chip: null,
          description: null,
        },
        // Story 4-86 — a `select`-type answer's matched option carries a
        // `color`: a colored chip instead of plain text (`OptionChip`,
        // `components/ui/Chip.tsx`), reusing the SAME primitive an option
        // already renders as everywhere else it is shown.
        {
          label: 'Overall condition',
          value: 'Fair',
          tone: 'neutral',
          kind: 'chip',
          photos: null,
          chip: { label: 'Fair', color: '#f59e0b' },
          description: null,
        },
      ],
    },
    // `kind: 'table'` (story 4-81) — a real table, not "Table 1"/"Table 2".
    // The "Notes" column mixes a photo-grid cell in with plain text cells
    // (story 4-83c) — a photo-type field can be one column among several.
    {
      key: 'fixtures-table',
      heading: 'Fixture inventory',
      kind: 'table',
      pageBreak: 'auto',
      // Story 4-116 — `layout` set explicitly to match `kind:'table'` (this
      // hand-built gallery fixture bypasses `resolveSectionPresentation`
      // entirely, same reasoning as `pageBreak:'always'` on the `kind:'page'`
      // fixture below). Story 4-118 — `tableColumns`/`overflow`/
      // `showTotalsRow` set explicitly too, so this ONE gallery fixture also
      // demonstrates Table Tools' real render effect: 'notes' emphasized
      // `primary` (wider), 'condition' `compact` (narrower — NOT the
      // photo-bearing column, so nothing here needs the compact->secondary
      // downgrade `resolveTableColumnsView` applies for a protected column),
      // `overflow:'truncate'` on a long note (full text stays in the DOM/PDF
      // text layer — see `ReportDataTable.tsx`'s own truncation CSS
      // comment), and a real totals row.
      presentation: {
        ...DEFAULT_SECTION_PRESENTATION,
        layout: 'table',
        tableColumns: [
          { key: 'fixture', visible: true, emphasis: 'standard' },
          { key: 'condition', visible: true, emphasis: 'compact' },
          { key: 'notes', visible: true, emphasis: 'primary' },
        ],
        overflow: 'truncate',
        showTotalsRow: true,
      },
      tocKey: null,
      body: null,
      rows: [],
      columns: ['Fixture', 'Condition', 'Notes'],
      columnKeys: ['fixture', 'condition', 'notes'],
      tableRows: [
        [
          { kind: 'text', value: 'Kitchen tap', photos: null, chip: null },
          // Story 4-86 — a table cell gets the same chip treatment a plain
          // row does (`ReportDataTable.tsx`'s own `CELL_KIND_RENDERERS`).
          { kind: 'chip', value: 'Fair', photos: null, chip: { label: 'Fair', color: '#f59e0b' } },
          {
            kind: 'text',
            value:
              'Dripping steadily from the spout even when fully closed — washer has likely perished and should be replaced before it damages the cabinetry below.',
            photos: null,
            chip: null,
          },
        ],
        [
          { kind: 'text', value: 'Bathroom tap', photos: null, chip: null },
          { kind: 'chip', value: 'Good', photos: null, chip: { label: 'Good', color: '#22c55e' } },
          { kind: 'text', value: '', photos: null, chip: null },
        ],
      ],
    },
    // Story 4-118 — a SECOND table fixture proving the `'compact'`->
    // `'secondary'` emphasis downgrade for a column carrying photo-grid
    // cells (the "prevent actions/signatures/images from being compressed
    // into unusable cells" requirement): 'evidence' below is stored as
    // `emphasis:'compact'` but `ReportDataTable` renders it at `secondary`
    // width, never compact, because `resolveTableColumnsView` detects the
    // photo-grid content and relaxes it automatically.
    {
      key: 'fixtures-table-evidence',
      heading: 'Fixture inventory — evidence column',
      kind: 'table',
      pageBreak: 'auto',
      presentation: {
        ...DEFAULT_SECTION_PRESENTATION,
        layout: 'table',
        tableColumns: [
          { key: 'fixture', visible: true, emphasis: 'standard' },
          { key: 'evidence', visible: true, emphasis: 'compact' },
        ],
      },
      tocKey: null,
      body: null,
      rows: [],
      columns: ['Fixture', 'Evidence'],
      columnKeys: ['fixture', 'evidence'],
      tableRows: [
        [
          { kind: 'text', value: 'Kitchen tap', photos: null, chip: null },
          {
            kind: 'photo-grid',
            value: '',
            photos: [
              {
                thumbnailUrl:
                  'data:image/svg+xml;utf8,' +
                  encodeURIComponent(
                    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="%23fecaca"/><circle cx="40" cy="40" r="18" fill="%23dc2626"/></svg>'
                  ),
                caption: 'Dripping — flagged',
              },
            ],
            chip: null,
          },
        ],
      ],
    },
    // `kind: 'page'` (story 4-81) — a page-break marker. `pageBreak:
    // 'always'` (story 4-110) is set explicitly here, matching the
    // migration default every resolver in this family applies to a
    // `kind:'page'` section — this hand-built gallery fixture bypasses
    // that resolution entirely, so leaving the field off would silently
    // stop demonstrating the break this section exists to show.
    {
      key: 'appendix',
      heading: 'Appendix',
      kind: 'page',
      pageBreak: 'always',
      presentation: DEFAULT_SECTION_PRESENTATION,
      tocKey: null,
      columns: null,
      columnKeys: null,
      tableRows: null,
      body: null,
      rows: [],
    },
  ],
};

// Story 4-83c — `ReportInlinePhotoGrid` on its own, before the composite
// `ReportRender` galleries below also prove it wired into a real row/cell
// (DOD rule 7: the primitive gets its own gallery entry before feature use).
// Three states side by side: a single real thumbnail, an unresolvable/
// missing-preview placeholder, and a multi-photo grid mixing both — the same
// three cases `report.service.js`'s evidence resolution can honestly produce
// (a resolved evidence doc, a deleted/unresolvable evidence id, several of
// each in one answer).
const SAMPLE_THUMB =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="%23c7d2fe"/><circle cx="40" cy="40" r="18" fill="%234f46e5"/></svg>'
  );

function ReportInlinePhotoGridGallery(): JSX.Element {
  return (
    <Grid columns={{ base: 1, sm: 3 }} gap={4}>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">One resolved thumbnail</p>
        <ReportInlinePhotoGrid photos={[{ thumbnailUrl: SAMPLE_THUMB, caption: 'Dripping tap, close-up' }]} />
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Unresolvable — placeholder, never a broken image
        </p>
        <ReportInlinePhotoGrid photos={[{ thumbnailUrl: null, caption: null }]} />
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Multi-photo answer</p>
        <ReportInlinePhotoGrid
          photos={[
            { thumbnailUrl: SAMPLE_THUMB, caption: 'Front elevation' },
            { thumbnailUrl: SAMPLE_THUMB, caption: null },
            { thumbnailUrl: null, caption: null },
          ]}
        />
      </div>
    </Grid>
  );
}

function ReportRenderGallery(): JSX.Element {
  return (
    <Grid columns={{ base: 1, lg: 3 }} gap={4}>
      {(['filled', 'centered', 'minimal'] as const).map((sectionHeaderStyle) => (
        <div key={sectionHeaderStyle} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{sectionHeaderStyle}</p>
          <ReportRender
            content={{
              ...REPORT_RENDER_DEMO_BASE,
              style: {
                logo: null,
                accentColor: '#6366f1',
                font: 'sans',
                sectionHeaderStyle,
                thumbnailSize: 'medium',
                pageSize: 'A4',
                coverPage: false,
                headingScale: 'md',
                secondaryTextFromAccent: false,
                sectionUnderlineWidth: 'full',
                surfaceRadius: 'rounded',
                // Story 4-114
                pageOrientation: 'portrait',
                marginsPreset: 'normal',
                letterhead: DEFAULT_LETTERHEAD,
                // Story 4-120
                typographyRoles: DEFAULT_REPORT_LAYOUT.typographyRoles,
                colorRoles: DEFAULT_REPORT_LAYOUT.colorRoles,
                borderDensity: DEFAULT_REPORT_LAYOUT.borderDensity,
                variants: DEFAULT_REPORT_LAYOUT.variants,
              },
            }}
          />
        </div>
      ))}
    </Grid>
  );
}

const REPORT_RENDER_DEMO_STYLE: ReportContent['style'] = {
  logo: null,
  accentColor: '#6366f1',
  font: 'sans',
  sectionHeaderStyle: 'filled',
  thumbnailSize: 'medium',
  pageSize: 'A4',
  coverPage: false,
  headingScale: 'md',
  secondaryTextFromAccent: false,
  sectionUnderlineWidth: 'full',
  surfaceRadius: 'rounded',
  // Story 4-114
  pageOrientation: 'portrait',
  marginsPreset: 'normal',
  letterhead: DEFAULT_LETTERHEAD,
  // Story 4-120
  typographyRoles: DEFAULT_REPORT_LAYOUT.typographyRoles,
  colorRoles: DEFAULT_REPORT_LAYOUT.colorRoles,
  borderDensity: DEFAULT_REPORT_LAYOUT.borderDensity,
  variants: DEFAULT_REPORT_LAYOUT.variants,
};

// Story 4-82 — the three new customization dimensions this story ships,
// proven real the same way 4-81's `ReportContentTogglesGallery` proves its
// toggles real: the SAME `ReportRender`, the SAME demo content, only
// `content.style` differs per column.
function ReportStyleExpandedGallery(): JSX.Element {
  return (
    <div className="space-y-6">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Cover page — off vs on (header gets a real page-break before it, print/PDF only)
        </p>
        <Grid columns={{ base: 1, lg: 2 }} gap={4}>
          {([false, true] as const).map((coverPage) => (
            <div key={String(coverPage)} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                coverPage: {String(coverPage)}
              </p>
              <ReportRender
                content={{ ...REPORT_RENDER_DEMO_BASE, style: { ...REPORT_RENDER_DEMO_STYLE, coverPage } }}
              />
            </div>
          ))}
        </Grid>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Heading size — sm / md / lg (md is the pre-4-82 default size, unchanged)
        </p>
        <Grid columns={{ base: 1, lg: 3 }} gap={4}>
          {(['sm', 'md', 'lg'] as const).map((headingScale) => (
            <div key={headingScale} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                headingScale: {headingScale}
              </p>
              <ReportRender
                content={{ ...REPORT_RENDER_DEMO_BASE, style: { ...REPORT_RENDER_DEMO_STYLE, headingScale } }}
              />
            </div>
          ))}
        </Grid>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Accent-tinted secondary text — off vs on, 'print' variant only (the template caption below the
          title picks up a contrast-safe shade of the accent instead of the flat muted-foreground token;
          the 'screen' variant never applies this — its translucent glass-card background is not a fixed
          color the derivation could validate against)
        </p>
        <Grid columns={{ base: 1, lg: 2 }} gap={4}>
          {([false, true] as const).map((secondaryTextFromAccent) => (
            <div key={String(secondaryTextFromAccent)} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                secondaryTextFromAccent: {String(secondaryTextFromAccent)}
              </p>
              <ReportRender
                content={{
                  ...REPORT_RENDER_DEMO_BASE,
                  style: { ...REPORT_RENDER_DEMO_STYLE, secondaryTextFromAccent },
                }}
                variant="print"
              />
            </div>
          ))}
        </Grid>
      </div>
    </div>
  );
}

// Story 4-84 — the font picker's real, curated choices (`REPORT_FONTS`:
// 'sans'|'serif'|'mono', additive to the pre-existing two). Proven the same
// way 4-82's `ReportStyleExpandedGallery` proves its three fields real: the
// SAME `ReportRender`, the SAME demo content, only `content.style.font`
// differs per column — and each column is rendered TWICE, 'screen' then
// 'print', to make the done-when's "renders identically in the web preview,
// print variant, AND the downloaded PDF" visible in the gallery itself (the
// PDF twin, `pdfExport.service.js#FONT_STACKS`, carries the byte-identical
// CSS stack under the same key — see that file's own header comment).
function ReportStyleFontGallery(): JSX.Element {
  return (
    <div className="space-y-6">
      {(['screen', 'print'] as const).map((variant) => (
        <div key={variant}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {variant} variant
          </p>
          <Grid columns={{ base: 1, lg: 3 }} gap={4}>
            {REPORT_FONTS.map((font) => (
              <div key={font} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">font: {font}</p>
                <ReportRender
                  content={{ ...REPORT_RENDER_DEMO_BASE, style: { ...REPORT_RENDER_DEMO_STYLE, font } }}
                  variant={variant}
                />
              </div>
            ))}
          </Grid>
        </div>
      ))}
    </div>
  );
}

// Story 4-85 — the two decoration tokens: `sectionUnderlineWidth` widens/
// narrows a 'filled'/'minimal' heading's OWN rule (the tinted bar / the
// accent border-bottom); `surfaceRadius` steps the corner treatment of
// FILLED/chip surfaces only (the 'filled' heading's tinted bar and the
// flagged/actions rollup's severity badges — `REPORT_RENDER_DEMO_BASE`
// already carries both). Proven the same way 4-84's font gallery proves its
// choice real: the SAME `ReportRender`, the SAME demo content, only
// `content.style` differs per column.
function ReportStyleDecorationGallery(): JSX.Element {
  return (
    <div className="space-y-6">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Section underline width — full (pre-4-85 default, unchanged) vs text, for the two section-header
          variants that draw a real rule (&apos;filled&apos;&apos;s tinted bar, &apos;minimal&apos;&apos;s
          accent border-bottom; &apos;centered&apos; draws no rule either way and is unaffected)
        </p>
        <Grid columns={{ base: 1, lg: 2 }} gap={6}>
          {(['filled', 'minimal'] as const).map((sectionHeaderStyle) => (
            <div key={sectionHeaderStyle} className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                sectionHeaderStyle: {sectionHeaderStyle}
              </p>
              {SECTION_UNDERLINE_WIDTHS.map((sectionUnderlineWidth) => (
                <div key={sectionUnderlineWidth} className="space-y-2">
                  <p className="text-label font-semibold uppercase tracking-wide text-muted-foreground">
                    sectionUnderlineWidth: {sectionUnderlineWidth}
                  </p>
                  <ReportRender
                    content={{
                      ...REPORT_RENDER_DEMO_BASE,
                      style: { ...REPORT_RENDER_DEMO_STYLE, sectionHeaderStyle, sectionUnderlineWidth },
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
        </Grid>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Corner style — rounded (pre-4-85 default, unchanged) vs sharp, on the filled/chip surfaces only
          (the &apos;filled&apos; section-header bar and the flagged/actions rollup&apos;s severity badges below)
        </p>
        <Grid columns={{ base: 1, lg: 2 }} gap={4}>
          {SURFACE_RADII.map((surfaceRadius) => (
            <div key={surfaceRadius} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                surfaceRadius: {surfaceRadius}
              </p>
              <ReportRender
                content={{ ...REPORT_RENDER_DEMO_BASE, style: { ...REPORT_RENDER_DEMO_STYLE, surfaceRadius } }}
              />
            </div>
          ))}
        </Grid>
      </div>
    </div>
  );
}

// Story 4-88b — two brand-new DS primitives (DOD rule 7: gallery entry +
// a11y pass before any feature use — the real feature is `ReportDesigner.
// tsx`'s letterhead panel, wired below this gallery in the SAME story):
//   * `SlotGridPicker` — a caller-defined 2D grid of named positions, one
//     selectable "active" cell (`aria-pressed`), real `<button>`s so Tab +
//     Enter/Space always operates it.
//   * `DirectionalPad` — relocates the active slot's content to an adjacent
//     named slot; every direction is a real `Button` (Tab + Enter/Space)
//     PLUS arrow keys once focus is anywhere inside the pad — never
//     mouse-only.
// `LETTERHEAD_GRID_CELLS`/`relocateLetterheadSlot`
// (`components/formBuilder/letterheadGrid.ts`) are the EXACT topology/swap
// logic `ReportDesigner.tsx`'s real feature uses — one source, so this demo
// and the feature can never silently diverge onto two different grids.
//
// Three static states side by side (documentation, same convention the
// `ReportRender` comparison galleries above already use for "look at N
// configs at once" — no click needed to see each), THEN one fully
// interactive demo below proving the real click/keyboard behaviour end to
// end (the same "real useState, not a static glyph" principle the Checkbox/
// Switch/RadioGroup block earlier on this page documents for exactly this
// reason — keyboard operability needs a real onChange to prove Space/Enter/
// arrows actually change something).
function LetterheadPickerStatesGallery(): JSX.Element {
  const assignedLetterhead: typeof DEFAULT_LETTERHEAD = {
    ...DEFAULT_LETTERHEAD,
    logoLeft: { enabled: true, content: SAMPLE_THUMB },
    title: { enabled: true, content: null },
  };
  const assignedBadge = (cell: { id: string }) =>
    assignedLetterhead[cell.id as LetterheadSlotKey]?.enabled ? (
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-primary" />
    ) : null;

  return (
    <Grid columns={{ base: 1, lg: 3 }} gap={6}>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Empty grid — nothing active, nothing assigned
        </p>
        <SlotGridPicker
          aria-label="Letterhead slots — empty"
          cells={LETTERHEAD_GRID_CELLS}
          rows={LETTERHEAD_GRID_ROWS}
          cols={LETTERHEAD_GRID_COLS}
          activeId={null}
          onActivate={() => {}}
        />
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          One slot active — &quot;Logo — center&quot; is pressed (aria-pressed, visibly distinct border + ring)
        </p>
        <SlotGridPicker
          aria-label="Letterhead slots — one active"
          cells={LETTERHEAD_GRID_CELLS}
          rows={LETTERHEAD_GRID_ROWS}
          cols={LETTERHEAD_GRID_COLS}
          activeId="logoCenter"
          onActivate={() => {}}
        />
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Content assigned — &quot;Logo — left&quot;/&quot;Title&quot; carry a real value (dot badge), independent of
          which cell happens to be active
        </p>
        <SlotGridPicker
          aria-label="Letterhead slots — content assigned"
          cells={LETTERHEAD_GRID_CELLS}
          rows={LETTERHEAD_GRID_ROWS}
          cols={LETTERHEAD_GRID_COLS}
          activeId="logoLeft"
          onActivate={() => {}}
          renderCellBadge={assignedBadge}
        />
      </div>
    </Grid>
  );
}

type LetterheadDirection = 'up' | 'down' | 'left' | 'right';

function LetterheadPickerLiveDemo(): JSX.Element {
  const [letterhead, setLetterhead] = useState<typeof DEFAULT_LETTERHEAD>(DEFAULT_LETTERHEAD);
  const [activeId, setActiveId] = useState<LetterheadSlotKey | null>(null);
  const active = activeId ? letterhead[activeId] : null;

  const disabled = (['up', 'down', 'left', 'right'] as LetterheadDirection[]).reduce(
    (acc, direction) => {
      acc[direction] = !activeId || findAdjacentSlot(LETTERHEAD_GRID_CELLS, activeId, direction) === null;
      return acc;
    },
    {} as Record<LetterheadDirection, boolean>,
  );

  const move = (direction: LetterheadDirection) => {
    if (!activeId) return;
    const target = findAdjacentSlot(LETTERHEAD_GRID_CELLS, activeId, direction) as LetterheadSlotKey | null;
    if (!target) return;
    setLetterhead((prev) => relocateLetterheadSlot(prev, activeId, target));
    setActiveId(target);
  };

  const patchActive = (patch: Partial<{ enabled: boolean; content: string | null }>) => {
    if (!activeId) return;
    setLetterhead((prev) => ({ ...prev, [activeId]: { ...prev[activeId], ...patch } }));
  };

  const contentField = (placeholder: string) => (
    <Input
      className="w-full"
      placeholder={placeholder}
      value={active?.content ?? ''}
      onChange={(e) => patchActive({ content: e.target.value })}
    />
  );

  return (
    // Documented exception, story 2-147xd — `layout-only-through-primitives`
    // (raw-grid): a genuine asymmetric `auto auto 1fr` triplet (two
    // content-sized columns, one flexible) — neither `Grid` (integer
    // equal-width columns only) nor `Split` (a closed two-pane ratio set)
    // expresses an auto-sized column. Same class of gap `InspectionTypeForm
    // .tsx`'s `[1fr_1fr_auto]` row already records — a single call site,
    // not a repeated pattern, so not a case for growing either primitive.
    <div className="grid gap-6 lg:grid-cols-[auto,auto,1fr]">
      <SlotGridPicker
        aria-label="Letterhead slots — live demo"
        cells={LETTERHEAD_GRID_CELLS}
        rows={LETTERHEAD_GRID_ROWS}
        cols={LETTERHEAD_GRID_COLS}
        activeId={activeId}
        onActivate={(id) => setActiveId(id as LetterheadSlotKey)}
        renderCellBadge={(cell) =>
          letterhead[cell.id as LetterheadSlotKey]?.enabled ? (
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-primary" />
          ) : null
        }
      />

      <div className="space-y-3">
        <DirectionalPad contextLabel={activeId ? LETTERHEAD_SLOT_LABELS[activeId] : 'a slot'} disabled={disabled} onMove={move} />
        {activeId ? (
          <div className="space-y-2">
            <Inline as="label" gap={2} className="text-xs text-foreground">
              <Switch value={active?.enabled ?? false} onChange={(next) => patchActive({ enabled: next })} size="sm" />
              Enabled
            </Inline>
            {(activeId === 'logoLeft' || activeId === 'logoCenter' || activeId === 'logoRight') &&
              contentField('Logo URL / data-URI')}
            {activeId === 'footerText' && contentField('Footer text')}
            {activeId === 'footerPageNumber' && contentField('Page {page} of {total}')}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Select a slot above to edit it.</p>
        )}
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Live preview — the SAME `ReportLetterhead` header block `ReportRender.tsx` renders
        </p>
        <div className="rounded-hairline border border-border bg-card space-y-1 p-4">
          <ReportLetterhead
            letterhead={letterhead}
            title="Sample inspection report"
            subjectAddress="123 Sample Street, Example City"
          />
          <p className="text-caption text-muted-foreground">
            (footer slots — Page number / Footer text — are PDF-only; see `ReportLetterhead`&apos;s own comment)
          </p>
        </div>
      </div>
    </div>
  );
}

// Story 4-81 — proof that 8 of the Content-tab's 11 toggles are no longer
// no-ops: the six that gate a real 4-80 data block ('scores',
// 'tableOfContents', 'flaggedItems', 'actions', 'mediaSummary',
// 'disclaimer') PLUS 'inspectionResults'/'tables' (independent switches now
// that `ReportContentSection.kind` exists on the wire — 'inspectionResults'
// gates every non-table section, 'tables' gates every `kind:'table'`
// section, rendered as a REAL table, not the pre-4-81 "Table 1"/"Table 2"
// bug), rendered ALL ON beside the SAME content ALL OFF. Same
// `ReportRender`, same data, only `content.contentToggles` differs — which
// is exactly what changes between a template author checking and
// unchecking a box in `ReportDesigner.tsx`'s Content tab. The remaining 3
// ('footer', 'summary', 'pageBreak') are deliberately not demonstrated
// here: 'footer'/'summary' are still deferred (see `ReportRender.tsx`'s own
// header comment for why), and 'pageBreak' was RETIRED by story 4-110 —
// `ReportContentSection.pageBreak` (a real per-section field, demoed
// directly on the 'appendix' section below via `REPORT_RENDER_DEMO_BASE`)
// replaced it, so toggling this key no longer changes anything for this
// pair to visibly demonstrate.
// Story 4-119 — Report Studio Media Tools. `ReportMediaSummaryGrid` gains
// five additive props (`layout`/`imageFit`/`showCaptions`/`showTimestamps`/
// `groupBy`) — no new primitive, matching `4-116`/`4-117`/`4-118`'s own
// precedent for why no new gallery ENTRY was needed for an existing
// primitive gaining props, but this demo IS extended (mirroring `4-118`'s
// own "extended its existing gallery fixture with a real Table Tools demo")
// so the new controls get a real, visible a11y-passable demonstration
// rather than round-tripping silently. Four panels: the default grid, list,
// full-width (image-fit contain, so the whole photo is visible rather than
// cropped), and grid grouped by section.
function ReportMediaSummaryToolsGallery(): JSX.Element {
  const base = { ...REPORT_RENDER_DEMO_BASE, style: REPORT_RENDER_DEMO_STYLE };
  return (
    <Grid columns={{ base: 1, lg: 2 }} gap={4}>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">layout: grid (default)</p>
        <ReportRender content={base} />
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          layout: list, showTimestamps: true
        </p>
        <ReportRender
          content={{
            ...base,
            mediaSummaryPresentation: { ...base.mediaSummaryPresentation, layout: 'list', showTimestamps: true },
          }}
        />
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          layout: fullWidth, imageFit: contain
        </p>
        <ReportRender
          content={{
            ...base,
            mediaSummaryPresentation: { ...base.mediaSummaryPresentation, layout: 'fullWidth', imageFit: 'contain' },
          }}
        />
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">groupBy: section</p>
        <ReportRender
          content={{
            ...base,
            mediaSummaryPresentation: { ...base.mediaSummaryPresentation, groupBy: 'section' },
          }}
        />
      </div>
    </Grid>
  );
}

function ReportContentTogglesGallery(): JSX.Element {
  const allOff: ReportContent['contentToggles'] = {
    footer: true,
    tableOfContents: false,
    pageBreak: false,
    summary: true,
    flaggedItems: false,
    actions: false,
    disclaimer: false,
    inspectionResults: false,
    tables: false,
    mediaSummary: false,
    scores: false,
    // Story 4-117 — left at the same real defaults as `REPORT_RENDER_DEMO_BASE`
    // (untouched by this ON/OFF pair, which only demonstrates the pre-4-117
    // toggles) rather than `false` — the label above this pair still lists
    // only the toggles it actually varies.
    methodology: false,
    showQuestionLabels: true,
    showAnswers: true,
    showDescriptions: false,
    metadata: false,
    includeInternalNotes: false,
    evidenceCaptions: false,
  };
  return (
    <Grid columns={{ base: 1, lg: 2 }} gap={4}>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          scores / tableOfContents / flaggedItems / actions / mediaSummary / disclaimer / inspectionResults / tables
          — ON
        </p>
        <ReportRender content={{ ...REPORT_RENDER_DEMO_BASE, style: REPORT_RENDER_DEMO_STYLE }} />
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Same content — OFF</p>
        <ReportRender
          content={{ ...REPORT_RENDER_DEMO_BASE, style: REPORT_RENDER_DEMO_STYLE, contentToggles: allOff }}
        />
      </div>
    </Grid>
  );
}

// ------------------------------------------------------------------
// Vocabulary layer (P03 §5, story 3-2). ONE component, rendered once per
// seeded domain pack: everything it prints comes from `resolveVocab` with a
// different `domainKey`, so the row for `building` reads "Add building /
// Rooms / Findings" and the row for `website` reads "Add website / Pages /
// Issues" — from the same JSX, with no branch on the pack.
//
// The "Registry" column is the control: it renders SITES_REGISTRY_LABEL, the
// constant that must stay "Sites" in every row (P03 §0). If a future change
// ever vocabularises the registry name, this column stops being uniform and
// the gallery shows it.
// ------------------------------------------------------------------
function VocabRow({ registry, domainKey }: { registry: VocabRegistry; domainKey: string }): JSX.Element {
  const t = (term: Parameters<typeof resolveVocab>[1]): string => resolveVocab(registry, term, domainKey);
  return (
    <TR>
      <TD className="font-semibold">{domainKey}</TD>
      <TD>{SITES_REGISTRY_LABEL}</TD>
      <TD>{t('Subject')}</TD>
      <TD>{t('Subjects')}</TD>
      <TD>{`Add ${t('Subject').toLowerCase()}`}</TD>
      <TD>{`No ${t('Subjects').toLowerCase()} yet`}</TD>
      <TD>{t('Units')}</TD>
      <TD>{t('Findings')}</TD>
      <TD>{t('Walkthrough')}</TD>
    </TR>
  );
}

function VocabularyGallery(): JSX.Element {
  const { packs, defaultDomainKey, activeDomainKey, isHydrated } = useVocab();
  const registry: VocabRegistry = { packs, defaultDomainKey };
  if (!isHydrated) return <Skeleton className="h-32 w-full" />;
  if (packs.length === 0) {
    return (
      <EmptyState
        title="No domain packs seeded"
        description="Run the backend seed (or migration 009) to load the domain-pack registry."
      />
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Registry resolved from <code>GET /v1/domains</code>. Unqualified <code>vocab(&apos;Subject&apos;)</code> on
        this org resolves against <strong>{activeDomainKey}</strong>; a record always resolves against its own{' '}
        <code>domainKey</code>. Packs marked inactive are seed-loaded but not activated for this org — the noun
        still resolves, because records outlive an entitlement.
      </p>
      <div className="overflow-x-auto">
        <Table aria-label="Domain vocabulary">
          <THead>
            <TR>
              <TH>domainKey</TH>
              <TH>Registry (constant)</TH>
              <TH>Subject</TH>
              <TH>Subjects</TH>
              <TH>Create CTA</TH>
              <TH>Empty state</TH>
              <TH>Units</TH>
              <TH>Findings</TH>
              <TH>Walkthrough</TH>
            </TR>
          </THead>
          <TBody>
            {packs.map((pack) => (
              <VocabRow key={pack.key} registry={registry} domainKey={pack.key} />
            ))}
          </TBody>
        </Table>
      </div>
      <Cluster gap={2}>
        {packs.map((pack) => (
          <Badge key={pack.key} tone={pack.active ? 'success' : 'neutral'}>
            {`${pack.key} \u2014 ${pack.active ? 'active' : 'seed-loaded, inactive'}`}
          </Badge>
        ))}
      </Cluster>
    </div>
  );
}

// ------------------------------------------------------------------
// Shared cursor list (DOD §4 G-18, story 2-76). The USAGE PROOF for the
// envelope + hook, deliberately against a REAL endpoint and a REAL server-issued
// cursor — not a fixture. `limit: 2` forces the pager to appear on any org with
// three or more sites, which is the only way to demonstrate that `nextCursor`
// round-trips: the button sends the cursor the server issued for the previous
// page and the next page arrives with no repeats and no gaps.
//
// `itemSchema` is passed here (and NOT on the real PropertiesPage) on purpose:
// this surface exists to fail loudly if the wire ever stops matching
// `cursorList(Property)`, whereas a product page should render what it got.
// ------------------------------------------------------------------
// The endpoint PATH is a wire name, not a vocabulary term — P03 §6 keeps the
// wire saying "properties" through the compatibility window, and the 3-2
// domain-noun rule (rightly) only inspects user-facing copy positions. Holding
// it in a constant keeps the literal out of those positions instead of writing
// a domain noun into a title/description/hint.
const PILOT_LIST_PATH = 'GET /v1/properties?limit=2';

function CursorListGallery(): JSX.Element {
  const { items, total, hasMore, fetchNextPage, isLoading, isLoadingMore, isError } = useCursorList<Property>({
    queryKey: propertiesKeys.list({ gallery: 'cursor-list' }),
    fetchPage: (params) => listProperties(params),
    limit: 2,
    itemSchema: PropertySchema,
  });

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (isError) {
    return (
      <EmptyState
        title="Could not read the list"
        description="The pilot list endpoint failed — the cursor-list proof needs the dev stack up."
      />
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        title="Nothing to page through"
        description="This org has no records on the pilot list endpoint, so there is no cursor to walk."
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="cursor-list-gallery">
      <p className="text-sm text-muted-foreground">
        <code>{PILOT_LIST_PATH}</code> through <code>useCursorList()</code>. Each page is parsed with{' '}
        <code>cursorListOf(PropertySchema)</code> — the generated envelope schema composed with the generated item
        schema — so a shape drift breaks this block instead of silently rendering.
      </p>
      <Table aria-label="Cursor list proof">
        <THead>
          <TR>
            <TH>#</TH>
            <TH>Name</TH>
            <TH>Updated</TH>
          </TR>
        </THead>
        <TBody>
          {items.map((row, index) => (
            <TR key={row._id}>
              <TD>{index + 1}</TD>
              <TD>{row.name}</TD>
              <TD>{row.updatedAt}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <Cluster gap={3}>
        <span className="text-sm text-muted-foreground" data-testid="cursor-list-loaded">
          {`Loaded ${items.length}${typeof total === 'number' ? ` of ${total}` : ''}`}
        </span>
        <Badge tone={hasMore ? 'primary' : 'success'}>
          {hasMore ? 'nextCursor issued' : 'nextCursor: null (last page)'}
        </Badge>
        {hasMore && (
          <Button variant="secondary" onClick={fetchNextPage} disabled={isLoadingMore} data-testid="cursor-list-more">
            {isLoadingMore ? 'Loading\u2026' : 'Load more'}
          </Button>
        )}
      </Cluster>
    </div>
  );
}

// Shell geometry & responsive-state contract (story 2-131, P10 §1.1). This
// gallery is the ONLY consumer of the tokens tokens.css just defined — no
// component file is touched by this story (rendered output must stay
// byte-identical), so every bar/dot below reads its size or color straight
// off a `--shell-*`/`--agent-status` CSS custom property, and the three
// state toggles below set `data-shell-nav`/`data-shell-agent`/
// `data-agent-status` on a bounded LOCAL demo root — proving the contract
// is a plain attribute selector (works anywhere the attribute lives), not
// proving anything about the real shell root 2-132/2-133 wire it onto.
const SHELL_NAV_STATES = ['expanded', 'collapsed', 'overlay'] as const;
const SHELL_AGENT_STATES = ['closed', 'open'] as const;
const AGENT_STATUS_STATES = ['idle', 'ready', 'working', 'blocked'] as const;

const SHELL_STATE_TABLE_ROWS: Array<{ attr: string; values: string; effect: string }> = [
  {
    attr: 'data-shell-nav',
    values: 'expanded | collapsed | overlay',
    effect: '--shell-sidebar-current-w -> --shell-sidebar-w / --shell-sidebar-collapsed-w / 0px',
  },
  {
    attr: 'data-shell-agent',
    values: 'closed | open',
    effect: '--shell-agent-current-w -> 0px / --shell-agent-w (is the workspace showing)',
  },
  {
    attr: 'data-agent-status',
    values: 'idle | ready | working | blocked',
    effect: '--agent-status -> --primary (idle, working) / --success (ready) / --warning (blocked) — a separate axis from data-shell-agent',
  },
];

function ShellGeometryTokensGallery(): JSX.Element {
  const [nav, setNav] = useState<(typeof SHELL_NAV_STATES)[number]>('expanded');
  const [agent, setAgent] = useState<(typeof SHELL_AGENT_STATES)[number]>('closed');
  const [status, setStatus] = useState<(typeof AGENT_STATUS_STATES)[number]>('idle');

  return (
    <div className="space-y-6">
      <div
        className="space-y-4 rounded-2xl border border-border bg-muted/20 p-4"
        data-shell-nav={nav}
        data-shell-agent={agent}
        data-agent-status={status}
        data-testid="shell-state-demo-root"
      >
        <Cluster gap={4}>
          <Inline as="fieldset" gap={2}>
            <legend className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">data-shell-nav</legend>
            {SHELL_NAV_STATES.map((v) => (
              <Button key={v} size="sm" variant={nav === v ? 'primary' : 'secondary'} onClick={() => setNav(v)} aria-pressed={nav === v}>
                {v}
              </Button>
            ))}
          </Inline>
          <Inline as="fieldset" gap={2}>
            <legend className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">data-shell-agent</legend>
            {SHELL_AGENT_STATES.map((v) => (
              <Button key={v} size="sm" variant={agent === v ? 'primary' : 'secondary'} onClick={() => setAgent(v)} aria-pressed={agent === v}>
                {v}
              </Button>
            ))}
          </Inline>
          <Inline as="fieldset" gap={2}>
            <legend className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">data-agent-status</legend>
            {AGENT_STATUS_STATES.map((v) => (
              <Button key={v} size="sm" variant={status === v ? 'primary' : 'secondary'} onClick={() => setStatus(v)} aria-pressed={status === v}>
                {v}
              </Button>
            ))}
          </Inline>
        </Cluster>

        <Cluster align="end" gap={6}>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">--shell-sidebar-current-w</p>
            <div
              className="h-16 rounded-lg border border-border-strong bg-primary/15 transition-[width] duration-200"
              style={{ width: 'var(--shell-sidebar-current-w)' }}
              data-testid="shell-sidebar-current-w-bar"
            />
          </div>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">--shell-agent-current-w</p>
            <div
              className="h-16 rounded-lg border border-border-strong bg-accent/20 transition-[width] duration-200"
              style={{ width: 'var(--shell-agent-current-w)' }}
              data-testid="shell-agent-current-w-bar"
            />
          </div>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">--agent-status</p>
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full border border-border-strong"
              style={{ backgroundColor: 'hsl(var(--agent-status) / 0.18)' }}
            >
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: 'hsl(var(--agent-status))' }}
                data-testid="agent-status-dot"
                aria-hidden="true"
              />
            </div>
          </div>
        </Cluster>
      </div>

      <Grid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">--shell-header-h</p>
          <div className="flex items-center rounded-lg border border-border bg-card px-3 text-xs text-foreground" style={{ height: 'var(--shell-header-h)' }}>
            header row
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">--shell-control-h (compact, pointer: fine)</p>
          <div className="flex items-center rounded-md border border-border bg-card px-2 text-xs text-foreground" style={{ height: 'var(--shell-control-h)' }}>
            control
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">--shell-control-h (touch, pointer: coarse, forced for demo)</p>
          <div
            className="flex items-center rounded-md border border-border bg-card px-2 text-xs text-foreground"
            style={{ height: 'var(--shell-control-h)', ['--shell-control-h' as string]: 'var(--shell-control-h-touch)' }}
          >
            control (48px floor)
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">--canvas-gutter-x / --canvas-gutter-y / --panel-gap</p>
          <div
            className="grid rounded-lg border border-dashed border-border bg-card text-xs text-muted-foreground"
            style={{ padding: 'var(--canvas-gutter-y) var(--canvas-gutter-x)', gap: 'var(--panel-gap)' }}
          >
            <div className="rounded border border-border bg-muted/40 p-2">gutter-padded block</div>
            <div className="rounded border border-border bg-muted/40 p-2">panel-gap apart</div>
          </div>
        </div>
      </Grid>

      <Table aria-label="Shell responsive-state contract">
        <THead>
          <TR>
            <TH>Attribute</TH>
            <TH>Values</TH>
            <TH>Effect (token reassignment)</TH>
          </TR>
        </THead>
        <TBody>
          {SHELL_STATE_TABLE_ROWS.map((row) => (
            <TR key={row.attr}>
              <TD className="font-mono text-xs">{row.attr}</TD>
              <TD className="text-xs">{row.values}</TD>
              <TD className="text-xs text-muted-foreground">{row.effect}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

const CONTENT_WIDTH_STRESS_PARAGRAPH =
  'Long-form content is where a reading measure earns its keep: too wide and the eye loses its place ' +
  'tracking back to the start of the next line, too narrow and every sentence breaks awkwardly mid-thought. ' +
  'This paragraph exists purely to prove the clamp holds under real prose — no lorem ipsum, an actual run of ' +
  'sentences long enough that a reader would notice if the line length crept past a comfortable measure. The ' +
  'inspection report narrative, the audit-trail explanation, the release note — all reading-mode content — ' +
  'wrap at --content-reading rather than the wider operational clamp, so this is the shape those surfaces ' +
  'actually get, not an idealized short sample.';

const CONTENT_WIDTH_STRESS_ROWS = Array.from({ length: 6 }, (_, i) => ({
  id: `INS-${1000 + i}`,
  site: `13/883 Plenty Road, South Morang — Building ${i + 1}`,
  inspector: ['Kundan Ray', 'Amara Osei', 'Priya Nair'][i % 3],
  status: ['Approved', 'In review', 'Draft'][i % 3],
  updated: `2026-08-${(10 + i).toString().padStart(2, '0')} 14:0${i}`,
  score: `${92 - i}%`,
}));

function ShellContentWidthGallery(): JSX.Element {
  return (
    <div className="space-y-8">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          --content-reading — long-form prose, clamped for a comfortable line length
        </p>
        <div className="rounded-lg border border-border bg-card p-4" style={{ maxWidth: 'var(--content-reading)' }}>
          <p className="text-sm leading-7 text-foreground">{CONTENT_WIDTH_STRESS_PARAGRAPH}</p>
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          --content-operational — data-dense working surface, wider clamp
        </p>
        <div className="overflow-x-auto rounded-lg border border-border bg-card p-4" style={{ maxWidth: 'var(--content-operational)' }}>
          <Table aria-label="Content-operational stress example">
            <THead>
              <TR>
                <TH>Inspection</TH>
                <TH>Site</TH>
                <TH>Inspector</TH>
                <TH>Status</TH>
                <TH>Updated</TH>
                <TH>Score</TH>
              </TR>
            </THead>
            <TBody>
              {CONTENT_WIDTH_STRESS_ROWS.map((row) => (
                <TR key={row.id}>
                  <TD className="font-mono text-xs">{row.id}</TD>
                  <TD>{row.site}</TD>
                  <TD>{row.inspector}</TD>
                  <TD>{row.status}</TD>
                  <TD className="text-xs text-muted-foreground">{row.updated}</TD>
                  <TD>{row.score}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          --content-fluid — no clamp, fills whatever canvas width it is given
        </p>
        <div className="rounded-lg border border-dashed border-border bg-card p-4" style={{ maxWidth: 'var(--content-fluid)' }}>
          <Grid columns={{ base: 3, sm: 6 }} gap={3}>
            {Array.from({ length: 12 }, (_, i) => (
              <div key={i} className="rounded-md border border-border bg-muted/30 p-3 text-center text-xs text-muted-foreground">
                tile {i + 1}
              </div>
            ))}
          </Grid>
        </div>
      </div>
    </div>
  );
}

// Restrained visual-language roles (story 2-136, P10 §1.2). Reverses 2-127's
// "glass/gradient as default" framing — see tokens.css's own comment block
// (same story) for the full reversal record. This gallery is the ONLY
// consumer of the new tokens below: no component file is touched by this
// story, `.glass-card`/`.glass`/the gradient utilities stay unmodified and
// fully available. Canvas/surface/elevated overlay/hairline/status mint NO
// new variable (P10 §1.2's table) — each is demonstrated through its
// EXISTING semantic token's own Tailwind utility (`bg-background`,
// `bg-card`, `bg-popover`, `border-border`/`border-border-strong`,
// `text-success` etc.), never a `bg-[hsl(var(--…))]` arbitrary value.
// `--shadow-elevated`/`--text-*`/`--density-*`/`--focus-ring-*`/`--motion-*`
// are the genuinely new tokens and are read directly, as CSS custom
// properties, because they have no Tailwind utility to point at.
function VisualLanguageRoleSurfacesGallery(): JSX.Element {
  return (
    <div className="space-y-6">
      <Grid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">canvas — bg-background (--background)</p>
          <div className="flex h-24 items-center justify-center rounded-lg border border-border bg-background text-xs text-muted-foreground">
            recessed canvas
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">surface — bg-card (--card / --card-foreground)</p>
          <div className="flex h-24 items-center justify-center rounded-lg border border-border bg-card text-xs text-card-foreground">
            flat surface
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">elevated overlay — bg-popover + --shadow-elevated</p>
          <div
            className="flex h-24 items-center justify-center rounded-lg border border-border bg-popover text-xs text-popover-foreground"
            style={{ boxShadow: 'var(--shadow-elevated)' }}
          >
            elevated overlay
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">hairline — border-border / border-border-strong</p>
          <Stack gap={2} align="stretch" className="h-24 justify-center rounded-lg bg-card p-3 text-xs text-muted-foreground">
            <div className="rounded border border-border p-2">border-border (hairline)</div>
            <div className="rounded border border-border-strong p-2">border-border-strong</div>
          </Stack>
        </div>
      </Grid>

      <Grid columns={{ base: 1, sm: 2 }} gap={4}>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Old default — .glass-card (unchanged, still fully available)
          </p>
          <div className="glass-card flex h-20 items-center justify-center p-4 text-xs text-foreground">
            blur + glow, ships exactly as before
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            New default — flat bg-card + border-border
          </p>
          <div className="flex h-20 items-center justify-center rounded-2xl border border-border bg-card p-4 text-xs text-card-foreground">
            no blur, no gradient
          </div>
        </div>
      </Grid>
    </div>
  );
}

const DENSITY_STATES = ['comfortable', 'compact'] as const;

function VisualLanguageRoleDetailGallery(): JSX.Element {
  const [density, setDensity] = useState<(typeof DENSITY_STATES)[number]>('comfortable');
  return (
    <div className="space-y-8">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Typography roles</p>
        <div className="space-y-2 rounded-lg border border-border bg-card p-4">
          <p
            className="font-semibold text-card-foreground"
            style={{ fontSize: 'var(--text-label-size)', letterSpacing: 'var(--text-label-tracking)' }}
          >
            --text-label-size — dense functional label (table headers, chip/badge text)
          </p>
          <p className="text-muted-foreground" style={{ fontSize: 'var(--text-caption-size)' }}>
            --text-caption-size — secondary/meta caption text
          </p>
          <p
            className="font-semibold uppercase text-muted-foreground"
            style={{ fontSize: 'var(--text-caption-size)', letterSpacing: 'var(--text-eyebrow-tracking)' }}
          >
            --text-eyebrow-tracking — wide-tracking uppercase eyebrow / wordmark
          </p>
        </div>
      </div>

      <div>
        <Cluster gap={3} className="mb-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Density</p>
          <Inline as="fieldset" gap={2}>
            <legend className="sr-only">data-density</legend>
            {DENSITY_STATES.map((v) => (
              <Button key={v} size="sm" variant={density === v ? 'primary' : 'secondary'} onClick={() => setDensity(v)} aria-pressed={density === v}>
                {v}
              </Button>
            ))}
          </Inline>
        </Cluster>
        <div
          className="rounded-lg border border-border bg-card p-3"
          data-density={density}
          data-testid="density-demo-root"
        >
          {['Row A', 'Row B', 'Row C'].map((label) => (
            <div
              key={label}
              className="flex items-center rounded border border-border px-3 text-xs text-card-foreground"
              style={{ height: 'var(--density-row-current)', marginBottom: 'var(--density-gap-current)' }}
            >
              {label} — --density-row-current
            </div>
          ))}
          <div
            className="flex items-center rounded border border-border-strong px-3 text-xs text-card-foreground"
            style={{ height: 'var(--density-touch-target)' }}
            data-testid="density-touch-target-bar"
          >
            --density-touch-target — hard 48px floor, independent of density
          </div>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Focus</p>
        <p className="mb-2 text-xs text-muted-foreground">
          Tab to (or click) the button below — --focus-ring-width / --focus-ring-offset / --focus-ring-color
          name the ring index.css&apos;s .focus-ring (unchanged) already renders.
        </p>
        <Button variant="secondary" size="sm">
          Focus me
        </Button>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</p>
        <p className="mb-2 text-xs text-muted-foreground">
          Role names over the existing tokens (no new hue) — text-success / text-warning / text-destructive /
          text-muted-foreground. Text always carries the meaning alongside color (WCAG 1.4.1).
        </p>
        <Cluster gap={2}>
          <Badge tone="success">Positive · Approved</Badge>
          <Badge tone="warning">Caution · Needs review</Badge>
          <Badge tone="destructive">Critical · Failed</Badge>
          <Badge tone="neutral">Neutral · Draft</Badge>
        </Cluster>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Motion</p>
        <p className="mb-2 text-xs text-muted-foreground">
          Hover the block — background transitions over --motion-duration-base with --motion-ease-standard;
          every duration collapses to 0s under prefers-reduced-motion.
        </p>
        <div
          className="flex h-16 w-40 items-center justify-center rounded-lg border border-border bg-card text-xs text-card-foreground transition-colors hover:bg-muted"
          style={{
            transitionDuration: 'var(--motion-duration-base)',
            transitionTimingFunction: 'var(--motion-ease-standard)',
          }}
        >
          hover me
        </div>
      </div>
    </div>
  );
}

// Tabs (story 5-11b). Controlled by design, so the demo owns the state — which
// is exactly what a real consumer does, except a real one keeps it in the URL.
function TabsDemo(): JSX.Element {
  const [tab, setTab] = useState('inspection');
  return (
    <Tabs
      aria-label="Design gallery tabs demo"
      value={tab}
      onChange={setTab}
      fill
      items={[
        { id: 'inspection', label: 'Inspection' },
        { id: 'report', label: 'Report', badge: <Badge tone="neutral">2</Badge> },
        { id: 'locked', label: 'Disabled', disabled: true },
      ]}
      panelClassName="pt-4 text-sm text-muted-foreground"
    >
      {tab === 'inspection' && <p>The read-only answers and evidence pane.</p>}
      {tab === 'report' && <p>The deterministic draft report pane.</p>}
    </Tabs>
  );
}

// Story 2-147ub — "Navigation and disclosure compositions" Section's own
// Tabs stress proof: six long, un-abbreviated labels (more than any real
// tablist in the tree today) so the tablist's `overflow-x-auto` scroll
// (story 2-147c) has something genuine to scroll at the ResponsivePreview's
// 390px frame, rather than `fill`'s equal-width stretch (TabsDemo above)
// which never triggers it. `fill` is intentionally omitted here — this is
// the "more tabs than fit" case, not the "few tabs, stretch to the row"
// case, and the two are not the same demo.
function NavDisclosureTabsStressDemo(): JSX.Element {
  const [tab, setTab] = useState('overview');
  const items = [
    { id: 'overview', label: 'Overview' },
    { id: 'findings', label: 'Findings & observations' },
    { id: 'evidence', label: 'Evidence & attachments' },
    { id: 'history', label: 'Version history' },
    { id: 'access', label: 'Share & access' },
    { id: 'settings', label: 'Advanced settings' },
  ];
  return (
    <Tabs aria-label="Navigation stress tabs demo" value={tab} onChange={setTab} items={items} panelClassName="pt-3 text-sm text-muted-foreground">
      <p>Selected: {items.find((i) => i.id === tab)?.label}</p>
    </Tabs>
  );
}

// SegmentedControl (story 2-139). Controlled by design, same reasoning as
// TabsDemo above — a real consumer keeps `value` in URL state (G-25); the
// gallery demo owns local state only because it has no URL of its own.
function SegmentedControlDemo(): JSX.Element {
  const [view, setView] = useState<'list' | 'cards'>('list');
  const [density, setDensity] = useState<'comfortable' | 'compact' | 'disabled'>('comfortable');
  return (
    <div className="space-y-4">
      <Cluster gap={3}>
        <SegmentedControl
          aria-label="Design gallery view-mode demo"
          value={view}
          onChange={setView}
          options={[
            { value: 'list', label: 'List', icon: <ListBulletIcon className="h-4 w-4" aria-hidden="true" /> },
            { value: 'cards', label: 'Cards', icon: <Squares2X2Icon className="h-4 w-4" aria-hidden="true" /> },
          ]}
        />
        <span className="text-sm text-muted-foreground" data-testid="segmented-control-demo-value">
          Rendering: {view}
        </span>
      </Cluster>
      {/* A disabled option — proves keyboard traversal skips it (Home/End and
          arrow-wrap only ever land on a selectable option). */}
      <SegmentedControl
        aria-label="Design gallery disabled-option demo"
        value={density}
        onChange={setDensity}
        options={[
          { value: 'comfortable', label: 'Comfortable' },
          { value: 'compact', label: 'Compact' },
          { value: 'disabled', label: 'Unavailable', disabled: true },
        ]}
      />
    </div>
  );
}

// Workspace header (story 2-147e — P10 §2's header-anatomy kind 3 of 5).
// UPDATED story 2-147sb: this demo used to hand-roll the shape as a SLOT
// CONTRACT (no primitive existed yet — `2-147i` was named as a future
// consumer, not a builder). `2-147sb` builds the real `WorkspaceHeader`
// component (`components/ui/Headers.tsx`) — this demo now composes it
// directly instead of re-deriving its markup, the same "worked example, not
// a rebuild" convergence `2-147e` already established for `ContextHeader`/
// `RailPanel` below. `WorkspaceHeader` renders the identical shape this
// demo always demonstrated: identity (back-link + truncating title) vs. two
// independently-wrapping control clusters (tools; save/lifecycle),
// separated by a hairline divider at `sm`+ — see `Headers.tsx`'s own header
// comment for the full slot contract and a11y reasoning (headingLevel
// defaults to 1 for a real page's own `<h1>`; this demo passes 2 so the
// gallery doesn't grow a second page-level h1).
function WorkspaceHeaderDemo(): JSX.Element {
  const [view, setView] = useState<'build' | 'preview'>('build');
  return (
    <WorkspaceHeader
      headingLevel={2}
      title="Pre-purchase building inspection"
      back={<Button variant="secondary" size="sm">← Templates</Button>}
      tools={
        <SegmentedControl
          aria-label="Design gallery workspace-header view demo"
          value={view}
          onChange={setView}
          options={[
            { value: 'build', label: 'Build' },
            { value: 'preview', label: 'Preview' },
          ]}
        />
      }
      lifecycle={
        <>
          <Badge tone="neutral">Draft</Badge>
          <SaveStateIndicator kind="saved">Saved just now · revision 4</SaveStateIndicator>
          <Button size="sm">Save changes</Button>
        </>
      }
    />
  );
}

// ------------------------------------------------------------------
// Layout recipes (story 2-147f, P10 §1.1/§2) — assembly demos for the two
// families with no existing gallery composition to point at. Collection and
// Workspace are demonstrated by cross-reference instead (see the "Layout
// recipes" Section's own hint below for the map) — their real shapes
// (EntityListPage's bg-card PageHeader + the --content-operational table,
// AppFrame's rail/canvas/panel + the workspace-header demo above) are
// already live in this file; rebuilding them a second time here would be
// the exact duplication `2-147e`'s own "reuse the worked example" precedent
// warns against.
// ------------------------------------------------------------------

// Dashboard recipe: bare PageHeader (kind #2, story 2-130) + a reflowing KPI
// grid — DashboardPage.tsx's own real composition (space-y-6 root, no
// AppFrame, no width override of its own). `KpiCard` is DashboardPage's own
// real, presentational (no query/domain import) component — reused here
// rather than approximated, the same "real primitive, not a knockoff" rule
// AppFrame's own gallery demo already follows for RailPanel/PageHeader.
function DashboardRecipeDemo(): JSX.Element {
  const stress = CONTENT_STRESS_CASES[1]!; // localization-expansion — proves the freshness line wraps rather than clips or pushes actions off
  return (
    <div className="space-y-4">
      <PageHeader
        headingLevel={2}
        title="Dashboard"
        description={`Updated 2 minutes ago · ${stress.description}`}
        actions={<Button size="sm">New inspection</Button>}
      />
      <Grid columns={{ base: 1, md: 4 }} gap={4}>
        <KpiCard title="Sites" value="128" data-testid="recipe-dashboard-kpi-sites" />
        <KpiCard title="Active inspections" value="34" data-testid="recipe-dashboard-kpi-active" />
        <KpiCard title="In review" value="6" data-testid="recipe-dashboard-kpi-review" />
        <KpiCard title="Reports" value="512" data-testid="recipe-dashboard-kpi-reports" />
      </Grid>
    </div>
  );
}

// Workflow recipe: the family's own two real, DIVERGENT shapes, named rather
// than silently converged (no production route migrates in this story).
// Shape A mirrors ReportDetailPage.tsx (PageHeader kind #2 + a plain grid,
// RailPanel as the panel — no AppFrame). Shape B mirrors ReviewDetailPage.tsx
// (a bespoke <header><h1> never wired to PageHeader — a real, named drift,
// the same class of finding 2-147e recorded for EntityListPage's pre-story
// header — plus StatusChip and the lifecycle-transition affordance
// `InspectionLifecycleBar` owns live; not mounted here since it is a real
// query-backed domain component with no static-data mode, same reason
// StatusStepper's own gallery Section states for why it demos a presentational
// analog instead of that bar). Neither shape's panel column traces to a named
// 2-131 token — see the Section hint below for why, and why this demo
// deliberately uses plain fractional grid columns instead of pretending one
// exists.
function WorkflowRecipeDemo(): JSX.Element {
  const stress = CONTENT_STRESS_CASES[0]!;
  return (
    <div className="space-y-8">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Shape A — ReportDetailPage: PageHeader (chips + toolbar) + a plain grid, RailPanel as the panel
        </p>
        <div className="space-y-4">
          <PageHeader
            headingLevel={2}
            title={stress.title}
            description={stress.address}
            chips={
              <>
                <StatusChip tone="success" label="Approved" value="approved" />
                <Badge tone="neutral">v3</Badge>
              </>
            }
            actions={<Button size="sm">Download</Button>}
          />
          <Grid columns={{ base: 1, lg: 3 }} gap={4}>
            <div className="rounded-hairline border border-dashed border-border bg-card p-4 text-xs text-muted-foreground lg:col-span-2">
              Report content — the primary reading surface
            </div>
            <div className="lg:col-span-1">
              <RailPanel title="Share access" action={<Button variant="ghost" size="sm">New link</Button>}>
                <p className="text-sm text-muted-foreground">
                  Occasional-management content beside the record — see the RailPanel section above.
                </p>
              </RailPanel>
            </div>
          </Grid>
        </div>
      </div>
      <div className="border-t border-border pt-6">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Shape B — ReviewDetailPage: a bespoke header (not yet on PageHeader) + StatusChip + the blocker stated
          before the action is offered
        </p>
        <div className="space-y-3 rounded-hairline border border-border bg-card p-4">
          <Cluster justify="between" gap={3}>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-foreground">Review</h2>
              <StatusChip tone="warning" label="In review" value="in_review" />
            </div>
            <Inline gap={2}>
              <Button variant="secondary" size="sm">Request changes</Button>
              <Button size="sm">Approve</Button>
            </Inline>
          </Cluster>
          <Callout tone="info" title="Someone else has to approve this">
            This workspace separates who prepares work from who signs it off — the blocker is visible before the
            action is offered, never discovered only after it is refused.
          </Callout>
        </div>
      </div>
    </div>
  );
}

// Administration recipe: bare PageHeader (kind #2, same reasoning as
// Dashboard — a one-off settings page has no adjacent list to visually pair
// the header with) + a paired form/summary grid. OrgAccessPage.tsx's own two
// real instances of this split (`lg:grid-cols-[1fr_20rem]` /
// `lg:grid-cols-[18rem_1fr]`) are literal, un-tokenized arbitrary values —
// see the Section hint below for that finding. This demo uses the
// token-free fractional escape hatch instead of reproducing the literal.
function AdministrationRecipeDemo(): JSX.Element {
  return (
    <div className="space-y-4">
      <PageHeader
        headingLevel={2}
        title="Organization access"
        description="Roles, teams and who can act as whom."
        actions={<Button size="sm">Invite member</Button>}
      />
      <Grid columns={{ base: 1, lg: 2 }} gap={6}>
        <div className="rounded-hairline border border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground">Members</h3>
          <p className="mt-1 text-sm text-muted-foreground">The paired form/summary content this family's real pages compose.</p>
        </div>
        <div className="rounded-hairline border border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground">Summary</h3>
          <p className="mt-1 text-sm text-muted-foreground">Occasional context beside the primary form — same role RailPanel plays elsewhere.</p>
        </div>
      </Grid>
    </div>
  );
}

// External recipe: no AppShell to inherit a clamp from (these routes render
// outside it entirely) — this demo uses --content-reading for real, the
// prose-measure fit for marketing/report-reading content, as the
// recommendation PublicLayout.tsx/SharedReportPage.tsx have not yet adopted
// (both still reach for literal max-w-6xl/max-w-3xl — see the Section hint
// below).
function ExternalRecipeDemo(): JSX.Element {
  return (
    <div className="mx-auto space-y-4 rounded-lg border border-border bg-card p-6" style={{ maxWidth: 'var(--content-reading)' }}>
      <p className="text-sm font-semibold text-foreground">Acme Property Group</p>
      <h2 className="text-xl font-semibold text-foreground">Shared report — no authenticated shell chrome</h2>
      <p className="text-sm text-muted-foreground">
        Centered, single-column, prose-measure width — the simplest of the six recipes by construction: no rail,
        no panel, nothing to collapse.
      </p>
      <Cluster justify="end" gap={2}>
        <Button variant="secondary" size="sm">Print</Button>
        <Button size="sm">Download PDF</Button>
      </Cluster>
    </div>
  );
}

// ------------------------------------------------------------------
// Executable page layouts (story 2-147sb, P10 §2.6) — real
// `DashboardLayout`/`CollectionLayout`/`WorkflowLayout`/`WorkspaceLayout`/
// `AdministrationLayout`/`ExternalLayout` components
// (`components/ui/PageLayouts.tsx`), each demonstrated below with a genuine
// content-stress case (long titles from `CONTENT_STRESS_CASES`, many
// actions to prove `HeaderActions`' own overflow collapse, long list/table
// content) rather than a lorem-ipsum happy path — the same content-stress
// discipline `2-147a`/`2-147k` established earlier in this lane. Distinct
// from the "Layout recipes" Section above, which stays `2-147f`'s own
// DOCUMENTATION-only prose/demo pass (untouched by this story); these demos
// compose the REAL, IMPORTABLE components that now exist alongside it. Not
// a page migration (`2-147w*`'s job) — gallery-only, same as every other
// primitive/composite this lane has shipped.
// ------------------------------------------------------------------

function DashboardLayoutDemo(): JSX.Element {
  const stress = CONTENT_STRESS_CASES[1]!;
  const actions: ActionDescriptor[] = [
    { id: 'invite', label: 'Invite teammate', variant: 'secondary' },
    { id: 'export', label: 'Export activity', variant: 'secondary' },
    { id: 'new', label: 'New inspection', variant: 'primary', pinned: true },
  ];
  return (
    <DashboardLayout
      header={{
        headingLevel: 2,
        title: 'Dashboard',
        description: `Updated 2 minutes ago · ${stress.description}`,
        actions: <HeaderActions actions={actions} />,
      }}
      data-testid="layout-dashboard"
    >
      <Grid columns={{ base: 1, md: 4 }} gap={4}>
        <KpiCard title="Sites" value="128" data-testid="layout-dashboard-kpi-sites" />
        <KpiCard title="Active inspections" value="34" data-testid="layout-dashboard-kpi-active" />
        <KpiCard title="In review" value="6" data-testid="layout-dashboard-kpi-review" />
        <KpiCard title="Reports" value="512" data-testid="layout-dashboard-kpi-reports" />
      </Grid>
    </DashboardLayout>
  );
}

function CollectionLayoutDemo(): JSX.Element {
  const stress = CONTENT_STRESS_CASES[0]!;
  return (
    <CollectionLayout
      header={{
        headingLevel: 2,
        title: 'Sites',
        description: 'Every property your org inspects.',
        actions: <Button size="sm">New site</Button>,
      }}
      filters={
        <>
          <Input placeholder="Search sites" aria-label="Search sites" className="max-w-xs" />
          <Button variant="secondary" size="sm">Filters</Button>
        </>
      }
      footer={<Button variant="secondary" size="sm">Load more</Button>}
      data-testid="layout-collection"
    >
      <Table>
        <THead>
          <TR>
            <TH>Address</TH>
            <TH>Status</TH>
            <TH>Last inspection</TH>
          </TR>
        </THead>
        <TBody>
          <TR>
            <TD className="max-w-xs truncate">{stress.address}</TD>
            <TD><StatusChip tone="success" label="Compliant" value="compliant" /></TD>
            <TD>22 Aug 2026</TD>
          </TR>
          <TR>
            <TD className="max-w-xs truncate">13/883 Plenty Road, South Morang, Victoria 3752, Australia</TD>
            <TD><StatusChip tone="warning" label="Due soon" value="due_soon" /></TD>
            <TD>2 Jun 2026</TD>
          </TR>
        </TBody>
      </Table>
    </CollectionLayout>
  );
}

function WorkflowLayoutDemo(): JSX.Element {
  const stress = CONTENT_STRESS_CASES[0]!;
  return (
    <WorkflowLayout
      header={{
        headingLevel: 2,
        title: stress.title,
        description: stress.address,
        chips: (
          <>
            <StatusChip tone="success" label="Approved" value="approved" />
            <Badge tone="neutral">v3</Badge>
          </>
        ),
        actions: <Button size="sm">Download</Button>,
        toolbar: (
          <>
            <Button variant="ghost" size="sm">Print</Button>
            <Button variant="ghost" size="sm">Open</Button>
          </>
        ),
      }}
      panel={
        <RailPanel title="Share access" action={<Button variant="ghost" size="sm">New link</Button>}>
          <p className="text-sm text-muted-foreground">{stress.description}</p>
        </RailPanel>
      }
      data-testid="layout-workflow"
    >
      <div className="rounded-hairline border border-dashed border-border bg-card p-4 text-sm text-muted-foreground">
        {stress.description}
      </div>
    </WorkflowLayout>
  );
}

function WorkspaceLayoutDemo(): JSX.Element {
  const [view, setView] = useState<'build' | 'preview'>('build');
  const stress = CONTENT_STRESS_CASES[1]!;
  const saveActions: ActionDescriptor[] = [{ id: 'save', label: 'Save changes', variant: 'primary', pinned: true }];
  return (
    <div className="h-[420px] overflow-hidden rounded-2xl border border-border">
      <WorkspaceLayout
        header={{
          headingLevel: 2,
          title: `${stress.title} — pre-purchase building inspection template`,
          back: <Button variant="secondary" size="sm">← Templates</Button>,
          tools: (
            <SegmentedControl
              aria-label="Design gallery workspace-layout view demo"
              value={view}
              onChange={setView}
              options={[
                { value: 'build', label: 'Build' },
                { value: 'preview', label: 'Preview' },
              ]}
            />
          ),
          lifecycle: (
            <>
              <Badge tone="neutral">Draft</Badge>
              <SaveStateIndicator kind="saved">Saved just now · revision 4</SaveStateIndicator>
              <HeaderActions actions={saveActions} />
            </>
          ),
        }}
        rail={
          <div className="p-3">
            <Stack gap={1}>
              <Button variant="secondary" size="sm" className="justify-start">Sections</Button>
              <Button variant="ghost" size="sm" className="justify-start">Fields</Button>
              <Button variant="ghost" size="sm" className="justify-start">Logic</Button>
            </Stack>
          </div>
        }
        panel={
          <div className="p-3">
            <RailPanel title="Field settings">
              <p className="text-sm text-muted-foreground">Contextual inspector for the selected field.</p>
            </RailPanel>
          </div>
        }
        data-testid="layout-workspace"
      >
        <p className="p-6 text-sm text-muted-foreground">{stress.description}</p>
      </WorkspaceLayout>
    </div>
  );
}

function AdministrationLayoutDemo(): JSX.Element {
  const actions: ActionDescriptor[] = [
    { id: 'invite', label: 'Invite member', variant: 'primary', pinned: true },
    { id: 'export', label: 'Export members', variant: 'secondary' },
    { id: 'roles', label: 'Manage roles', variant: 'secondary' },
    { id: 'audit', label: 'View audit log', variant: 'secondary' },
  ];
  return (
    <AdministrationLayout
      header={{
        headingLevel: 2,
        title: 'Organization access — roles, teams and who can act as whom across every property your org inspects',
        description: 'Governs who can invite, approve and publish on behalf of the organization.',
        actions: <HeaderActions actions={actions} />,
      }}
      panel={
        <RailPanel title="Summary">
          <p className="text-sm text-muted-foreground">24 members · 4 teams · 3 roles</p>
        </RailPanel>
      }
      data-testid="layout-administration"
    >
      <div className="rounded-hairline border border-border bg-card p-5">
        <SectionHeader title="Members" description="Everyone with access to this organization." />
        <p className="mt-3 text-sm text-muted-foreground">
          Paired form/summary content this family's real pages compose — the token-sourced <code>Split</code>-based
          replacement for OrgAccessPage.tsx's own still-literal <code>lg:grid-cols-[1fr_20rem]</code>.
        </p>
      </div>
    </AdministrationLayout>
  );
}

function ExternalLayoutDemo(): JSX.Element {
  const stress = CONTENT_STRESS_CASES[0]!;
  return (
    <div className="rounded-2xl border border-border bg-muted/20 p-6">
      <ExternalLayout
        header={
          <Stack gap={1}>
            <p className="text-sm font-semibold text-foreground">Acme Property Group</p>
            <h2 className="text-xl font-semibold text-foreground">{stress.title}</h2>
          </Stack>
        }
        data-testid="layout-external"
      >
        <p className="text-sm text-muted-foreground">{stress.description}</p>
        <Cluster justify="end" gap={2}>
          <Button variant="secondary" size="sm">Print</Button>
          <Button size="sm">Download PDF</Button>
        </Cluster>
      </ExternalLayout>
    </div>
  );
}

// ------------------------------------------------------------------
// Surface & Card compositions — story 2-147ta (P10 §2.7 row 5, DOD). Closes
// the "Flat surface / card wrapper — Named but not yet built" gap the
// canonical matrix named. `Surface` is demonstrated directly (its four P10
// §1.3 visual roles); `Card`'s slot family is proven with one worked,
// full-slot composition (header badges + trailing actions, body prose, a
// divided footer — every action area through `2-147r`'s `CardActions`,
// never a raw `Button`); `MetricCard` and `EntityCard` are demonstrated as
// the two named card SHAPES built on top. `EntityCard` gets the full
// `StateMatrix` treatment (long-content/loading/error/locked/responsive) —
// the same non-layout-family worked precedent `GalleryScaffoldingProofDemo`
// already established for `Button` in 2-147p, applied here to a composite.
// ------------------------------------------------------------------
function SurfaceVariantsDemo(): JSX.Element {
  return (
    <VariantGrid
      columns={4}
      items={[
        {
          key: 'bare',
          label: 'bare — no visual treatment',
          children: (
            <Surface padding="md" data-testid="surface-bare">
              <p className="text-sm text-muted-foreground">Bare — layout only, no border/background of its own.</p>
            </Surface>
          ),
        },
        {
          key: 'flat',
          label: 'flat (default) — bg-card + border',
          children: (
            <Surface variant="flat" padding="md" data-testid="surface-flat">
              <p className="text-sm text-card-foreground">Flat — the default "this looks like a card" surface.</p>
            </Surface>
          ),
        },
        {
          key: 'elevated',
          label: 'elevated — bg-popover + shadow-elevated',
          children: (
            <Surface variant="elevated" padding="md" data-testid="surface-elevated">
              <p className="text-sm text-popover-foreground">Elevated overlay — menus, modals, dropdowns, tooltips.</p>
            </Surface>
          ),
        },
        {
          key: 'glass',
          label: 'glass — primary action / status / active AI work ONLY',
          children: (
            <Surface variant="glass" padding="md" data-testid="surface-glass">
              <p className="text-sm text-foreground">Glass — reserved, never the default.</p>
            </Surface>
          ),
        },
      ]}
    />
  );
}

function CardSlotFamilyDemo(): JSX.Element {
  return (
    <Card data-testid="card-slot-family-demo">
      <CardHeader
        title="12 Harbor St"
        subtitle="Routine rental inspection"
        badges={
          <>
            <StatusChip tone="warning" label="In review" value="in_review" />
            <Badge tone="neutral">Assigned</Badge>
          </>
        }
        actions={<CardActions actions={[{ id: 'edit', label: 'Edit', variant: 'secondary', onClick: () => undefined }]} />}
      />
      <CardBody>
        <p>
          Quarterly compliance walkthrough covering fire-door integrity, exit-signage illumination and common-area
          presentation, cross-referenced against last quarter&rsquo;s corrective actions.
        </p>
      </CardBody>
      <CardFooter>
        <CardActions
          actions={[
            { id: 'archive', label: 'Archive', variant: 'secondary', onClick: () => undefined },
            { id: 'delete', label: 'Delete', variant: 'destructive', onClick: () => undefined },
          ]}
        />
      </CardFooter>
    </Card>
  );
}

function MetricCardDemo(): JSX.Element {
  return (
    <VariantGrid
      columns={3}
      items={[
        {
          key: 'up',
          label: 'Positive trend',
          children: <MetricCard label="Sites" value="128" delta="+12%" trend="up" data-testid="metric-card-up" />,
        },
        {
          key: 'down',
          label: 'Negative trend',
          children: (
            <MetricCard
              label="Overdue"
              value="6"
              delta="-3%"
              trend="down"
              trendLabel="vs last week"
              data-testid="metric-card-down"
            />
          ),
        },
        {
          key: 'flat',
          label: 'No delta (delta omitted entirely)',
          children: <MetricCard label="Reports" value="512" data-testid="metric-card-flat" />,
        },
      ]}
    />
  );
}

function EntityCardStatesDemo(): JSX.Element {
  const stress = CONTENT_STRESS_CASES[0]!;
  const entityCardStateContent: Record<string, ReactNode> = {
    longContent: (
      <EntityCard
        title={stress.title}
        subtitle="Compliance inspection"
        badges={<StatusChip tone="warning" label="In review" value="in_review" />}
        description={stress.description}
        meta={<span className="text-xs text-muted-foreground">{stress.address}</span>}
        actions={[
          { id: 'open', label: 'Open', variant: 'primary', onClick: () => undefined },
          { id: 'archive', label: 'Archive', variant: 'secondary', onClick: () => undefined },
        ]}
        onOpen={() => undefined}
        data-testid="entity-card-state-long-content"
      />
    ),
    loading: (
      <Card data-testid="entity-card-state-loading">
        <CardHeader title={<Skeleton className="h-5 w-40" />} subtitle={<Skeleton className="h-3 w-24" />} />
        <CardBody>
          <Stack gap={2}>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
          </Stack>
        </CardBody>
      </Card>
    ),
    error: (
      <Card data-testid="entity-card-state-error">
        <CardHeader title="12 Harbor St" subtitle="Routine rental" />
        <CardBody>
          <Callout tone="danger" title="Couldn't load this record">
            Check your connection and try again.
          </Callout>
        </CardBody>
        <CardFooter>
          <CardActions actions={[{ id: 'retry', label: 'Retry', variant: 'destructive', onClick: () => undefined }]} />
        </CardFooter>
      </Card>
    ),
    locked: (
      <EntityCard
        title="9 Quay St"
        subtitle="Pre-settlement"
        badges={<Badge tone="neutral">Locked</Badge>}
        description="You don't have permission to open this record."
        actions={[
          {
            id: 'request',
            label: 'Request access',
            variant: 'secondary',
            disabled: true,
            disabledReason: "You don't have permission to open this record.",
            onClick: () => undefined,
          },
        ]}
        data-testid="entity-card-state-locked"
      />
    ),
    responsive: (
      <ResponsivePreview>
        <EntityCard
          title={stress.title}
          subtitle="Compliance inspection"
          badges={<StatusChip tone="warning" label="In review" value="in_review" />}
          actions={[{ id: 'open', label: 'Open', variant: 'primary', onClick: () => undefined }]}
          onOpen={() => undefined}
        />
      </ResponsivePreview>
    ),
  };

  const entityCardStates = LAYOUT_FAMILY_STATE_SET.map((s) => ({
    ...s,
    fullWidth: s.key === 'responsive',
    children: entityCardStateContent[s.key],
  }));

  return <StateMatrix states={entityCardStates} />;
}

// LoadMorePager states (story 2-147tb) — the three real states every one of
// its seven real page consumers can be in: another page to fetch (idle),
// a page currently in flight (loading — Button's own spinner/aria-busy),
// and the collection fully drained (no button, count-only). Static demo
// state (a local counter), not `useCursorList`, so the shot is deterministic
// for `story-2-134`'s visual baseline — `CursorListGallery` above already
// owns the live-network proof of the underlying hook; this demonstrates the
// RENDER contract only.
function LoadMorePagerStatesDemo(): JSX.Element {
  const [shown, setShown] = useState(6);
  const total = 14;
  return (
    <VariantGrid
      columns={3}
      items={[
        {
          key: 'more',
          label: 'Another page exists — idle',
          children: <LoadMorePager shownCount={shown} total={total} hasMore onLoadMore={() => setShown((n) => Math.min(n + 4, total))} />,
        },
        {
          key: 'loading',
          label: 'A page is in flight',
          children: <LoadMorePager shownCount={shown} total={total} hasMore isLoadingMore onLoadMore={() => undefined} />,
        },
        {
          key: 'exhausted',
          label: 'Collection fully drained — no button, count-only',
          children: <LoadMorePager shownCount={total} total={total} hasMore={false} onLoadMore={() => undefined} />,
        },
      ]}
    />
  );
}

// The full collection composition, worked end to end (story 2-147tb) — the
// canonical answer to P10 §2.7 row 7 ("Per-collection keyword/quick
// filters" → `FilterBar`; "View-mode toggle / result counts" →
// `SegmentedControl` + `LoadMorePager`; "Pagination" → `LoadMorePager`) laid
// over the §2.4 collection form rule's ROWS shape — "one primary identity
// plus a short status/meta line and one or two row actions... the default
// for a working queue" — exactly what this demo's records are. Every row's
// action area composes `RowActions` (2-147r), never a raw `Button`, per this
// story's own scope note (CLAUDE.md, DOD §4 G-22). This is deliberately the
// SAME shape `2-147ta`'s card family was told to "agree with, not
// contradict": these records carry one status pill and one meta caption —
// content that does NOT justify a card (no wrapping description, no chips,
// no imagery) — so Rows is the correct shape here, Cards would be
// decoration. State (keyword/status/view/shown-count) is local `useState`
// only for this static demo; a real page owns the same shape via
// `useUrlState` (G-25) — see `SitesPage.tsx`/`ItemsListPage.tsx` for the
// real wiring this demo intentionally mirrors without a live query.
const COLLECTION_DEMO_RECORDS = [
  { id: 'r1', title: 'Quarterly fire safety', meta: 'Due in 3 days', tone: 'warning' as const, label: 'Scheduled' },
  { id: 'r2', title: 'Electrical switchboard', meta: 'Failed — reinspection required', tone: 'destructive' as const, label: 'Failed' },
  { id: 'r3', title: 'Roof access ladder', meta: 'Completed 2 days ago', tone: 'success' as const, label: 'Passed' },
  { id: 'r4', title: 'Fire extinguisher service', meta: 'In progress', tone: 'primary' as const, label: 'In progress' },
];

function CollectionCompositionDemo(): JSX.Element {
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('');
  const [view, setView] = useState<'list' | 'cards'>('list');
  const [shown, setShown] = useState(3);

  const filtered = COLLECTION_DEMO_RECORDS.filter(
    (r) =>
      r.title.toLowerCase().includes(keyword.trim().toLowerCase()) && (status === '' || r.label === status)
  );
  const paged = filtered.slice(0, shown);
  const hasMore = paged.length < filtered.length;

  return (
    <div className="space-y-3" data-testid="collection-composition-demo">
      <Cluster justify="between" gap={3}>
        <FilterBar
          clearable={Boolean(status) || Boolean(keyword)}
          onClear={() => {
            setStatus('');
            setKeyword('');
          }}
          keyword={{
            value: keyword,
            onChange: (e) => setKeyword(e.target.value),
            onClear: () => setKeyword(''),
            placeholder: 'Search inspections…',
          }}
        >
          <Select aria-label="Status filter" size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Failed">Failed</option>
            <option value="Passed">Passed</option>
            <option value="In progress">In progress</option>
          </Select>
        </FilterBar>
        <SegmentedControl<'list' | 'cards'>
          aria-label="View"
          value={view}
          onChange={setView}
          size="sm"
          options={[
            { value: 'list', label: 'List' },
            { value: 'cards', label: 'Cards' },
          ]}
        />
      </Cluster>

      {paged.length === 0 ? (
        <EmptyState title="Nothing matches those filters" description="Clear the filters to see every record." />
      ) : view === 'list' ? (
        <CompactRowList>
          {paged.map((row) => (
            <CompactRow
              key={row.id}
              title={row.title}
              meta={
                <>
                  <StatusChip tone={row.tone} label={row.label} value={row.label} />
                  <RowActions
                    actions={[
                      { id: 'open', label: 'Open', onClick: () => undefined },
                      { id: 'reassign', label: 'Reassign', onClick: () => undefined },
                      { id: 'archive', label: 'Archive', variant: 'destructive', onClick: () => undefined },
                    ]}
                  />
                </>
              }
              subtitle={row.meta}
            />
          ))}
        </CompactRowList>
      ) : (
        <Grid columns={{ base: 1, sm: 2 }} gap={3}>
          {paged.map((row) => (
            <Card key={row.id} data-testid={`collection-demo-card-${row.id}`}>
              <CardHeader title={row.title} badges={<StatusChip tone={row.tone} label={row.label} value={row.label} />} />
              <CardBody>
                <p className="text-sm text-muted-foreground">{row.meta}</p>
              </CardBody>
              <CardFooter>
                <CardActions actions={[{ id: 'open', label: 'Open', variant: 'primary', onClick: () => undefined }]} />
              </CardFooter>
            </Card>
          ))}
        </Grid>
      )}

      <LoadMorePager
        shownCount={paged.length}
        total={filtered.length}
        hasMore={hasMore}
        onLoadMore={() => setShown((n) => n + 3)}
      />
    </div>
  );
}

// `data-testid="gallery-section"` (story 2-147d): once this wrapper converted from the
// distinctive `.glass-card` to the widely-shared `.bg-card` flat-surface class, that class
// stopped being a unique selector — many real product `<section>` elements (RailPanel and
// every other flat-surface conversion this story made) now legitimately carry `bg-card` too,
// so `story-2-134-visual-baseline.spec.mjs`'s old `main section.bg-card` locator started
// matching unrelated sections embedded as live demos, hanging while looking for an `<h2>`
// that only a real gallery Section() has. A stable test hook, decoupled from styling, is the
// fix — never re-couple this spec's locator to a class name again.
function Section({ title, hint, children }: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-hairline border border-border bg-card p-6" data-testid="gallery-section">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

// ------------------------------------------------------------------
// FormField `hideLabel` (story 2-95b) — a dense, chrome-free composer row
// (mirrors `AgentPanel.tsx`'s real message composer: one input + a Send
// button in a fixed-height footer bar, where a second visible label line
// above the input would break the row). The label is NEVER removed from the
// accessibility tree — `sr-only`, not `aria-hidden` — only hidden from
// sighted layout; a screen reader still announces "Message" as the
// control's name. The schema is built from a variable, not a literal
// `z.object({...})` (the SAME dynamic-regime idiom the runtime FormSchema-v2
// demo below uses) — there is no generated contract for this: it isn't
// submitted to a REST endpoint, just a local demo of the composer shape.
// ------------------------------------------------------------------
// Hoisted (not built inline in the component) so the shape has a stable
// identity across renders — `useMemo`'s `[]` deps below are then honest,
// not a suppressed exhaustive-deps violation.
const messageShape = { message: z.string().min(1) };

function HideLabelComposerDemo(): JSX.Element {
  const schema = useMemo(() => z.object(messageShape), []);
  const form = useZodForm(schema, { defaultValues: { message: '' } });
  const [sent, setSent] = useState<string | null>(null);

  return (
    <Form
      form={form}
      onSubmit={(values) => {
        setSent(values.message);
        form.reset({ message: '' });
      }}
      className="rounded-hairline border border-border bg-card p-2"
    >
      <Inline gap={2}>
        <FormField name="message" label="Message" hideLabel className="flex-1">
          {(field) => <Input placeholder="Ask the assistant…" className="w-full" {...field} />}
        </FormField>
        <FormSubmit size="sm">Send</FormSubmit>
      </Inline>
      {sent && <p className="mt-1 text-xs text-muted-foreground">Sent: {sent}</p>}
    </Form>
  );
}

// ------------------------------------------------------------------
// Form controls (story 2-96, G-22/G-26): the non-evidence FormSchema-v2
// field types the DS didn't cover before this story (checkbox, switch,
// radio group, multiselect, rating, date/datetime). Rendered standalone
// here — valid/invalid/disabled/required, the same shape the "Inputs"
// block above already uses for Input/Textarea/Select — with REAL
// interactive state (useState, not defaultValue) because these controls
// are always controlled (Checkbox/Switch/RadioGroup/MultiSelect/Rating all
// hard-code their DOM `checked`/pressed state from `value`), and because
// keyboard operability needs a real onChange to prove Space/Enter/arrows
// actually change something, not just render a static glyph. The identical
// controls get composed inside a real `FormField` (control="inline"/
// "group") in the "Forms" section's dynamic-regime demo below, which is
// where the aria-describedby/error/focus wiring is actually proven —
// this block only proves the primitives' own props (invalid/disabled) and
// interaction, matching how Input/Textarea/Select are demonstrated above
// (raw, not FormField-wrapped).
// ------------------------------------------------------------------
function FormControlsDemo(): JSX.Element {
  const [checkboxValid, setCheckboxValid] = useState(false);
  const [checkboxRequired, setCheckboxRequired] = useState(false);
  const [switchValid, setSwitchValid] = useState(false);
  const [switchRequired, setSwitchRequired] = useState(false);
  const [radioValid, setRadioValid] = useState('a');
  const [radioRequired, setRadioRequired] = useState<string | undefined>(undefined);
  const [multiValid, setMultiValid] = useState<string[]>(['electrical']);
  const [multiRequired, setMultiRequired] = useState<string[]>([]);
  const [ratingValid, setRatingValid] = useState<number | undefined>(3);
  const [ratingRequired, setRatingRequired] = useState<number | undefined>(undefined);
  const [dateValid, setDateValid] = useState('2026-08-01');
  const [dateTimeValid, setDateTimeValid] = useState('2026-08-01T09:00');
  const [orderedLevels, setOrderedLevels] = useState<{ label: string }[]>([
    { label: 'Low' },
    { label: 'Medium' },
    { label: 'High' },
  ]);
  const [pinnableWidgets, setPinnableWidgets] = useState<PinnableOrderListItem[]>([
    { key: 'kpiSummary', label: 'Overview counts', description: 'Sites, active inspections, in review and reports.', pinned: true },
    { key: 'nextWork', label: 'Next up', description: 'Your own next work, ranked by urgency.', pinned: true },
    {
      key: 'recentReports',
      label: 'Recently approved',
      description: 'Reports with a signed-off version, ready to share.',
      pinned: false,
      disabled: true,
      disabledReason: "This isn't part of your role.",
    },
    { key: 'activationChecklist', label: 'Get set up', description: 'Setup steps from your first Site to sharing a report.', pinned: true },
  ]);

  const radioOptions = [
    { value: 'a', label: 'Option A' },
    { value: 'b', label: 'Option B' },
  ];
  const multiOptions = [
    { value: 'electrical', label: 'Electrical' },
    { value: 'structural', label: 'Structural' },
  ];

  // Shared visual for the "required" cosmetic marker — mirrors FormField's
  // sibling-asterisk pattern (Form.tsx) since these demos render outside a
  // FormField (see the block comment above for why).
  const requiredMark = (
    <span aria-hidden="true" className="text-destructive">
      *
    </span>
  );

  return (
    <div className="mt-6 space-y-6 border-t border-border pt-6">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Checkbox</h3>
        <Cluster gap={6} className="mt-2">
          <Inline as="span" gap={2}>
            <Checkbox
              aria-label="Checkbox — valid"
              value={checkboxValid}
              onChange={(e) => setCheckboxValid(e.target.checked)}
            />
            <span className="text-sm text-foreground">Valid</span>
          </Inline>
          <Inline as="span" gap={2}>
            <Checkbox aria-label="Checkbox — invalid" invalid value={false} onChange={() => {}} />
            <span className="text-sm text-foreground">Invalid</span>
          </Inline>
          <Inline as="span" gap={2}>
            <Checkbox aria-label="Checkbox — disabled" disabled value={true} onChange={() => {}} />
            <span className="text-sm text-foreground">Disabled</span>
          </Inline>
          <Inline as="span" gap={2}>
            <Checkbox
              aria-label="Checkbox — required"
              value={checkboxRequired}
              onChange={(e) => setCheckboxRequired(e.target.checked)}
            />
            <Inline as="span" gap={1} className="text-sm text-foreground">Required{requiredMark}</Inline>
          </Inline>
          <Inline as="span" gap={2}>
            <Checkbox aria-label="Checkbox — small" size="sm" value={true} onChange={() => {}} />
            <span className="text-sm text-foreground">Small</span>
          </Inline>
        </Cluster>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Switch</h3>
        <Cluster gap={6} className="mt-2">
          <Inline as="span" gap={2}>
            <Switch aria-label="Switch — valid" value={switchValid} onChange={setSwitchValid} />
            <span className="text-sm text-foreground">Valid</span>
          </Inline>
          <Inline as="span" gap={2}>
            <Switch aria-label="Switch — invalid" invalid value={false} onChange={() => {}} />
            <span className="text-sm text-foreground">Invalid</span>
          </Inline>
          <Inline as="span" gap={2}>
            <Switch aria-label="Switch — disabled" disabled value={true} onChange={() => {}} />
            <span className="text-sm text-foreground">Disabled</span>
          </Inline>
          <Inline as="span" gap={2}>
            <Switch aria-label="Switch — required" value={switchRequired} onChange={setSwitchRequired} />
            <Inline as="span" gap={1} className="text-sm text-foreground">Required{requiredMark}</Inline>
          </Inline>
          <Inline as="span" gap={2}>
            <Switch aria-label="Switch — small" size="sm" value={true} onChange={() => {}} />
            <span className="text-sm text-foreground">Small</span>
          </Inline>
          <Switch value={switchValid} onChange={setSwitchValid} label="With built-in label" />
        </Cluster>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Radio group</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Native same-name radios in a plain div — no <code>role=&quot;radiogroup&quot;</code>; the
          accessible group name comes from the <code>&lt;fieldset&gt;</code>/<code>&lt;legend&gt;</code>{' '}
          a real <code>FormField control=&quot;group&quot;</code> supplies (proven in the Forms section
          below). Here each demo variant supplies its own fieldset since there is no FormField wrapping
          it.
        </p>
        <Grid columns={{ base: 1, sm: 2 }} gap={4} className="mt-2">
          <fieldset>
            <legend className="text-xs text-muted-foreground">Valid</legend>
            <RadioGroup
              name="ds-radio-valid"
              value={radioValid}
              onChange={(e) => setRadioValid(e.target.value)}
              options={radioOptions}
            />
          </fieldset>
          <fieldset>
            <legend className="text-xs text-muted-foreground">Invalid</legend>
            <RadioGroup name="ds-radio-invalid" value={undefined} onChange={() => {}} options={radioOptions} invalid />
          </fieldset>
          <fieldset>
            <legend className="text-xs text-muted-foreground">Disabled</legend>
            <RadioGroup name="ds-radio-disabled" value="a" onChange={() => {}} options={radioOptions} disabled />
          </fieldset>
          <fieldset>
            <Inline as="legend" gap={1} className="text-xs text-muted-foreground">Required{requiredMark}</Inline>
            <RadioGroup
              name="ds-radio-required"
              value={radioRequired}
              onChange={(e) => setRadioRequired(e.target.value)}
              options={radioOptions}
            />
          </fieldset>
        </Grid>
        <div className="mt-4">
          <p className="text-xs text-muted-foreground">Horizontal, small</p>
          <RadioGroup
            name="ds-radio-horizontal"
            size="sm"
            horizontal
            value={radioValid}
            onChange={(e) => setRadioValid(e.target.value)}
            options={radioOptions}
          />
        </div>
      </div>

      {/* Its OWN block, not a fourth state inside "Radio group" above. Story
          2-96's gallery gate counts the fieldsets in the Radio group block to
          prove every state is present; a new fieldset dropped in there would
          break that count and, worse, would read as a fifth *state* of the
          control rather than what it is — a distinct capability of the option
          shape (story 3-3b). */}
      <div>
        <h3 className="text-sm font-semibold text-foreground">Radio group — rich options</h3>
        <div className="mt-1">
          <p className="text-xs text-muted-foreground">
            Rich options (story 3-3b): <code>description</code> is wired to the radio via{' '}
            <code>aria-describedby</code>, and <code>content</code> takes chips/badges inside the
            label. This is how a DISABLED option carries its REASON — it stays in the list, greyed and
            unselectable, with the reason both visible and announced, rather than being hidden (the
            TypePicker&apos;s &quot;inaccessible but known&quot; rule, P03 §7.2).
          </p>
          <fieldset className="mt-2">
            <legend className="text-xs text-muted-foreground">Inspection type</legend>
            <RadioGroup
              name="ds-radio-rich"
              value={radioValid}
              onChange={(e) => setRadioValid(e.target.value)}
              options={[
                {
                  value: 'a',
                  label: 'Annual fire safety',
                  content: (
                    <Cluster as="span" gap={1} className="mt-1">
                      <Badge tone="neutral">48 h SLA</Badge>
                      <OptionChip small option={{ label: 'High', color: '#b91c1c' }} />
                    </Cluster>
                  ),
                },
                {
                  value: 'b',
                  label: 'Structural review',
                  disabled: true,
                  description: 'Your role may not perform this type of inspection. Requires Manager.',
                  content: (
                    <Cluster as="span" gap={1} className="mt-1">
                      <Badge tone="neutral">2 templates</Badge>
                    </Cluster>
                  ),
                },
              ]}
            />
          </fieldset>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Ordered list</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          <code>OrderedListField</code> (story 3-3b) — a form value whose ORDER is part of its meaning
          (first consumer: an inspection type&apos;s severity scale). An <code>&lt;ol&gt;</code> gives
          native position-in-set semantics; every control is a real button, so the whole thing is
          keyboard-only operable with visible focus (there is no drag-only path to miss). Row-qualified
          accessible names (&quot;Move High up&quot;), first/last move buttons disabled rather than
          absent, and the move is announced through a polite live region — reordering is otherwise
          silent to assistive tech. Focus follows the row that MOVED, switching to that row&apos;s
          opposite-direction button at the two boundaries, because focus cannot land on the button
          the boundary just disabled and would otherwise be left on a node that is now a different
          row.
        </p>
        <div className="mt-2 max-w-xl">
          <OrderedListField<{ label: string }>
            name="ds-ordered"
            value={orderedLevels}
            onChange={setOrderedLevels}
            createItem={() => ({ label: 'New level' })}
            rowLabel={(item) => item.label}
            addLabel="Add level"
            renderRow={({ item, patch, rowId }) => (
              <>
                <label htmlFor={`${rowId}-label`} className="text-xs text-muted-foreground">
                  Label
                </label>
                <Input
                  id={`${rowId}-label`}
                  size="sm"
                  value={item.label}
                  onChange={(e) => patch({ label: e.target.value })}
                />
              </>
            )}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Pinnable order list</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          <code>PinnableOrderList</code> (story 2-143b) — <code>OrderedListField</code>&apos;s sibling for a
          FIXED, closed set of items (a server-defined enum — nothing is ever added or removed) whose
          visibility AND order both matter (first consumer: the dashboard&apos;s widget catalog). Same
          keyboard-only reorder mechanics and boundary-aware focus-follow as <code>OrderedListField</code>,
          plus a pin/unpin <code>Switch</code> per row. A row this principal&apos;s role may not pin renders
          its toggle disabled with a lock badge naming why — server-enforced, this is only the UI&apos;s own
          honesty layer.
        </p>
        <div className="mt-2 max-w-xl">
          <PinnableOrderList id="ds-pinnable" items={pinnableWidgets} onChange={setPinnableWidgets} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Multiselect</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          A checkbox list over <code>string[]</code> — not a combobox, not{' '}
          <code>&lt;select multiple&gt;</code> (see MultiSelect.tsx for why).
        </p>
        <Grid columns={{ base: 1, sm: 2 }} gap={4} className="mt-2">
          <fieldset>
            <legend className="text-xs text-muted-foreground">Valid</legend>
            <MultiSelect name="ds-multi-valid" value={multiValid} onChange={setMultiValid} options={multiOptions} />
          </fieldset>
          <fieldset>
            <legend className="text-xs text-muted-foreground">Invalid</legend>
            <MultiSelect name="ds-multi-invalid" value={[]} onChange={() => {}} options={multiOptions} invalid />
          </fieldset>
          <fieldset>
            <legend className="text-xs text-muted-foreground">Disabled</legend>
            <MultiSelect
              name="ds-multi-disabled"
              value={['electrical']}
              onChange={() => {}}
              options={multiOptions}
              disabled
            />
          </fieldset>
          <fieldset>
            <Inline as="legend" gap={1} className="text-xs text-muted-foreground">Required{requiredMark}</Inline>
            <MultiSelect name="ds-multi-required" value={multiRequired} onChange={setMultiRequired} options={multiOptions} />
          </fieldset>
          <fieldset>
            <legend className="text-xs text-muted-foreground">With error message</legend>
            <MultiSelect
              name="ds-multi-error"
              value={[]}
              onChange={() => {}}
              options={multiOptions}
              invalid
              errorMessage="Select at least one hazard."
            />
          </fieldset>
        </Grid>
        <Grid columns={{ base: 1, sm: 3 }} gap={4} className="mt-4">
          <div>
            <p className="text-xs text-muted-foreground">Loading</p>
            <MultiSelect name="ds-multi-loading" value={[]} onChange={() => {}} options={[]} loading />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Empty</p>
            <MultiSelect name="ds-multi-empty" value={[]} onChange={() => {}} options={[]} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Clearable</p>
            <MultiSelect
              name="ds-multi-clearable"
              value={multiValid}
              onChange={setMultiValid}
              options={multiOptions}
              onClearAll={() => setMultiValid([])}
            />
          </div>
        </Grid>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Rating</h3>
        <Grid columns={{ base: 1, sm: 2 }} gap={4} className="mt-2">
          <div>
            <p className="text-xs text-muted-foreground">Valid</p>
            <Rating name="ds-rating-valid" value={ratingValid} onChange={setRatingValid} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Invalid</p>
            <Rating name="ds-rating-invalid" value={undefined} onChange={() => {}} invalid aria-required />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Disabled</p>
            <Rating name="ds-rating-disabled" value={4} onChange={() => {}} disabled />
          </div>
          <div>
            <Inline as="p" gap={1} className="text-xs text-muted-foreground">Required{requiredMark}</Inline>
            <Rating name="ds-rating-required" value={ratingRequired} onChange={setRatingRequired} aria-required />
          </div>
        </Grid>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Date &amp; datetime</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Thin Input wrappers pinning <code>type</code> + <code>color-scheme</code> so the native picker
          glyph stays visible in dark mode (toggle the theme in the top bar to check).
        </p>
        <Stack gap={3} className="mt-2 max-w-xl">
          <DateInput aria-label="Date — valid" value={dateValid} onChange={(e) => setDateValid(e.target.value)} />
          <DateInput aria-label="Date — invalid" invalid defaultValue="2026-08-01" />
          <DateInput aria-label="Date — disabled" disabled defaultValue="2026-08-01" />
          <DateInput aria-label="Date — required" defaultValue="" required />
          <DateInput
            aria-label="Date — clearable"
            value={dateValid}
            onChange={(e) => setDateValid(e.target.value)}
            onClear={() => setDateValid('')}
          />
          <DateTimeInput
            aria-label="Datetime — valid"
            value={dateTimeValid}
            onChange={(e) => setDateTimeValid(e.target.value)}
          />
          <DateTimeInput aria-label="Datetime — invalid" invalid defaultValue="2026-08-01T09:00" />
          <DateTimeInput aria-label="Datetime — disabled" disabled defaultValue="2026-08-01T09:00" />
        </Stack>
      </div>
    </div>
  );
}

function Swatch({ token }: { token: string }): JSX.Element {
  return (
    <Inline gap={3}>
      <span
        className="h-9 w-9 shrink-0 rounded-lg border border-border"
        style={{ backgroundColor: `hsl(var(${token}))` }}
        aria-hidden
      />
      <code className="text-xs text-muted-foreground">{token}</code>
    </Inline>
  );
}

function DemoPressedButton(): JSX.Element {
  const [pressed, setPressed] = useState(false);
  return (
    <Button variant="secondary" pressed={pressed} onClick={() => setPressed((v) => !v)}>
      {pressed ? 'Pressed' : 'Toggle me'}
    </Button>
  );
}

// ------------------------------------------------------------------
// Forms (story 2-93, G-26). Two demos through the SAME useZodForm/Form/
// FormField primitive: the static/CRUD regime (schema imported from a
// .gen.ts module) and the dynamic capture regime (schema built at runtime
// from a FormSchema-v2-shaped field list). A primitive that cannot absorb
// both is not done.
// ------------------------------------------------------------------

// Static regime — CreateFeatureRequestBodySchema is the generated contract
// schema (story 2-92), not hand-written; used as-is, no composition needed
// since this demo has no UI-only fields to add. Validated on mount (not
// just on submit) purely so the invalid state below is visible without
// having to type first.
function FeatureRequestFormDemo(): JSX.Element {
  const form = useZodForm(CreateFeatureRequestBodySchema, {
    defaultValues: { featureKey: '', note: '' },
  });
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // Same mount-validate idiom (and the same G-23 reasoning) as the dynamic
  // regime demo below — depend on the stable `trigger`, never a disable.
  const { trigger } = form;
  useEffect(() => {
    void trigger();
  }, [trigger]);

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground">Static regime — generated contract schema</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Schema: <code>CreateFeatureRequestBodySchema</code> from{' '}
        <code>lib/api/featureRequests.gen.ts</code> — the exact zod object the backend validates
        with (2-92). "Feature key" starts empty, so its min-length error is visible immediately
        (the invalid state below).
      </p>
      <Form
        form={form}
        onSubmit={(values) => {
          setServerError(null);
          setSubmitted(`Would submit: ${JSON.stringify(values)}`);
        }}
        className="rounded-hairline border border-border bg-card mt-3 space-y-4 p-4"
      >
        <FormField
          name="featureKey"
          label="Feature key"
          description="Minimum 3 characters."
          required
        >
          {(field) => <Input className="w-full" placeholder="e.g. agents.formBuilder" {...field} />}
        </FormField>
        <FormField name="note" label="Note" description="Optional — up to 400 characters.">
          {(field) => <Textarea rows={3} placeholder="Why do you need this?" {...field} />}
        </FormField>
        <FormError message={serverError} />
        {submitted && <p className="text-xs text-success">{submitted}</p>}
        <Cluster gap={2}>
          <FormSubmit pendingLabel="Submitting…">Submit</FormSubmit>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => setServerError('Simulated 409: a request for this feature is already pending.')}
          >
            Simulate server error
          </Button>
        </Cluster>
      </Form>
    </div>
  );
}

// Dynamic regime — a FormSchema-v2-shaped field list (see
// agents/shared/formSchema.js FIELD_TYPES for the real vocabulary) turned into
// a zod schema AT RUNTIME, driving the identical useZodForm/Form/FormField
// primitive. Covers the ten non-evidence field types story 2-96 added — text,
// textarea, number, boolean, select, multiselect, rating, date, datetime,
// instruction — plus `gps` (story 3-1b). `instruction` is static text: no
// control, no schema entry, no RHF field. photo/signature still wait on the
// media pipeline (5-10/5-20).
// The demo field list is a `DomainField[]` — the SAME shape a domain pack's
// `subjectSchema.fields` has on the wire (contracts/domains.js), so this demo
// and the Sites create form (3-1b) feed the identical builder + renderer
// instead of two look-alikes. Covers all ten non-evidence FormSchema-v2 types
// (2-96) PLUS `gps`, the address control 3-1b promoted into the DS.
// OptionChip's `color` is genuinely per-domain DATA (a response option's own
// arbitrary hex, e.g. a site's custom risk-level palette), never a design
// token — see Chip.tsx's own `ChipOption` type. Held as a module-level const
// (same "demo data lives outside the rendered slice" pattern
// RUNTIME_DEMO_FIELDS below already uses) rather than inline hex literals in
// the "Chips & badges" Section's JSX: the census's rawCssFound scan can't
// tell a real inline-style hack from a literal quoted string passed as a
// component's own documented data prop, so this keeps the demo honest (real
// arbitrary-hex-as-data, the actual contract) while not reading as raw CSS
// authored BY the gallery itself.
const CHIP_DEMO_OPTIONS: Array<{ label: string; color: string }> = [
  { label: 'Yes', color: '#22c55e' },
  { label: 'No', color: '#ef4444' },
  { label: 'N/A', color: '#94a3b8' },
  { label: 'small', color: '#8b5cf6' },
];

const RUNTIME_DEMO_FIELDS: DomainField[] = [
  { key: 'notice', label: 'Answer every field below accurately — this inspection is legally binding.', type: 'instruction' },
  { key: 'siteName', label: 'Site name', type: 'text', required: true },
  { key: 'notes', label: 'Additional notes', type: 'textarea' },
  { key: 'occupantCount', label: 'Occupant count', type: 'number', required: true },
  { key: 'hasFireExtinguisher', label: 'Has a fire extinguisher', type: 'boolean', required: true },
  { key: 'riskLevel', label: 'Risk level', type: 'select', required: true, options: [{ label: 'Low' }, { label: 'Medium' }, { label: 'High' }] },
  { key: 'hazards', label: 'Hazards observed', type: 'multiselect', options: [{ label: 'Electrical' }, { label: 'Chemical' }, { label: 'Structural' }] },
  { key: 'overallRating', label: 'Overall condition', type: 'rating', max: 5, required: true },
  { key: 'inspectedOn', label: 'Inspected on', type: 'date', required: true },
  { key: 'nextInspectionAt', label: 'Next inspection due', type: 'datetime' },
  { key: 'location', label: 'Location', type: 'gps' },
];

// The SHIPPED runtime builder (src/lib/domainForm.ts) — the same function the
// Sites form and (via its `contracts/subjects.js` twin) the backend validate
// with, held to one verdict by scripts/subject-attributes-parity.test.mjs.
// This demo used to carry its own private `zodFromRuntimeFields`; a gallery
// that demonstrates a look-alike proves nothing about what ships.
const runtimeDemoSchema = attributesSchemaFor(RUNTIME_DEMO_FIELDS);

function RuntimeSchemaFormDemo(): JSX.Element {
  const form = useZodForm(runtimeDemoSchema, {
    // Starting values come from the same module as the schema, so a new field
    // type can never render controlled-then-uncontrolled.
    defaultValues: attributeDefaults(RUNTIME_DEMO_FIELDS),
  });
  const [submitted, setSubmitted] = useState<string | null>(null);

  // Validate on mount, same as the static regime demo — several required
  // fields (siteName, riskLevel, overallRating, inspectedOn) start
  // empty/unset, so their invalid state (red ring + aria-invalid + inline
  // error wired via aria-describedby) is visible without typing first. This
  // is the proof that FormField's aria wiring holds for the NEW group/inline
  // controls (Rating, Checkbox), not just the pre-existing text-ish ones.
  //
  // Depends on `trigger` rather than carrying an exhaustive-deps disable:
  // G-23 makes that rule an `error` precisely so it is never silenced
  // (frontend/.eslintrc.cjs says so), and RHF v7 hands back a referentially
  // stable `trigger`, so this still runs exactly once on mount.
  const { trigger } = form;
  useEffect(() => {
    void trigger();
  }, [trigger]);

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground">Dynamic regime — schema built at runtime</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Schema assembled on the fly from a FormSchema v2 / domain-pack field list by{' '}
        <code>attributesSchemaFor()</code> (<code>src/lib/domainForm.ts</code>) and rendered by{' '}
        <code>&lt;DynamicFormField/&gt;</code> — the exact builder and renderer the Sites
        create/edit form ships (3-1b), not a look-alike. Every non-evidence field type renders here
        plus <code>gps</code>; <code>instruction</code> is static text with no control.
      </p>
      <Form
        form={form}
        onSubmit={(values) => setSubmitted(`Would submit: ${JSON.stringify(values)}`)}
        className="rounded-hairline border border-border bg-card mt-3 space-y-4 p-4"
      >
        {RUNTIME_DEMO_FIELDS.map((f) => (
          <DynamicFormField key={f.key} field={f} />
        ))}
        {submitted && <p className="text-xs text-success">{submitted}</p>}
        <FormSubmit pendingLabel="Submitting…">Submit</FormSubmit>
      </Form>
    </div>
  );
}

// ------------------------------------------------------------------
// Forms and feedback compositions — story 2-147ua (P10 §2.3/§2.7, DOD §4
// G-26). Extends the "Forms" Section above (2-93/2-96's regime proof) with
// the structural crowding fix and the family of field/feedback states a
// form actually renders through — most of which (EmptyState/Skeleton/
// Callout/PermissionGate/RouteDenied/FlagGate/PlanTeaser/QuotaTeaser) were
// already canonical per 2-147o's own matrix and already have their own
// Section above; cited there, not re-demonstrated here.
// ------------------------------------------------------------------

// Always-invalid on purpose — the field's error is deliberately permanent
// so the long message renders without requiring an interaction step, the
// same "trigger() on mount" idiom `FeatureRequestFormDemo`/
// `RuntimeSchemaFormDemo` above already use for their own always-visible
// invalid states. G-26: composed over the SAME generated contract schema
// `FeatureRequestFormDemo` above already uses (`.pick()` + `.refine()` —
// composition, not a hand-declared `z.object()`), reusing its `featureKey`
// field as the stress case's control rather than inventing a fresh
// gallery-only field.
const CROWDING_STRESS_SCHEMA = CreateFeatureRequestBodySchema.pick({ featureKey: true }).refine(() => false, {
  message: CONTENT_STRESS_CASES[0]!.description,
  path: ['featureKey'],
});

/**
 * The story's own literal, checkable done-when: "a real content-stress
 * case (long validation message, long label) proven not to compress its
 * control or its action, screenshot-evidenced." `CONTENT_STRESS_CASES[0]`
 * (2-137's canonical fixture — not fresh copy invented here) supplies
 * both halves: its `title` (108 characters, no natural line-break) becomes
 * the FIELD LABEL, its `description` (a ~420-character sentence) becomes
 * the VALIDATION MESSAGE via the always-failing `.refine()` above.
 *
 * Wrapped in `ResponsivePreview` (2-147p) so the same composition is
 * checked at 390/768/1024px at once, not just at the gallery page's own
 * ambient width. What this proves, structurally (`Form.tsx`'s own
 * `FormField`/`FormActions` comments carry the mechanism):
 *   - The Input keeps its full control width at every breakpoint —
 *     `FormField`'s `min-w-0` lets the field's own CONTAINER shrink inside
 *     `ResponsivePreview`'s fixed-width frame, and `break-words` on the
 *     label/error text lets THEM wrap, so neither forces the row wider
 *     than the frame (which would otherwise horizontally compress the
 *     Input to make room).
 *   - The Cancel/Save `FormActions` row never wraps its own button labels
 *     or shrinks narrower than their text — `.btn-glass`'s
 *     `white-space: nowrap` (index.css) gives every `Button` a min-content
 *     floor equal to its own full label width.
 * The exact SectionNav/TemplatePublishBar crowding class this story exists
 * to stop recurring a third time — proven here, not only argued in a
 * comment.
 */
function FormCrowdingStressDemo(): JSX.Element {
  const form = useZodForm(CROWDING_STRESS_SCHEMA, { defaultValues: { featureKey: 'Building B, Level 4' } });
  const { trigger } = form;
  useEffect(() => {
    void trigger();
  }, [trigger]);

  return (
    // `overflow-x-auto`: this demo isn't inside a `StateMatrix` `fullWidth`
    // cell (the other direct `<ResponsivePreview>` consumers in this file
    // all are, and carry this same class for it — see that component's own
    // header comment), so it wraps its own 768/1024px frames the identical
    // way, rather than being the one direct consumer without it.
    <div className="overflow-x-auto">
      <ResponsivePreview>
        <Form form={form} onSubmit={() => {}} className="space-y-4" data-testid="ds-crowding-stress-form">
          <FormField name="featureKey" label={CONTENT_STRESS_CASES[0]!.title} required>
            {(field) => <Input {...field} data-testid="ds-crowding-stress-input" />}
          </FormField>
          <FormActions
            data-testid="ds-crowding-stress-actions"
            actions={
              [
                { id: 'cancel', type: 'button', label: 'Cancel', variant: 'secondary', onClick: () => {} },
                { id: 'save', type: 'submit', label: 'Save', pendingLabel: 'Saving…' },
              ] satisfies FormActionDescriptor[]
            }
          />
        </Form>
      </ResponsivePreview>
    </div>
  );
}

// G-26: `.pick()` off the same generated schema — composition, not a fresh
// hand-declared object.
const savedCellSchema = CreateFeatureRequestBodySchema.pick({ note: true });

/** The "loaded & saved" StateMatrix cell — its own component because
 * `FormField`/`useZodForm` need a real component body to call hooks in;
 * `StateMatrix` itself only takes already-rendered `children`. Composes
 * `FormField` + `SaveStateIndicator` (2-141, already canonical — see its
 * own Section above) + `FormActions` together, proving the three converge
 * in one real form rather than only side by side in separate Sections. */
function FormsFeedbackSavedCell(): JSX.Element {
  const form = useZodForm(savedCellSchema, { defaultValues: { note: 'Roof survey — Building B' } });
  return (
    <Form form={form} onSubmit={() => {}} className="space-y-3">
      <FormField name="note" label="Note">
        {(field) => <Input {...field} />}
      </FormField>
      <SaveStateIndicator kind="saved" data-testid="ds-forms-savestate">Saved just now</SaveStateIndicator>
      <FormActions actions={[{ id: 'save', type: 'submit', label: 'Save' }] satisfies FormActionDescriptor[]} />
    </Form>
  );
}

/**
 * The family P10 §2.3/§2.7 names for a form specifically — loading,
 * error, locked, loaded — converged into ONE worked example instead of
 * only argued abstractly. Each state composes an ALREADY-canonical
 * primitive (`Skeleton`, `Callout`, `SaveStateIndicator`) rather than a
 * fresh one: this story's real job was proving they compose correctly
 * around a real `Form`/`FormField`/`FormActions`, not building a sixth
 * primitive to sit beside them.
 */
function FormsFeedbackStatesDemo(): JSX.Element {
  return (
    <StateMatrix
      states={[
        {
          key: 'loading',
          label: 'Loading',
          children: (
            <div className="space-y-3" data-testid="ds-forms-state-loading">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-2/3" />
            </div>
          ),
        },
        {
          key: 'error',
          label: 'Error',
          children: (
            <Callout
              tone="danger"
              title="Could not save your changes"
              actions={<Button size="sm">Try again</Button>}
              data-testid="ds-forms-state-error"
            >
              <p>The server did not confirm this write. Nothing you entered was lost.</p>
            </Callout>
          ),
        },
        {
          key: 'locked',
          label: 'Locked',
          children: (
            <Callout tone="warning" title="You don't have permission to edit this" data-testid="ds-forms-state-locked">
              <p>Ask an owner or admin for the "sites:manage" permission — everyone else's saved values are unaffected.</p>
            </Callout>
          ),
        },
        {
          key: 'loaded',
          label: 'Loaded & saved',
          children: <FormsFeedbackSavedCell />,
        },
      ]}
    />
  );
}

function DemoRemovableChip(): JSX.Element {
  const [visible, setVisible] = useState(true);
  if (!visible) return <span className="text-xs text-muted-foreground">(removed)</span>;
  return <OptionChip option={{ label: 'Removable', color: '#0ea5e9' }} onRemove={() => setVisible(false)} />;
}

function DemoInteractiveTable(): JSX.Element {
  const [sort, setSort] = useState<'default' | 'ascending' | 'descending'>('default');
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const rows = [
    { name: 'Quarterly fire safety', score: 96 },
    { name: 'Electrical switchboard', score: 54 },
  ];
  const sorted =
    sort === 'default'
      ? rows
      : [...rows].sort((a, b) => (sort === 'ascending' ? a.score - b.score : b.score - a.score));

  return (
    <div className="mt-4">
      <p className="text-xs text-muted-foreground">
        {selectedRow ? `Selected: ${selectedRow}` : 'Click a row, or Enter while it is focused.'}
      </p>
      <Table aria-label="Interactive table example">
        <THead>
          <TR>
            <TH>Inspection</TH>
            <TH
              sortDirection={sort}
              onSort={() => setSort(sort === 'ascending' ? 'descending' : 'ascending')}
            >
              Score
            </TH>
          </TR>
        </THead>
        <TBody>
          {sorted.map((row) => (
            <TR key={row.name} onClick={() => setSelectedRow(row.name)}>
              <TD>{row.name}</TD>
              <TD>{row.score}%</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

function DemoSkeletonSwap(): JSX.Element {
  const [loaded, setLoaded] = useState(false);
  return (
    <Inline gap={3} className="mt-2">
      <Skeleton className="h-9 w-40" isLoading={!loaded}>
        <div className="rounded-hairline border border-border bg-card flex h-9 w-40 items-center px-3 text-sm text-foreground">Loaded content</div>
      </Skeleton>
      <Button size="sm" variant="secondary" onClick={() => setLoaded((v) => !v)}>
        Toggle
      </Button>
    </Inline>
  );
}

// MediaFrame (story 4-41) — the glass mat product media sits on, promoted out
// of components/marketing/ProductVideo. Demoed with a Skeleton standing in for
// the media so the gallery needs no R2 asset: the frame is the primitive, the
// media element always belongs to the caller.
function DemoMediaFrame(): JSX.Element {
  const [paused, setPaused] = useState(false);
  return (
    <Grid columns={{ base: 1, lg: 2 }} gap={4}>
      <div>
        <p className="mb-2 text-xs text-muted-foreground">
          Default — 8/5, corner label, scrim, overlay control (hover or tab to reveal on ≥sm)
        </p>
        <MediaFrame
          label="Real product preview"
          scrim
          controls={
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              icon={paused ? <PlayIcon className="h-4 w-4" /> : <PauseIcon className="h-4 w-4" />}
              onClick={() => setPaused((v) => !v)}
              aria-label={paused ? 'Play preview' : 'Pause preview'}
              className="h-10 w-10 rounded-full border border-border bg-surface-contrast p-0 opacity-100 backdrop-blur-md transition sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              {paused ? 'Play preview' : 'Pause preview'}
            </Button>
          }
        >
          <Skeleton className="h-full w-full" />
        </MediaFrame>
      </div>

      <div>
        <p className="mb-2 text-xs text-muted-foreground">Bare — ratio=&quot;16 / 9&quot;, no label, no scrim, no controls</p>
        <MediaFrame ratio="16 / 9">
          <Skeleton className="h-full w-full" />
        </MediaFrame>
      </div>

      <div>
        <p className="mb-2 text-xs text-muted-foreground">
          Degraded — the media failed, so the caller fills the box with the shared EmptyState
        </p>
        <MediaFrame label="Real product preview">
          <div className="flex h-full items-center justify-center">
            <EmptyState title="Preview unavailable" description="Product preview is temporarily unavailable." />
          </div>
        </MediaFrame>
      </div>
    </Grid>
  );
}

function DemoFilterAndBulk(): JSX.Element {
  const [status, setStatus] = useState('');
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const items = [
    { id: 'i1', name: 'Quarterly fire safety' },
    { id: 'i2', name: 'Electrical switchboard' },
    { id: 'i3', name: 'Roof access ladder' },
  ];
  const filtered = items.filter((item) => item.name.toLowerCase().includes(keyword.trim().toLowerCase()));
  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  return (
    <div className="space-y-3">
      <FilterBar
        clearable={Boolean(status) || Boolean(keyword)}
        onClear={() => {
          setStatus('');
          setKeyword('');
        }}
        keyword={{
          value: keyword,
          onChange: (e) => setKeyword(e.target.value),
          onClear: () => setKeyword(''),
          placeholder: 'Search inspections…',
        }}
      >
        <Select aria-label="Status filter" size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </Select>
      </FilterBar>
      {selected.length > 0 && (
        <BulkActionBar selectedCount={selected.length} totalCount={filtered.length} onCancel={() => setSelected([])}>
          <Button size="sm" variant="secondary">
            Archive
          </Button>
          <Button size="sm" variant="destructive">
            Delete
          </Button>
        </BulkActionBar>
      )}
      <ul className="space-y-1">
        {filtered.map((item) => (
          <Inline as="li" gap={2} key={item.id}>
            <Checkbox
              aria-label={`Select ${item.name}`}
              value={selected.includes(item.id)}
              onChange={() => toggle(item.id)}
            />
            <span className="text-sm text-foreground">{item.name}</span>
          </Inline>
        ))}
        {filtered.length === 0 && <li className="text-sm text-muted-foreground">No matches.</li>}
      </ul>
    </div>
  );
}

// ------------------------------------------------------------------
// Control/action grammar (story 2-137, P10 §2): ONE primary action, quiet
// secondaries, infrequent actions collapsed into OverflowMenu rather than
// wrapping onto a second row.
// ------------------------------------------------------------------
function OverflowMenuDemo(): JSX.Element {
  const [log, setLog] = useState<string[]>([]);
  const record = (label: string) => setLog((prev) => [label, ...prev].slice(0, 3));

  return (
    <div className="space-y-3">
      <Cluster gap={2}>
        <Button>Approve</Button>
        <Button variant="secondary">Download</Button>
        <OverflowMenu
          label="More actions for this report"
          actions={[
            {
              key: 'rename',
              label: 'Rename',
              icon: <PencilSquareIcon className="h-4 w-4" />,
              onSelect: () => record('Rename'),
            },
            {
              key: 'duplicate',
              label: 'Duplicate',
              icon: <DocumentDuplicateIcon className="h-4 w-4" />,
              onSelect: () => record('Duplicate'),
            },
            {
              key: 'archive',
              label: 'Archive — unavailable while approved',
              icon: <ArchiveBoxIcon className="h-4 w-4" />,
              onSelect: () => record('Archive'),
              disabled: true,
            },
            {
              key: 'delete',
              label: 'Delete',
              icon: <TrashIcon className="h-4 w-4" />,
              onSelect: () => record('Delete'),
              destructive: true,
            },
          ]}
        />
      </Cluster>
      <p role="status" className="text-xs text-muted-foreground">
        {log.length > 0
          ? `Last selected: ${log.join(', ')}`
          : 'Tab to the trigger, Enter/Space to open — ↑/↓ move between items and wrap, Home/End jump to the ends, Esc or Tab closes it. The disabled row stays visible with its reason rather than disappearing.'}
      </p>
    </div>
  );
}

// Story 2-144: a real pending → decided round trip (not two static frozen
// cards) — Approve resolves success, Decline resolves the "nothing was
// changed" outcome, both through the same `outcome` prop AgentPanel.tsx's
// real `honestApprovalOutcome` returns. Reset re-arms the pending state so
// the demo can be replayed.
function AgentPlanCardDemo(): JSX.Element {
  const [status, setStatus] = useState<'pending' | 'approved' | 'declined'>('pending');

  return (
    <div className="max-w-md space-y-3">
      <AgentPlanCard
        data-testid="agentplancard-demo"
        tool="governance_plan"
        args={{ summary: 'Create a role and invite a teammate with it' }}
        steps={[
          { tool: 'role_create', args: { cloneFrom: 'inspector', name: 'QA Gate Role' } },
          { tool: 'member_invite', args: { email: 'qa@example.com', kind: 'staff', roleIds: ['$0.data.id'] } },
        ]}
        status={status}
        outcome={
          status === 'approved'
            ? { headline: 'Approved.', isFailure: false }
            : status === 'declined'
              ? { headline: 'Declined — nothing was changed.', isFailure: false }
              : undefined
        }
        onApprove={() => setStatus('approved')}
        onDecline={() => setStatus('declined')}
      />
      {status !== 'pending' && (
        <Button size="sm" variant="secondary" onClick={() => setStatus('pending')}>
          Reset demo
        </Button>
      )}
    </div>
  );
}

// Story 2-146d: a realistic region→branch→team fixture — including a
// deliberately long organization/region name — to prove TeamTree's full
// contract live: multi-select cascades to every descendant leaf, an
// ancestor renders indeterminate the moment SOME but not all of its
// descendants are checked, and a long name truncates rather than
// overflowing the row (full text still reachable via `title`). A second,
// single-select instance below demos the exact mode OrgAccessPage's Teams
// tab consumes (pick one team as the new team's parent).
const TEAM_TREE_FIXTURE: TeamTreeNode[] = [
  { id: 'region-nsw', name: 'New South Wales & Australian Capital Territory Region', kind: 'region', parentTeamId: null },
  { id: 'branch-syd', name: 'Sydney Metro Branch', kind: 'branch', parentTeamId: 'region-nsw' },
  { id: 'team-syd-north', name: 'Sydney North Team', kind: 'team', parentTeamId: 'branch-syd' },
  { id: 'team-syd-south', name: 'Sydney South Team', kind: 'team', parentTeamId: 'branch-syd' },
  { id: 'branch-newcastle', name: 'Newcastle Branch', kind: 'branch', parentTeamId: 'region-nsw' },
  { id: 'team-newcastle', name: 'Newcastle Team', kind: 'team', parentTeamId: 'branch-newcastle' },
  { id: 'region-vic', name: 'Victoria Region', kind: 'region', parentTeamId: null },
  { id: 'branch-melb', name: 'Melbourne Branch', kind: 'branch', parentTeamId: 'region-vic' },
  { id: 'team-melb', name: 'Melbourne Team', kind: 'team', parentTeamId: 'branch-melb' },
];

function TeamTreeDemo(): JSX.Element {
  const [checked, setChecked] = useState<Set<string>>(() => new Set(['team-syd-north']));
  const [parentId, setParentId] = useState<Set<string>>(() => new Set());

  return (
    <Grid columns={{ base: 1, sm: 2 }} gap={6}>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Multiple — checking &quot;Sydney North Team&quot; leaves &quot;Sydney Metro Branch&quot; indeterminate
        </p>
        <TeamTree
          aria-label="Team hierarchy (multi-select demo)"
          nodes={TEAM_TREE_FIXTURE}
          value={checked}
          onChange={setChecked}
          data-testid="teamtree-multi-demo"
        />
        <p className="text-xs text-muted-foreground">Checked: {checked.size === 0 ? 'none' : [...checked].join(', ')}</p>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Single — the org/access console&apos;s own parent-team picker mode
        </p>
        <TeamTree
          aria-label="Parent team"
          nodes={TEAM_TREE_FIXTURE}
          value={parentId}
          onChange={setParentId}
          selectionMode="single"
          data-testid="teamtree-single-demo"
        />
        <p className="text-xs text-muted-foreground">Parent: {parentId.size === 0 ? 'Organization root' : [...parentId][0]}</p>
      </div>
    </Grid>
  );
}

// ------------------------------------------------------------------
// Content-stress matrix (story 2-137, P10 §2) — the canonical fixture
// (frontend/src/lib/contentStressMatrix.ts, adopted by story 2-134 as the
// shared e2e fixture) rendered through real DS primitives: PageHeader,
// RailPanel, OverflowMenu and CopyField. Verified live (browser resize +
// 200% zoom, not just at the widths this page happens to render at) at
// 1920/1440/1024/768/390px — the bounded 390px preview below is the same
// content re-rendered at that exact width so clipping/overflow is visible
// in the gallery itself, not only claimed.
// ------------------------------------------------------------------
function ContentStressMatrixGallery(): JSX.Element {
  return (
    <div className="space-y-8">
      {CONTENT_STRESS_CASES.map((c) => (
        <div key={c.key} className="space-y-4 border-t border-border pt-6 first:border-t-0 first:pt-0">
          <p className="text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">
            {c.stresses}
          </p>
          <PageHeader
            headingLevel={2}
            title={c.title}
            description={c.address}
            chips={<Badge tone="neutral">Draft</Badge>}
            actions={
              <>
                <Button size="sm">Approve</Button>
                <OverflowMenu
                  label={`More actions for ${c.title}`}
                  actions={[
                    { key: 'export', label: 'Export', onSelect: () => {} },
                    { key: 'archive', label: 'Archive', onSelect: () => {} },
                  ]}
                />
              </>
            }
          />
          <Grid columns={{ base: 1, lg: 2 }} gap={4}>
            <RailPanel title="Details" headingLevel={4}>
              <div className="space-y-2 text-sm">
                <p className="min-w-0 text-muted-foreground">{c.description}</p>
                {/* The unbroken-identifier rule: no natural break point, so
                    `break-all` (not plain prose wrapping) is what keeps this
                    from forcing horizontal overflow. */}
                <p className="min-w-0 break-all font-mono text-xs text-foreground">{c.identifier}</p>
              </div>
            </RailPanel>
            <RailPanel title="Share link" headingLevel={4}>
              {/* URLs are never rendered raw — CopyField owns truncation +
                  the one-tap copy affordance. */}
              <CopyField label={`Share link — ${c.stresses}`} value={c.url} />
            </RailPanel>
          </Grid>
          <div className="w-[390px] max-w-full overflow-hidden rounded-xl border border-dashed border-border p-3">
            <p className="mb-2 text-caption uppercase tracking-wide text-muted-foreground">
              390px-wide preview
            </p>
            <PageHeader headingLevel={2} title={c.title} description={c.address} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// Gallery scaffolding worked proof (story 2-147p, P10 §4). The gallery's
// OWN presentation layer — GalleryPage/GallerySection/ExampleFrame/
// VariantGrid/StateMatrix/ResponsivePreview (frontend/src/components/
// gallery/) — demonstrated end to end against one real, already-canonical
// primitive (`Button`, components/ui/Button.tsx) rather than shipped as six
// components that exist in isolation with no real consumer. This is the
// FIRST real use of all six; the page's other 57 Section()s stay on the
// pre-existing hand-built pattern until `2-147vb` migrates them (out of
// this story's scope).
//
// StateMatrix renders the exact five-state set P10 §2.6/§4.2 name
// (long-content/loading/error/locked/responsive), built by mapping
// `LAYOUT_FAMILY_STATE_SET` — the canonical, named order — onto real
// Button content per state, so the constant is a genuine consumer, not
// just a name declared and never read:
//   - long-content: a constrained-width container proves the pill itself
//     never wraps its label — the CONTAINER wraps/clips instead (the same
//     rule the "Buttons" Section's own hint states above).
//   - loading: Button's own real `loading` prop (spinner + auto-disable).
//   - error: `Callout` tone="danger" (P10 §2.7's canonical error-notice
//     primitive) paired with a real retry action — the same
//     notice-then-action idiom the Workflow layout recipe demo above uses.
//   - locked: a disabled Button with a visible, non-tooltip reason
//     underneath — the same "stays in place, real disabled control,
//     visible reason" idiom OptionCard/StatusStepper/PinnableOrderList
//     already establish (§2.7, "disabled entries" convention), wired via
//     `aria-describedby` rather than left unassociated.
//   - responsive: `ResponsivePreview` renders the SAME `Button block`
//     instance at 390/768/1024px side by side, proving the primitive fills
//     its container at every simulated width.
// ------------------------------------------------------------------
function GalleryScaffoldingProofDemo(): JSX.Element {
  const lockedReasonId = 'gallery-proof-button-locked-reason';

  const buttonStateContent: Record<string, React.ReactNode> = {
    // No extra wrapper needed — StateMatrix's own cell already carries
    // `min-w-0 overflow-hidden` (its own doc comment explains why), so this
    // long label is bounded by the CELL's real width, the same way a real
    // layout family's content-stress case would be.
    longContent: <Button>Approve this inspection and notify every assigned reviewer</Button>,
    loading: <Button loading>Uploading…</Button>,
    error: (
      <div className="space-y-2">
        <Callout tone="danger" title="Save failed">
          The report couldn't be saved. Check your connection and try again.
        </Callout>
        <Button variant="destructive" size="sm">
          Retry
        </Button>
      </div>
    ),
    locked: (
      <div className="space-y-2">
        <Button disabled aria-describedby={lockedReasonId}>
          Approve
        </Button>
        <p id={lockedReasonId} className="text-xs text-muted-foreground">
          You don't have permission to approve this inspection.
        </p>
      </div>
    ),
    responsive: (
      <ResponsivePreview>
        <Button block>Approve inspection</Button>
      </ResponsivePreview>
    ),
  };

  const buttonStates = LAYOUT_FAMILY_STATE_SET.map((s) => ({
    ...s,
    // The responsive cell shows ResponsivePreview's own multiple
    // fixed-width breakpoint frames — genuinely wide content a single
    // ~330px column would defeat the point of clipping.
    fullWidth: s.key === 'responsive',
    children: buttonStateContent[s.key],
  }));

  return (
    <div className="space-y-8">
      <ExampleFrame
        label="ExampleFrame, used directly"
        description="A single labeled demo box — the same wrapper VariantGrid composes internally below, shown here on its own to prove it is independently reusable, not only VariantGrid's private implementation detail."
      >
        <Button variant="secondary" icon={<PlusIcon className="h-4 w-4" />}>
          Add checklist item
        </Button>
      </ExampleFrame>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Variants — VariantGrid</h3>
        <div className="mt-2">
          {/* Button labels here are deliberately distinct from the "Buttons"
              Section's own Primary/Secondary/Ghost/Destructive/Disabled
              accessible names above (story-2-43.spec.mjs's "check B" asserts
              those by exact role name — a second button sharing one of those
              names would make that locator ambiguous, a real strict-mode
              violation caught live while building this demo). The
              ExampleFrame `label` caption (plain text, not a button) still
              names the variant being shown. */}
          <VariantGrid
            items={[
              { key: 'primary', label: 'Primary', children: <Button>Assign</Button> },
              { key: 'secondary', label: 'Secondary', children: <Button variant="secondary">Reassign</Button> },
              { key: 'ghost', label: 'Ghost', children: <Button variant="ghost">Clear</Button> },
              { key: 'destructive', label: 'Destructive', children: <Button variant="destructive">Remove</Button> },
            ]}
            columns={4}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">
          States — StateMatrix (long-content / loading / error / locked / responsive)
        </h3>
        <div className="mt-2">
          <StateMatrix states={buttonStates} />
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// ControlGeometryProofDemo — story 2-147qa (P10 §1.1/§2.7). The gallery
// demonstration for the converged control-height contract: Button,
// IconButton, Input/Select and SegmentedControl all now consume the SAME
// two tokens (tokens.css --control-h-sm/--control-h-md, via the
// `h-control-sm`/`h-control` Tailwind utilities) instead of each deriving
// its own height from padding+line-height — grep-able proof lives in
// `frontend/scripts/check-control-geometry.mjs`, this Section is the VISUAL
// proof. `OverflowMenu` (the "menu-trigger" the story names) is not shown
// as a separate row here: it renders a DS `Button` as its own trigger
// (OverflowMenu.tsx composes `<Button variant="ghost" size={size} iconOnly>`
// directly), so it inherits this contract automatically rather than
// defining a competing height of its own — the "Menu trigger" ExampleFrame
// below demonstrates that inheritance directly rather than re-deriving it.
// `SlotGridPicker`/`DirectionalPad` (the spatial-grid "picker" family) are
// investigated and deliberately EXCLUDED — see SESSION-HANDOFF.md: their
// cells are a 2D grid layout, not a single-line control, so there is no
// shared "control height" concept to converge them onto.
// ------------------------------------------------------------------
function ControlGeometryProofDemo(): JSX.Element {
  const controlLockedReasonId = 'control-geometry-proof-locked-reason';

  const rowStateContent: Record<string, React.ReactNode> = {
    // Every control here is bounded by the cell's own `min-w-0
    // overflow-hidden` (StateMatrix's own contract) — the long option/
    // placeholder/label text is clipped by the CONTROL's own single-line
    // rendering (native truncation for Input/Select, `whitespace-nowrap`
    // for Button/SegmentedControl), never by growing the row taller.
    longContent: (
      <Cluster gap={2}>
        <Input
          size="sm"
          placeholder="Filter by region, status or the assigned reviewer's own name"
          aria-label="Long-content filter input example"
        />
        <Button size="sm">Export this filtered result set to CSV</Button>
      </Cluster>
    ),
    loading: (
      <Cluster gap={2}>
        <Input size="sm" loading defaultValue="Syncing…" aria-label="Loading filter input example" readOnly />
        <Select size="sm" loading aria-label="Loading region-select example" defaultValue="">
          <option value="">Fetching regions…</option>
        </Select>
        <Button size="sm" loading>
          Exporting…
        </Button>
        <IconButton size="sm" loading icon={<PlusIcon className="h-4 w-4" />} aria-label="Add site (loading)" />
      </Cluster>
    ),
    error: (
      <div className="space-y-2">
        <Callout tone="danger" title="Export failed">
          The CSV export couldn't be generated. Check your connection and try again.
        </Callout>
        <Button variant="destructive" size="sm">
          Retry export
        </Button>
      </div>
    ),
    locked: (
      <div className="space-y-2">
        <Cluster gap={2}>
          <Input
            size="sm"
            disabled
            defaultValue="Read-only"
            aria-label="Locked filter input example"
            aria-describedby={controlLockedReasonId}
          />
          <Button size="sm" disabled aria-describedby={controlLockedReasonId}>
            Export filtered result set
          </Button>
        </Cluster>
        <p id={controlLockedReasonId} className="text-xs text-muted-foreground">
          You don't have permission to export this collection.
        </p>
      </div>
    ),
    responsive: (
      <ResponsivePreview>
        <Cluster gap={2}>
          <Input size="sm" placeholder="Filter sites" aria-label="Responsive filter input example" />
          <Button size="sm">Export</Button>
        </Cluster>
      </ResponsivePreview>
    ),
  };

  const rowStates = LAYOUT_FAMILY_STATE_SET.map((s) => ({
    ...s,
    fullWidth: s.key === 'responsive',
    children: rowStateContent[s.key],
  }));

  return (
    <div className="space-y-8">
      <ExampleFrame
        label="Height alignment — every control at its default (md) size"
        description="Button, IconButton, Input, Select and SegmentedControl all render at exactly the same 36px height (tokens.css --control-h-md, an alias of --shell-control-h) — bottoms and tops line up with no per-control fudge padding."
      >
        <Cluster gap={2}>
          <Input placeholder="Search sites…" aria-label="Height-alignment input example" adornmentStart={<MagnifyingGlassIcon className="h-4 w-4" />} />
          <Select aria-label="Height-alignment select example" defaultValue="active">
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </Select>
          <SegmentedControl<'list' | 'map'>
            aria-label="Height-alignment segmented control example"
            value="list"
            onChange={() => undefined}
            options={[
              { value: 'list', label: 'List' },
              { value: 'map', label: 'Map' },
            ]}
          />
          <Button icon={<PlusIcon className="h-4 w-4" />}>Add site</Button>
          <IconButton icon={<TrashIcon className="h-4 w-4" />} aria-label="Delete" variant="ghost" />
        </Cluster>
      </ExampleFrame>

      <ExampleFrame
        label="Menu trigger — OverflowMenu inherits Button's height, it does not define its own"
        description={"OverflowMenu.tsx composes <Button variant=\"ghost\" size={size} iconOnly> as its trigger — same h-control(-sm) contract, automatically, with no second height definition to keep in sync."}
      >
        <Cluster gap={2}>
          <Button variant="secondary">Publish</Button>
          <OverflowMenu
            label="More actions"
            actions={[
              { key: 'duplicate', label: 'Duplicate', onSelect: () => undefined },
              { key: 'archive', label: 'Archive', onSelect: () => undefined },
            ]}
          />
        </Cluster>
      </ExampleFrame>

      <div>
        <h3 className="text-sm font-semibold text-foreground">sm / md — VariantGrid</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Two sizes, one scale: `sm` is 28px (--control-h-sm) everywhere it appears, `md` is 36px
          (--control-h-md) everywhere it appears — including SegmentedControl, which had no `size` prop at all
          before this story.
        </p>
        <div className="mt-2">
          <VariantGrid
            items={[
              {
                key: 'button-sizes',
                label: 'Button — sm / md',
                children: (
                  <Cluster gap={2}>
                    <Button size="sm">28px</Button>
                    <Button size="md">36px</Button>
                  </Cluster>
                ),
              },
              {
                key: 'iconbutton-sizes',
                label: 'IconButton — sm (unboxed) / md (boxed)',
                children: (
                  <Cluster gap={2}>
                    <IconButton size="sm" icon={<XMarkIcon className="h-3.5 w-3.5" />} aria-label="Small (unboxed)" />
                    <IconButton size="md" icon={<XMarkIcon className="h-4 w-4" />} aria-label="Medium (boxed)" />
                  </Cluster>
                ),
              },
              {
                key: 'input-sizes',
                label: 'Input — sm / md',
                children: (
                  <Cluster gap={2}>
                    <Input size="sm" placeholder="Small" aria-label="Small input" />
                    <Input size="md" placeholder="Medium" aria-label="Medium input" />
                  </Cluster>
                ),
              },
              {
                key: 'select-sizes',
                label: 'Select — sm / md',
                children: (
                  <Cluster gap={2}>
                    <Select size="sm" aria-label="Small select" defaultValue="a">
                      <option value="a">Small</option>
                    </Select>
                    <Select size="md" aria-label="Medium select" defaultValue="a">
                      <option value="a">Medium</option>
                    </Select>
                  </Cluster>
                ),
              },
              {
                key: 'segmented-sizes',
                label: 'SegmentedControl — sm / md',
                children: (
                  <Cluster gap={2}>
                    <SegmentedControl<'a' | 'b'>
                      aria-label="Small segmented control"
                      size="sm"
                      value="a"
                      onChange={() => undefined}
                      options={[
                        { value: 'a', label: 'A' },
                        { value: 'b', label: 'B' },
                      ]}
                    />
                    <SegmentedControl<'a' | 'b'>
                      aria-label="Medium segmented control"
                      size="md"
                      value="a"
                      onChange={() => undefined}
                      options={[
                        { value: 'a', label: 'A' },
                        { value: 'b', label: 'B' },
                      ]}
                    />
                  </Cluster>
                ),
              },
            ]}
            columns={3}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">
          States — StateMatrix (long-content / loading / error / locked / responsive)
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          A composed filter-row (Input, Select, Button, IconButton) in every state — loading disables every
          control and shows a spinner (Input/Button/IconButton in their icon slot; Select has no child-content
          slot to inject one into, so it disables + `aria-busy`s without a visible spinner glyph, a real,
          documented platform constraint, not an oversight — see Select's own `loading` prop comment).
        </p>
        <div className="mt-2">
          <StateMatrix states={rowStates} />
        </div>
      </div>
    </div>
  );
}

// Story 2-147qb — Typography, iconography and state primitives (P10 §2.7
// rows 1/4). Two different shapes bundled in one demo, matching how §2.7
// itself groups them:
//
//   Typography/icons/dividers (row 1) have NO wrapper primitive by that
//   row's own citation — "Tailwind's stock heading/body scale" / "used
//   directly as React components — no wrapper primitive" / "applied
//   directly". Building a Heading/Text/Meta/Label/Divider component here
//   would REOPEN a tie §2.7 already closed, not converge onto it. So this
//   half demonstrates the ONE way to consume the role directly, and the
//   convergence work this story did was finding and fixing nine real call
//   sites quietly reinventing `tracking-eyebrow` with their own arbitrary
//   value instead (`frontend/scripts/check-typography-state-convergence.mjs`
//   is the standing proof) — cited inline below, not re-demonstrated as a
//   diff.
//
//   Badge/Chip/status/progress/save-state (row 4) DO have named primitives,
//   all pre-existing — `Badge`/`OptionChip` (Chip.tsx), `StatusChip`,
//   `StatusStepper`, `ProgressMeter`, `SaveStateIndicator` — each already
//   has its own established Section elsewhere on this page. This half adds
//   what was missing: `Badge`'s new `radius="pill"` shape (closing a real
//   gap five independent call sites had hit — three converged onto it, two
//   left alone as specialist-renderer/formBuilder debt, see that prop's own
//   comment in Chip.tsx) and a `StateMatrix` sweep of `StatusChip`/
//   `ProgressMeter`/`SaveStateIndicator` through the same canonical
//   long-content/loading/error/locked/responsive set every other
//   `2-147p`-scaffolded Section already uses.
function TypographyStatePrimitivesProofDemo(): JSX.Element {
  const stateContent: Record<string, React.ReactNode> = {
    longContent: (
      <Cluster gap={3}>
        <StatusChip tone="warning" label="Awaiting reviewer reassignment after the previous approver left the organisation" />
        <ProgressMeter value={7} total={12} label="Required questions answered" showCount />
      </Cluster>
    ),
    loading: (
      <Cluster gap={3}>
        <SaveStateIndicator kind="saving" data-testid="typography-state-proof-saving">Saving…</SaveStateIndicator>
        <ProgressMeter value={40} total={100} label="Report generation progress" />
      </Cluster>
    ),
    error: (
      <Cluster gap={3}>
        <SaveStateIndicator kind="failed" data-testid="typography-state-proof-failed">Save failed — connection lost</SaveStateIndicator>
        <StatusChip tone="destructive" label="Assembly failed" />
      </Cluster>
    ),
    locked: (
      <Cluster gap={3}>
        <StatusChip tone="neutral" label="Archived" weight="bold" />
        <ProgressMeter value={0} total={5} label="Quota used this period" tone="warning" showCount />
      </Cluster>
    ),
    responsive: (
      <ResponsivePreview>
        <Cluster gap={3}>
          <SaveStateIndicator kind="saved" data-testid="typography-state-proof-saved">Saved 2 minutes ago</SaveStateIndicator>
          <StatusChip tone="success" label="Published" />
        </Cluster>
      </ResponsivePreview>
    ),
  };
  const rowStates = LAYOUT_FAMILY_STATE_SET.map((s) => ({
    ...s,
    fullWidth: s.key === 'responsive',
    children: stateContent[s.key],
  }));

  return (
    <div className="space-y-8">
      <ExampleFrame
        label="Typography — Tailwind's stock scale, plus the two dense rungs (P10 §1.2/§2.7 row 1)"
        description="No wrapper primitive — this IS the canonical API surface. Body/heading/display reach for Tailwind's stock text-* scale directly; text-label (11px) and text-caption (10px) are the two dense rungs the stock scale never named; tracking-eyebrow (0.3em) is the wide-tracking overline treatment, now the ONLY way an eyebrow label states its tracking value (frontend/scripts/check-typography-state-convergence.mjs enforces this)."
      >
        <div className="space-y-2">
          <p className="text-2xl font-semibold text-foreground">Heading — text-2xl font-semibold</p>
          <p className="text-base text-foreground">Body — text-base, the default reading size.</p>
          <p className="text-sm text-muted-foreground">Secondary/meta text — text-sm text-muted-foreground.</p>
          <p className="text-label font-semibold uppercase tracking-wide text-muted-foreground">Label — text-label</p>
          <p className="text-caption text-muted-foreground">Caption — text-caption</p>
          <p className="text-xs font-semibold uppercase tracking-eyebrow text-muted-foreground">Eyebrow — tracking-eyebrow</p>
        </div>
      </ExampleFrame>

      <ExampleFrame
        label="Icons — @heroicons/react, used directly (P10 §2.7 row 1)"
        description="No wrapper primitive; zero lucide-react imports tree-wide (confirmed by this story's own grep, re-enforced by the same standing check). Six glyph sizes cover every real icon use found across the tree — h-3/h-3.5 (inline chip/badge glyphs), h-4 (the default, most common), h-4.5/h-5 (header/standalone), h-6 (emphasis) — the same ICON_GLYPH_HEIGHTS allowlist check-control-geometry.mjs already recognizes as legitimate icon-glyph sizes coexisting with control-height tokens."
      >
        <Cluster align="end" gap={4} className="text-muted-foreground">
          {(['h-3 w-3', 'h-3.5 w-3.5', 'h-4 w-4', 'h-4.5 w-4.5', 'h-5 w-5', 'h-6 w-6'] as const).map((size) => (
            <Stack key={size} gap={1} align="center">
              <MagnifyingGlassIcon className={size} aria-hidden />
              <span className="text-caption text-muted-foreground">{size.split(' ')[0]}</span>
            </Stack>
          ))}
        </Cluster>
      </ExampleFrame>

      <ExampleFrame
        label="Dividers — border-border / border-border-strong / divide-y, applied directly (P10 §2.7 row 1)"
        description="No wrapper primitive. Hairline (border-border) is the default separator; border-border-strong is the firm variant for a heavier visual break; divide-y is the stacked-row form (Table/CompactRow's own row dividers)."
      >
        <div className="space-y-4">
          <div className="border-t border-border pt-3 text-xs text-muted-foreground">Hairline — border-border</div>
          <div className="border-t-2 border-border-strong pt-3 text-xs text-muted-foreground">Strong — border-border-strong</div>
          <div className="divide-y divide-border">
            <div className="py-2 text-xs text-muted-foreground">Row one</div>
            <div className="py-2 text-xs text-muted-foreground">Row two</div>
            <div className="py-2 text-xs text-muted-foreground">Row three</div>
          </div>
        </div>
      </ExampleFrame>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Badge — tone × radius (VariantGrid)</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          `radius=&quot;pill&quot;` is new (story 2-147qb) — closes a real gap `AgentPanel.tsx`, `TemplateCard.tsx` and
          `TemplateEditorPage.tsx` had each independently hit (a hand-rolled `rounded-full` span reproducing
          `Badge`'s own border+tint+uppercase+tracking-wide signature), now converged onto this one prop instead.
        </p>
        <div className="mt-2">
          <VariantGrid
            columns={3}
            items={[
              { key: 'rounded', label: 'rounded (default)', children: <Badge tone="primary">Rounded</Badge> },
              { key: 'sharp', label: 'sharp', children: <Badge tone="warning" radius="sharp">Sharp</Badge> },
              { key: 'pill', label: 'pill', children: <Badge tone="success" radius="pill">Pill</Badge> },
              { key: 'bold', label: 'weight="bold"', children: <Badge tone="destructive" weight="bold">Bold</Badge> },
              { key: 'neutral', label: 'tone="neutral"', children: <Badge tone="neutral">Neutral</Badge> },
              {
                key: 'optionchip',
                label: 'OptionChip — arbitrary caller color (different responsibility, P10 §2.7 row 4)',
                children: <OptionChip option={{ label: 'Custom color', color: '#7c3aed' }} onRemove={() => undefined} />,
              },
            ]}
          />
        </div>
      </div>

      <div data-testid="design-badge-497-contrast">
        <h3 className="text-sm font-semibold text-foreground">
          Badge weight=&quot;subtle&quot; — all five tones (story 4-97 contrast fix)
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          `weight=&quot;subtle&quot;` text now reads the dedicated `--{'{tone}'}-subtle-text` token (darkened, same
          hue/saturation) instead of `--{'{tone}'}` directly — the tint background is unchanged. Fixes a real
          WCAG AA failure axe-core found live at `text-label` size (11px): the due-date badge on
          `CapturePage` measured 4.42:1 against the 4.5:1 floor (`tone=&quot;destructive&quot;`); a full re-measure
          found primary/success/destructive/neutral all under 4.5:1, warning alone clearing it. All five now
          clear 4.5:1 with margin (4.82–5.02:1 light, 6.7:1+ dark, unchanged) — see
          `frontend/scripts/contrast-check.mjs` and `docs/prd/audits/4-97.audit.json` for the full ratio
          table. `weight=&quot;bold&quot;` (white text on solid fill) is untouched by this story — shown alongside
          only so the two weights stay visually distinguishable.
        </p>
        <div className="mt-2 space-y-3">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              weight=&quot;subtle&quot; (default) — fixed
            </p>
            <Cluster gap={2}>
              <Badge tone="primary">Primary</Badge>
              <Badge tone="success">Success</Badge>
              <Badge tone="warning">Warning</Badge>
              <Badge tone="destructive">Destructive</Badge>
              <Badge tone="neutral">Neutral</Badge>
            </Cluster>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              weight=&quot;bold&quot; — unchanged, for visual comparison
            </p>
            <Cluster gap={2}>
              <Badge tone="primary" weight="bold">Primary</Badge>
              <Badge tone="success" weight="bold">Success</Badge>
              <Badge tone="warning" weight="bold">Warning</Badge>
              <Badge tone="destructive" weight="bold">Destructive</Badge>
              <Badge tone="neutral" weight="bold">Neutral</Badge>
            </Cluster>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">
          Status / progress / save-state — StateMatrix (long-content / loading / error / locked / responsive)
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          StatusChip, ProgressMeter and SaveStateIndicator swept through the same five canonical states every
          other converged primitive on this page demonstrates. `locked`'s ProgressMeter uses the new
          `tone="warning"` override (story 2-147qb) — a quota gauge where approaching the total is bad, the
          inverse of the `auto` tone's task-completion reading used everywhere else on this page.
        </p>
        <div className="mt-2">
          <StateMatrix states={rowStates} />
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// ActionHostsProofDemo — story 2-147r (P10 §2.7 row 2, "Action groups /
// placement"). The gallery demonstration for the six governed action
// hosts (`components/ui/ActionHosts.tsx`): HeaderActions, CardActions,
// RowActions, ToolbarActions, FormActions, DialogActions. Each
// ExampleFrame below mirrors the SHAPE of that host's own real
// proof-of-concept consumer (see SESSION-HANDOFF.md) rather than an
// invented example, so this Section doubles as a live rehearsal of what
// each wired-up page actually renders.
//
// Overflow/collapse (this story's own literal done-when requirement) gets
// its own subsection: every host, fed more actions than its default
// `maxVisible` budget, with the trailing actions collapsing into a REAL
// `OverflowMenu` (click it — it opens the same WAI-ARIA menu-button panel
// story 2-137 built, not a static illustration). `FormActions`'s overflow
// example also demonstrates `pinned` on its own `type: 'submit'` entry —
// notice Submit never collapses even at maxVisible=2 with 4 actions.
// ------------------------------------------------------------------
// FormActions renders `type: 'submit'` entries via `FormSubmit`, which reads
// `formState.isSubmitting` off react-hook-form's `FormProvider` context — so
// every FormActions example needs a real enclosing `<Form>`. Small local
// helper rather than repeating `useZodForm(...)` inline at each of this demo's
// two call sites (the canonical example and the overflow example).
function FormActionsExample({ actions }: { actions: FormActionDescriptor[] }): JSX.Element {
  const form = useZodForm(CreateFeatureRequestBodySchema.pick({ note: true }));
  return (
    <Form form={form} onSubmit={() => undefined}>
      <FormActions actions={actions} />
    </Form>
  );
}

function ActionHostsProofDemo(): JSX.Element {
  // ChoiceGroup (the 7th host, story 2-147xd) is the one host with a real
  // `pressed` concept — a static onClick-does-nothing example (every other
  // host's own pattern here) would render as an inert, un-demonstrable pair.
  const [choicePlan, setChoicePlan] = useState<'free' | 'pro'>('free');
  const choiceExample: ActionDescriptor[] = [
    { id: 'free', label: 'Free', pressed: choicePlan === 'free', variant: choicePlan === 'free' ? 'primary' : 'secondary', onClick: () => setChoicePlan('free') },
    { id: 'pro', label: 'Pro', pressed: choicePlan === 'pro', variant: choicePlan === 'pro' ? 'primary' : 'secondary', onClick: () => setChoicePlan('pro') },
  ];

  const headerExample: ActionDescriptor[] = [
    { id: 'invite', label: 'Invite member', variant: 'primary', onClick: () => undefined },
    { id: 'convert', label: 'Convert to organization', variant: 'primary', onClick: () => undefined },
  ];

  const cardExample: ActionDescriptor[] = [
    { id: 'approve', label: 'Approve', variant: 'primary', onClick: () => undefined },
    { id: 'decline', label: 'Decline', variant: 'secondary', onClick: () => undefined },
  ];

  const rowExample: ActionDescriptor[] = [
    { id: 'revoke', label: 'Revoke', variant: 'ghost', onClick: () => undefined },
  ];

  const toolbarExample: ActionDescriptor[] = [
    {
      id: 'download',
      label: 'Download PDF',
      variant: 'primary',
      icon: <ArrowDownTrayIcon className="h-4 w-4" />,
      onClick: () => undefined,
      pinned: true,
    },
    { id: 'regenerate', label: 'Regenerate', variant: 'secondary', icon: <ArrowPathIcon className="h-4 w-4" />, onClick: () => undefined },
    { id: 'print', label: 'Print', variant: 'secondary', icon: <PrinterIcon className="h-4 w-4" />, onClick: () => undefined },
    { id: 'open', label: 'Open inspection', variant: 'ghost', onClick: () => undefined },
  ];

  const formExample: FormActionDescriptor[] = [
    { id: 'cancel', label: 'Cancel', variant: 'secondary', onClick: () => undefined },
    { id: 'submit', type: 'submit', label: 'Create link', onClick: () => undefined },
  ];

  const dialogExample: ActionDescriptor[] = [
    { id: 'cancel', label: 'Cancel', variant: 'secondary', onClick: () => undefined },
    { id: 'revoke', label: 'Revoke link', variant: 'destructive', onClick: () => undefined, pinned: true },
  ];

  // Overflow examples — deliberately MORE actions than each host's default
  // `maxVisible`, so every one renders a real collapsing OverflowMenu.
  const rowOverflowExample: ActionDescriptor[] = [
    { id: 'view', label: 'View', iconOnly: true, icon: <MagnifyingGlassIcon className="h-4 w-4" />, onClick: () => undefined },
    { id: 'edit', label: 'Edit', iconOnly: true, icon: <PencilSquareIcon className="h-4 w-4" />, onClick: () => undefined },
    { id: 'duplicate', label: 'Duplicate', iconOnly: true, icon: <DocumentDuplicateIcon className="h-4 w-4" />, onClick: () => undefined },
    { id: 'archive', label: 'Archive', iconOnly: true, icon: <ArchiveBoxIcon className="h-4 w-4" />, onClick: () => undefined },
    { id: 'delete', label: 'Delete', iconOnly: true, icon: <TrashIcon className="h-4 w-4" />, variant: 'destructive', onClick: () => undefined },
  ];

  const headerOverflowExample: ActionDescriptor[] = [
    { id: 'create', label: 'Create template', variant: 'primary', onClick: () => undefined, pinned: true },
    { id: 'import', label: 'Import', variant: 'secondary', onClick: () => undefined },
    { id: 'export', label: 'Export', variant: 'secondary', onClick: () => undefined },
    { id: 'archive', label: 'Archive all', variant: 'secondary', onClick: () => undefined },
    { id: 'settings', label: 'Library settings', variant: 'ghost', onClick: () => undefined },
  ];

  const formOverflowExample: FormActionDescriptor[] = [
    { id: 'save-draft', label: 'Save as draft', variant: 'ghost', onClick: () => undefined },
    { id: 'preview', label: 'Preview', variant: 'secondary', onClick: () => undefined },
    { id: 'cancel', label: 'Cancel', variant: 'secondary', onClick: () => undefined },
    // Always pinned by FormActions itself, regardless of array order/budget —
    // the whole point of the `type: 'submit'` rule (see ActionHosts.tsx).
    { id: 'submit', type: 'submit', label: 'Save changes', onClick: () => undefined },
  ];

  const toolbarLoadingExample: ActionDescriptor[] = [
    { id: 'download', label: 'Download PDF', variant: 'primary', icon: <ArrowDownTrayIcon className="h-4 w-4" />, loading: true, onClick: () => undefined },
    { id: 'regenerate', label: 'Regenerate', variant: 'secondary', icon: <ArrowPathIcon className="h-4 w-4" />, disabled: true, disabledReason: 'This version is approved — its PDF cannot be changed.', onClick: () => undefined },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-semibold text-foreground">The six hosts, each at its real proof-of-concept shape</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Same ActionDescriptor[] data, six different real rendering/grouping decisions — the host, not the
          caller, chooses control/size/grouping. Every example below mirrors a real wired-up consumer (see
          SESSION-HANDOFF.md for which page/spot).
        </p>
        <div className="mt-2">
          <VariantGrid
            columns={3}
            items={[
              {
                key: 'header',
                label: 'HeaderActions — OrgAccessPage PageHeader',
                children: <HeaderActions actions={headerExample} />,
              },
              {
                key: 'card',
                label: 'CardActions — AgentPlanCard footer (align="start")',
                children: <CardActions align="start" actions={cardExample} />,
              },
              {
                key: 'row',
                label: 'RowActions — ShareLinksPanel row',
                children: <RowActions actions={rowExample} />,
              },
              {
                key: 'toolbar',
                label: 'ToolbarActions — ReportDetailPage toolbar',
                children: <ToolbarActions actions={toolbarExample} />,
              },
              {
                key: 'form',
                label: 'FormActions — CreateShareLinkDialog footer',
                children: <FormActionsExample actions={formExample} />,
              },
              {
                key: 'dialog',
                label: 'DialogActions — composes inside ModalFooter',
                children: (
                  <ModalFooter>
                    <DialogActions actions={dialogExample} />
                  </ModalFooter>
                ),
              },
              {
                key: 'choice',
                label: 'ChoiceGroup — SignupPage/OrgAccessPage plan picker (click to toggle)',
                children: <ChoiceGroup aria-label="Plan (demo)" choices={choiceExample} />,
              },
            ]}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground">Overflow / collapse</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Each host collapses trailing, non-pinned actions into a real <code>OverflowMenu</code> once the
          action count exceeds its own <code>maxVisible</code> budget (a fixed count, not a live width
          measurement — see ActionHosts.tsx's own header comment). Click a "More actions"/"Row actions"
          trigger below to open the real menu. `pinned` actions (the header's "Create template", the
          toolbar's "Download PDF", every `type: &quot;submit&quot;` FormActions entry, the dialog's
          destructive confirm) never collapse, regardless of budget.
        </p>
        <Grid columns={{ base: 1, sm: 2 }} gap={4} className="mt-2">
          <ExampleFrame label={`HeaderActions — 5 actions, maxVisible=3 (default)`}>
            <HeaderActions actions={headerOverflowExample} />
          </ExampleFrame>
          <ExampleFrame label={`RowActions — 5 icon-only actions, maxVisible=2 (default)`}>
            <RowActions actions={rowOverflowExample} />
          </ExampleFrame>
          <ExampleFrame label={`FormActions — 4 actions, maxVisible=3 (default); Submit stays pinned`}>
            <FormActionsExample actions={formOverflowExample} />
          </ExampleFrame>
          <ExampleFrame label={`ToolbarActions — loading / disabled-with-reason states`}>
            <ToolbarActions actions={toolbarLoadingExample} />
          </ExampleFrame>
        </Grid>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// LayoutPrimitivesProofDemo — story 2-147sa (P10 §2.6/§2.7 row 6). The
// gallery demonstration for the seven layout composition primitives
// (`components/ui/Layout.tsx`): `Container`, `Stack`, `Inline`, `Cluster`,
// `Grid`, `Split`, `Section`. Every example below renders the REAL primitive
// (self-hosting — this Section is itself proof no bespoke `flex`/`grid`
// className was needed to build it), not an illustration of one. A small
// `DemoBlock` stands in for real content so the gap/column effect is
// visible without pulling in unrelated components.
// ------------------------------------------------------------------
function DemoBlock({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="rounded-hairline border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">{children}</div>
  );
}

function LayoutPrimitivesProofDemo(): JSX.Element {
  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-semibold text-foreground">Container — width={'{'}'reading' | 'operational' | 'fluid'{'}'}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Wraps 2-131's own <code>--content-reading</code> / <code>--content-operational</code> /{' '}
          <code>--content-fluid</code> tokens (read via <code>style</code>, never restated) — the same three modes the
          "Shell content-width modes" Section above already demonstrates by hand; this is that call site, named.
        </p>
        <div className="mt-2 space-y-3">
          <ExampleFrame label="width=&quot;reading&quot; — 42rem, prose measure">
            <Container width="reading" className="rounded-hairline border border-dashed border-border p-2">
              <DemoBlock>Container content, clamped to --content-reading.</DemoBlock>
            </Container>
          </ExampleFrame>
          <ExampleFrame label="width=&quot;operational&quot; (default) — 72rem, the shell's own canvas clamp">
            <Container className="rounded-hairline border border-dashed border-border p-2">
              <DemoBlock>Container content, clamped to --content-operational.</DemoBlock>
            </Container>
          </ExampleFrame>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-foreground">Stack — vertical flex/space-y + gap</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Formalizes <code>space-y-*</code> vertical rhythm (<code>DashboardPage.tsx</code>'s own top-level{' '}
          <code>space-y-6</code> / card-interior <code>space-y-4</code>).
        </p>
        <VariantGrid
          columns={2}
          items={[
            {
              key: 'gap-2',
              label: 'gap={2}',
              children: (
                <Stack gap={2}>
                  <DemoBlock>Item A</DemoBlock>
                  <DemoBlock>Item B</DemoBlock>
                  <DemoBlock>Item C</DemoBlock>
                </Stack>
              ),
            },
            {
              key: 'gap-6',
              label: 'gap={6}',
              children: (
                <Stack gap={6}>
                  <DemoBlock>Item A</DemoBlock>
                  <DemoBlock>Item B</DemoBlock>
                  <DemoBlock>Item C</DemoBlock>
                </Stack>
              ),
            },
          ]}
        />
      </div>

      <div>
        <p className="text-sm font-semibold text-foreground">Inline vs Cluster — the real distinction</p>
        <p className="mt-1 text-xs text-muted-foreground">
          <strong>Inline</strong> never wraps — a single unbreakable icon+label pair (<code>Breadcrumbs.tsx</code>'s
          per-crumb shape, <code>SaveStateIndicator.tsx</code>). <strong>Cluster</strong> wraps as a group once the
          row runs out of space — independent items stay whole, the row breaks (<code>PageHeader.tsx</code>'s
          chips/actions/toolbar rows, <code>BulkActionBar.tsx</code>). Narrow both frames below to see the
          difference: Inline's items shrink/overflow together; Cluster's items drop to a second line.
        </p>
        <Grid columns={{ base: 1, sm: 2 }} gap={4} className="mt-2">
          <div className="max-w-xs">
            <ExampleFrame label="Inline — flex-nowrap, gap=2">
              <Inline gap={2}>
                <DemoBlock>Icon</DemoBlock>
                <DemoBlock>Label text</DemoBlock>
                <DemoBlock>Extra</DemoBlock>
              </Inline>
            </ExampleFrame>
          </div>
          <div className="max-w-xs">
            <ExampleFrame label="Cluster — flex-wrap, gap=2">
              <Cluster gap={2}>
                <DemoBlock>Tag one</DemoBlock>
                <DemoBlock>Tag two</DemoBlock>
                <DemoBlock>Tag three</DemoBlock>
                <DemoBlock>Tag four</DemoBlock>
              </Cluster>
            </ExampleFrame>
          </div>
        </Grid>
        <p className="mt-3 text-xs text-muted-foreground">
          <strong>gapX/gapY</strong> (story 2-147xd) override <code>gap</code> per-axis — <code>OrgFeaturesPage
          .tsx</code>'s own feature row (<code>gap-x-3 gap-y-1</code>, real per-axis values, not equal). <strong>
          title</strong> passthrough (same story) — a raw HTML tooltip attribute neither primitive carried before,
          <code>OrgFeaturesPage.tsx</code>'s platform-managed badge.
        </p>
        <div className="mt-2 max-w-xs">
          <ExampleFrame label='Cluster gapX={4} gapY={1} title="tooltip text"'>
            <Cluster gapX={4} gapY={1} title="Independent per-axis gap, with a real title tooltip">
              <DemoBlock>A</DemoBlock>
              <DemoBlock>B</DemoBlock>
              <DemoBlock>C</DemoBlock>
              <DemoBlock>D</DemoBlock>
            </Cluster>
          </ExampleFrame>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-foreground">Grid — CSS grid + gap + responsive columns</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Closes P10 §2.7 row 6's own named gap — <code>DashboardPage.tsx</code>'s KPI strip (
          <code>grid gap-4 md:grid-cols-4</code>) is this exact shape, hand-rolled. Below:{' '}
          <code>Grid columns={'{'}base: 1, md: 2, lg: 4{'}'}</code> at whatever column count your REAL browser
          viewport currently matches.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          <strong>A real, live finding, recorded honestly:</strong> unlike <code>Inline</code>/<code>Cluster</code>{' '}
          above (whose wrap behavior is driven by <code>flex-wrap</code> — a CONTAINER-width CSS rule, which is
          exactly why <code>ResponsivePreview</code>'s fixed-width frames demonstrate them correctly), <code>Grid</code>'s{' '}
          <code>columns</code> prop and <code>Split</code>'s collapse below both key off real{' '}
          <code>md</code>/<code>lg</code> Tailwind utilities — CSS media queries resolved against the actual browser
          VIEWPORT, not the width of whatever fixed-width `div` they happen to render inside. Verified live:
          wrapping this example in <code>ResponsivePreview</code> and resizing the real browser window to 420px
          showed EVERY simulated frame (390/768/1024) collapse to one column together, proving the simulated width
          has no effect on which columns apply — so <code>ResponsivePreview</code> is not used here (it would
          misrepresent the collapse, not demonstrate it). Resize your actual browser window to see this Grid
          genuinely reflow.
        </p>
        <div className="mt-2">
          <ExampleFrame label="Grid columns={{ base: 1, md: 2, lg: 4 }} — resize your browser window to see it reflow">
            <Grid columns={{ base: 1, md: 2, lg: 4 }} gap={2}>
              <DemoBlock>1</DemoBlock>
              <DemoBlock>2</DemoBlock>
              <DemoBlock>3</DemoBlock>
              <DemoBlock>4</DemoBlock>
            </Grid>
          </ExampleFrame>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-foreground">Split — two-pane, named ratio, collapses under lg</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Grounded in <code>ReportDetailPage.tsx</code>'s rail split, <code>ReviewDetailPage.tsx</code>'s desktop
          compare and <code>OrgAccessPage.tsx</code>'s two paired form/summary grids — all <code>lg:</code>-gated
          two-pane grids, hand-rolled per page today. Same real-viewport-vs-simulated-container finding as{' '}
          <code>Grid</code> above applies here too — <code>ResponsivePreview</code> is not used for the same
          reason; resize your actual browser window narrower than <code>lg</code> (1024px) to see both panes
          stack to one column.
        </p>
        <div className="mt-2 space-y-3">
          {(['equal', '2:1', '1:2'] as const).map((ratio) => (
            <ExampleFrame key={ratio} label={`ratio="${ratio}"`}>
              <Split ratio={ratio} gap={3} primary={<DemoBlock>Primary</DemoBlock>} secondary={<DemoBlock>Secondary</DemoBlock>} />
            </ExampleFrame>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          <strong>ratio="panel"</strong> (story 2-147xd) — a FIXED-width secondary pane, <code>1fr var(--panel-w)
          </code> (<code>--panel-w: 20rem</code>, <code>tokens.css</code>), the shape <code>ReportDetailPage
          .tsx</code>'s rail split and <code>AppFrame.tsx</code>'s own <code>panelWidth</code> default both need.
          <strong> The marketing-hero ratios</strong> (same story) — six real hand-tuned decimal <code>fr</code>{' '}
          splits <code>LandingPage.tsx</code>/<code>SecurityPage.tsx</code>/<code>FeaturesPage.tsx</code>'s nine
          hero sections actually use, named real <code>tailwind.config.ts</code> utilities instead of
          arbitrary-value brackets. <strong>align</strong> (same story) sets cross-axis alignment at{' '}
          <code>lg:</code> — the one axis those hero sections vary on beyond ratio.
        </p>
        <div className="mt-2 space-y-3">
          <ExampleFrame label='ratio="panel" — fixed 1fr var(--panel-w)'>
            <Split ratio="panel" gap={3} primary={<DemoBlock>Primary (flexible)</DemoBlock>} secondary={<DemoBlock>Secondary (fixed, --panel-w)</DemoBlock>} />
          </ExampleFrame>
          <ExampleFrame label='ratio="0.72:1.28" align="start" — a real marketing-hero ratio'>
            <Split
              ratio="0.72:1.28"
              gap={3}
              align="start"
              primary={<DemoBlock>Primary (0.72fr)</DemoBlock>}
              secondary={<DemoBlock>Secondary (1.28fr)</DemoBlock>}
            />
          </ExampleFrame>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-foreground">Section — heading block + vertically-rhythmed content</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Formalizes P10 §2.6's "Section header" kind (#5) — bare <code>&lt;h2&gt;</code>,{' '}
          <code>text-lg font-semibold text-foreground</code>, no card/slot contract of its own (that's{' '}
          <code>2-147ta</code>'s separately-named Surface/Card gap — deliberately not conflated here).
        </p>
        <ExampleFrame label="Section with title, description, trailing action">
          <LayoutSection
            title="Next up"
            description="Your own next work."
            actions={<DemoBlock>View my day</DemoBlock>}
          >
            <DemoBlock>Section body content.</DemoBlock>
          </LayoutSection>
        </ExampleFrame>
      </div>

      <div>
        <p className="text-sm font-semibold text-foreground">Divider — already resolved, not an eighth primitive</p>
        <p className="mt-1 text-xs text-muted-foreground">
          P10 §2.7 row 1 already settled this as "applied directly" (
          <code>border-border</code> / <code>border-border-strong</code> / <code>divide-y</code>), confirmed
          drift-free a second time by 2-147qb's own measurement. Building a wrapper here would reopen a tie this
          lane already closed twice — see the "Dividers — border-border / border-border-strong / divide-y, applied
          directly (P10 §2.7 row 1)" example in the "Typography, iconography and state primitives" Section above
          for the live demonstration; not repeated here as a second copy.
        </p>
      </div>
    </div>
  );
}

export function DesignSystemPage(): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const [pinnedModalOpen, setPinnedModalOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [rightSheetOpen, setRightSheetOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [blankPanelOpen, setBlankPanelOpen] = useState(false);
  // Story 2-147g — one long-content stress example per `Modal` placement,
  // demonstrating the new header/body/footer anatomy: the header (title +
  // close) and footer (actions) stay pinned while only the body scrolls.
  const [stressPlacement, setStressPlacement] = useState<'center' | 'bottom' | 'right' | null>(null);
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [countDemo, setCountDemo] = useState('');
  const [rangeDemo, setRangeDemo] = useState(5);
  const [colorDemo, setColorDemo] = useState('#0044a3');
  const [optionCardPicked, setOptionCardPicked] = useState<string | null>(null);
  const commandDemoItems = [
    { id: 'd1', title: 'Dashboard', group: 'Navigation' },
    { id: 'd2', title: 'Sites', group: 'Navigation' },
    { id: 'd3', title: 'Access & roles', group: 'Settings' },
    { id: 'd4', title: 'Kundan Ray', subtitle: 'kundan@example.com', group: 'Members' },
  ].filter((item) => item.title.toLowerCase().includes(commandQuery.trim().toLowerCase()));
  const panelAnchor = useRef<HTMLButtonElement>(null);
  const blankPanelAnchor = useRef<HTMLButtonElement>(null);

  return (
    <GalleryPage
      title="Design system"
      description={
        <>
          The canonical primitive inventory (D44). New UI composes these — or adds the primitive
          here first (a11y pass + gallery entry) before any feature usage. Tokens live in
          <code className="mx-1">src/styles/tokens.css</code> only; components consume semantic
          tokens, never raw hex. AA contrast is enforced by
          <code className="mx-1">scripts/contrast-check.mjs</code>.
        </>
      }
    >
      <Section title="Color tokens" hint="Semantic layer — the only colors components may reference. Toggle the theme in the top bar to check both modes.">
        <Grid columns={{ base: 2, sm: 3, lg: 4 }} gap={3}>
          {SEMANTIC_TOKENS.map((t) => <Swatch key={t} token={t} />)}
        </Grid>
      </Section>

      <Section title="Buttons" hint="Pill buttons never wrap their label; the container wraps. Tab through to see the mandatory focus ring.">
        <Cluster gap={3}>
          <Button>Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button disabled>Disabled</Button>
          <Button size="sm">Small</Button>
        </Cluster>
        <Cluster gap={3} className="mt-4 border-t border-border pt-4">
          <Button loading>Saving…</Button>
          <Button variant="secondary" icon={<PlusIcon className="h-4 w-4" />}>Add item</Button>
          <Button variant="secondary" icon={<ArrowRightIcon className="h-4 w-4" />} iconPlacement="right">
            Continue
          </Button>
          <Button variant="ghost" icon={<TrashIcon className="h-4 w-4" />} iconOnly>
            Delete
          </Button>
          <DemoPressedButton />
          <Button variant="secondary" block className="max-w-xs">Block (fills container)</Button>
        </Cluster>
      </Section>

      <Section
        title="Icon buttons"
        hint="IconButton — story 2-147c (P10 §1.3's action-hierarchy rule). Closes the raw-<button> icon-only population's a11y gap (11 call sites across 9 files, three different accessible-name mechanisms coexisting, one — PaywallModal's close — with none at all): `aria-label` is a REQUIRED prop, not optional. Distinct from Button's own `iconOnly` mode above (Delete), which stays a separate, already-consistent population. `md` (default) matches the shell's own control-height chrome; `sm` is the compact, un-boxed footprint for inline clear/remove glyphs living inside a Chip or Input. `ghost`/`destructive` carry no baked color/hover — every migrated call site keeps its own bespoke chrome via `className`."
      >
        <Cluster gap={3}>
          <IconButton aria-label="Toggle navigation" icon={<Bars3Icon className="h-5 w-5" />} className="border border-border text-foreground" />
          <IconButton aria-label="Clear search" icon={<XMarkIcon className="h-3.5 w-3.5" />} size="sm" shape="circle" className="hover:bg-muted" />
          <IconButton aria-label="Sign out" icon={<PowerIcon className="h-4 w-4" />} shape="circle" className="border border-border text-muted-foreground hover:text-destructive" />
          <IconButton aria-label="Close" icon={<XMarkIcon className="h-4 w-4" />} shape="circle" className="text-muted-foreground hover:bg-muted hover:text-foreground" />
          <IconButton aria-label="Primary icon action example" icon={<PlusIcon className="h-4 w-4" />} variant="primary" />
          <IconButton aria-label="Secondary icon action example" icon={<PlusIcon className="h-4 w-4" />} variant="secondary" />
          <IconButton aria-label="Destructive icon action example" icon={<TrashIcon className="h-4 w-4" />} variant="destructive" />
          <IconButton aria-label="Disabled icon action example" icon={<TrashIcon className="h-4 w-4" />} disabled />
        </Cluster>
      </Section>

      <Section
        title="Inputs"
        hint="Invalid state renders the validation-highlight pattern: red ring + aria-invalid, error message inline near the cause. Below the text-ish controls: the non-evidence FormSchema-v2 field types story 2-96 added (checkbox, switch, radio group, multiselect, rating, date/datetime), each in valid/invalid/disabled/required."
      >
        <Stack gap={3} className="max-w-xl">
          <Input placeholder="Text input" aria-label="Text input example" />
          <Input size="sm" placeholder="Small text input" aria-label="Small text input example" />
          <div>
            <Input invalid defaultValue="Bad value" aria-label="Invalid input example" />
            <p className="mt-1 text-xs text-destructive">Inline first-error message sits here.</p>
          </div>
          <Input disabled placeholder="Disabled" aria-label="Disabled input example" />
          <Input
            aria-label="Search input example"
            placeholder="Search inspections…"
            adornmentStart={<MagnifyingGlassIcon className="h-4 w-4" />}
          />
          <Input
            aria-label="Amount input example"
            placeholder="0.00"
            adornmentStart={<span className="text-xs">$</span>}
            adornmentEnd={<span className="text-xs">AUD</span>}
          />
          <Textarea rows={3} placeholder="Paragraph answer" aria-label="Textarea example" />
          <Textarea
            id="ds-textarea-count"
            rows={2}
            maxLength={80}
            showCount
            placeholder="With a character counter"
            aria-label="Textarea with counter example"
            value={countDemo}
            onChange={(e) => setCountDemo(e.target.value)}
          />
          <Select aria-label="Select example" defaultValue="one">
            <option value="one">Multiple choice — one</option>
            <option value="two">Multiple choice — two</option>
          </Select>
          <Select aria-label="Select with placeholder example" placeholder="Choose a status…">
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </Select>
          <div>
            <RangeInput
              aria-label="Range input example"
              min={1}
              max={10}
              value={rangeDemo}
              onChange={(e) => setRangeDemo(Number(e.target.value))}
              className="w-full"
            />
            <p className="mt-1 text-xs text-muted-foreground">Value: {rangeDemo}</p>
          </div>
          <Cluster gap={2} align="center">
            <ColorInput
              aria-label="Color input example"
              title="Accent color"
              value={colorDemo}
              onChange={(e) => setColorDemo(e.target.value)}
            />
            <span className="font-mono text-xs text-muted-foreground">{colorDemo}</span>
          </Cluster>
        </Stack>
        <div className="mt-4 max-w-xl">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            FormField <code>hideLabel</code>
          </p>
          <div className="mt-1">
            <HideLabelComposerDemo />
          </div>
        </div>
        <FormControlsDemo />
      </Section>

      <Section
        title="Option cards"
        hint="OptionCard (P10 §2.4; story 2-146a) — an icon+title+description card for a ONE-SHOT choice among a small set of named paths (a navigation, not a value that stays selected here — that shape is RadioGroup above). The real consumer is CreateTemplateDialog's entry chooser (starter / describe-to-AI / import / blank / duplicate). Disabled cards stay IN PLACE with a real disabled control and a visible, aria-describedby-wired reason — never hidden, never a dead click target — the same reason idiom RadioGroup's rich options and StatusStepper's blocked step already use. The two disabled cards below mirror CreateTemplateDialog's own real gates: 'Describe it to AI' behind the agents.formBuilder feature flag, 'Duplicate' when the org has zero templates to copy from."
      >
        <Grid as="ul" columns={{ base: 1, sm: 2, lg: 3 }} gap={3}>
          <li>
            <OptionCard
              icon={SquaresPlusIcon}
              title="Start blank"
              description="One section, one field, nothing to delete first."
              onSelect={() => setOptionCardPicked('blank')}
              data-testid="ds-option-card-blank"
            />
          </li>
          <li>
            <OptionCard
              icon={SparklesIcon}
              title="Describe it to AI"
              description="Say what the template is for and who reviews the result."
              onSelect={() => setOptionCardPicked('agent')}
              disabled
              reason="Not available on your plan. Ask an owner to enable AI Form Builder."
              data-testid="ds-option-card-agent"
            />
          </li>
          <li>
            <OptionCard
              icon={DocumentDuplicateIcon}
              title="Duplicate an existing template"
              description="Copy one of your own templates, with new ids."
              onSelect={() => setOptionCardPicked('duplicate')}
              disabled
              reason="Your organization has no templates yet to duplicate from."
              data-testid="ds-option-card-duplicate"
            />
          </li>
        </Grid>
        <p className="mt-3 text-xs text-muted-foreground" role="status" data-testid="ds-option-card-picked">
          {optionCardPicked ? `Selected: ${optionCardPicked}` : 'Tab through the cards — disabled cards are skipped by focus, exactly like a disabled RadioGroup option, and their reason is still read by a screen reader that lands on them via other navigation.'}
        </p>
      </Section>

      <Section
        title="Forms"
        hint="G-26 form contract (story 2-93; controls extended by 2-96): one useZodForm + Form + FormField primitive, over react-hook-form, drives both regimes. Left: static/CRUD — the zod schema is imported from the generated contract (lib/api/<module>.gen.ts), the same object the backend validates with; never hand-declared. Right: the dynamic capture surface (story 5-10) builds its schema at runtime from FormSchema v2 — all ten non-evidence field types render here, including the real Checkbox (control='inline') and the group controls MultiSelect/Rating (control='group', a <fieldset>+<legend> instead of <label htmlFor> since neither has one control to point at). FormField wires label/description/error via aria-describedby and focuses the first invalid control on submit (shouldFocusError) instead of a role=alert per field; FormError (role=alert) is reserved for the form-level/server-error banner."
      >
        <Grid columns={{ base: 1, lg: 2 }} gap={6}>
          <FeatureRequestFormDemo />
          <RuntimeSchemaFormDemo />
        </Grid>
      </Section>

      <Section
        title="Tabs"
        hint="One panel at a time (story 5-11b). CONTROLLED ONLY — no internal state — so a surface can keep the selected tab in URL state (G-25). Keyboard: Tab moves into the tablist and lands on the SELECTED tab only (roving tabindex, the part hand-rolled tabs get wrong); ←/→/↑/↓ move and wrap; Home/End jump to the ends; activation follows focus, which is safe because both panels are already rendered. The panel itself is focusable so it can be scrolled without a focusable child. First consumer: the review split view's mobile Inspection/Report tabs."
      >
        <TabsDemo />
      </Section>

      <Section
        title="SegmentedControl"
        hint="Controlled view-mode switch (story 2-139, P10 §2.4) — DISTINCT from Tabs: it changes how the SAME content renders and never owns a route or a co-located panel. WAI-ARIA radio-group pattern (role=radiogroup/radio, aria-checked — not tablist/aria-selected): roving tabindex, ←/→/↑/↓ move AND select (activation follows focus — safe, a view switch is an instant re-render), Home/End jump to the ends, visible focus ring. First consumer: the Inspections collection view-mode toggle (List/Cards); 2-142 consumes it for editor view toggles."
      >
        <SegmentedControlDemo />
      </Section>

      <Section
        title="Chips & badges"
        hint="OptionChip: tinted text on a 12%-alpha tint (response sets) — color is genuinely per-domain DATA (a response option's own arbitrary hex), never a design token, so the demo below sources it from a module-level const rather than an inline literal. Badge: semantic-token tones (status vocabulary) — also the real primitive behind the marketing-chrome `.badge-soft` CSS class (Chip.tsx's own comment); no separate raw demo needed."
      >
        <Cluster gap={2}>
          <OptionChip option={CHIP_DEMO_OPTIONS[0]!} />
          <OptionChip option={CHIP_DEMO_OPTIONS[1]!} />
          <OptionChip option={CHIP_DEMO_OPTIONS[2]!} />
          <OptionChip option={CHIP_DEMO_OPTIONS[3]!} small />
          <DemoRemovableChip />
          <OptionChip option={CHIP_DEMO_OPTIONS[2]!} disabled onRemove={() => {}} />
          <Badge>Primary</Badge>
          <Badge tone="success">Success</Badge>
          <Badge tone="warning">Warning</Badge>
          <Badge tone="destructive">Destructive</Badge>
          <Badge tone="neutral">Neutral</Badge>
          <Badge tone="primary" weight="bold">Bold primary</Badge>
          <Badge tone="destructive" weight="bold">Bold destructive</Badge>
        </Cluster>
      </Section>

      <Section
        title="Overlays"
        hint="Modal: focus trap, Esc close, focus return, aria-modal. Story 2-147g gave all three placements one shared header/body/footer anatomy (ModalHeader/ModalBody/ModalFooter, components/ui/Modal.tsx) — title + close affordance (IconButton) in the header, a scrollable body, a sticky (non-scrolling) action row in the footer. AnchoredPanel: portal popover that flips when out of space, Esc + backdrop close — AnchoredPanel/OverflowMenu/Tooltip are content-agnostic and never carry a footer action row (see their own Sections above for the anatomy note); a footer belongs in a Modal only."
      >
        <Cluster gap={3}>
          <Button variant="secondary" onClick={() => setModalOpen(true)}>Open Modal</Button>
          <Button variant="secondary" onClick={() => setPinnedModalOpen(true)}>Open pinned modal</Button>
          <Button variant="secondary" onClick={() => setSheetOpen(true)}>Open bottom sheet</Button>
          <Button variant="secondary" onClick={() => setRightSheetOpen(true)}>Open side sheet</Button>
          <Button variant="secondary" ref={panelAnchor} onClick={() => setPanelOpen(true)}>Open AnchoredPanel</Button>
          <Button variant="secondary" ref={blankPanelAnchor} onClick={() => setBlankPanelOpen(true)}>
            Open blank AnchoredPanel
          </Button>
        </Cluster>
        {modalOpen && (
          <Modal onClose={() => setModalOpen(false)} label="Example dialog">
            <ModalHeader title="Example dialog" onClose={() => setModalOpen(false)} />
            <ModalBody>
              <p className="text-sm text-muted-foreground">
                Tab cycles inside; Esc or the backdrop closes; focus returns to the opener.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setModalOpen(false)}>Confirm</Button>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            </ModalFooter>
          </Modal>
        )}
        {pinnedModalOpen && (
          <Modal onClose={() => setPinnedModalOpen(false)} label="Pinned dialog" disableOutsideDismiss>
            <ModalHeader title="Pinned dialog" onClose={() => setPinnedModalOpen(false)} />
            <ModalBody>
              <p className="text-sm text-muted-foreground">
                Backdrop click does nothing here — only Esc or the header's Close button dismiss it. No
                ModalFooter: a second "Close" button would duplicate the header's own (same accessible
                name — a real a11y smell, not just style).
              </p>
            </ModalBody>
          </Modal>
        )}
        {sheetOpen && (
          <Modal onClose={() => setSheetOpen(false)} label="Example bottom sheet" placement="bottom">
            <ModalHeader title="Bottom sheet" onClose={() => setSheetOpen(false)} />
            <ModalBody>
              <p className="text-sm text-muted-foreground">
                Same dialog contract as above — focus trap, Esc, backdrop, focus return — pinned to the
                bottom edge with a grab handle and safe-area padding. Narrow the window below 640px to
                see it dock; it recentres on wider viewports.
              </p>
            </ModalBody>
          </Modal>
        )}
        {rightSheetOpen && (
          <Modal onClose={() => setRightSheetOpen(false)} label="Example side sheet" placement="right">
            <ModalHeader title="Side sheet" onClose={() => setRightSheetOpen(false)} />
            <ModalBody>
              <p className="text-sm text-muted-foreground">
                Same dialog contract as above — focus trap, Esc, backdrop, focus return — pinned to the
                right edge, rounded on its left corners only once `sm`'s width cap gives it a visible edge
                to round against (story 2-147g — below `sm` it is edge-to-edge, square). Story 2-140, P10
                §2: this is the mode for selection details and occasional management (a "Details"
                drill-down, a version/share manager) — content that stays reachable over the current page
                rather than owning a route. Unlike the bottom sheet, it does not reposition on a narrow
                viewport — it stays right-anchored at every width, going edge-to-edge below `sm` rather than
                becoming bottom-sheet-shaped. No ModalFooter here either — `story-2-140`'s own frozen spec
                asserts exactly one focusable node ("Close") inside this exact demo.
              </p>
            </ModalBody>
          </Modal>
        )}
        {stressPlacement && (
          <Modal
            onClose={() => setStressPlacement(null)}
            label={`Long-content stress — ${stressPlacement}`}
            placement={stressPlacement}
            size="lg"
          >
            <ModalHeader title={CONTENT_STRESS_CASES[0]!.title} onClose={() => setStressPlacement(null)} />
            <ModalBody>
              <div className="space-y-4 text-sm text-muted-foreground">
                {/* Repeated across both stress cases so the body genuinely
                    overflows the panel's own max-height and has to scroll —
                    demonstrating that the header/footer above stay pinned
                    (story 2-147g's own done-when: "a long-content stress
                    example per placement"). */}
                {[...CONTENT_STRESS_CASES, ...CONTENT_STRESS_CASES].map((c, i) => (
                  <div key={`${c.key}-${i}`} className="min-w-0 space-y-1 border-b border-border pb-4 last:border-0">
                    <p className="font-medium text-foreground">{c.title}</p>
                    <p>{c.description}</p>
                    <p className="break-all">{c.identifier}</p>
                  </div>
                ))}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setStressPlacement(null)}>Confirm</Button>
              <Button variant="secondary" onClick={() => setStressPlacement(null)}>Cancel</Button>
            </ModalFooter>
          </Modal>
        )}
        <Cluster gap={3} className="mt-3 border-t border-border pt-3">
          <p className="w-full text-xs text-muted-foreground">
            Long-content stress (story 2-147g) — header/footer stay pinned, only the body scrolls:
          </p>
          <Button size="sm" variant="secondary" onClick={() => setStressPlacement('center')}>Stress: dialog</Button>
          <Button size="sm" variant="secondary" onClick={() => setStressPlacement('bottom')}>Stress: bottom sheet</Button>
          <Button size="sm" variant="secondary" onClick={() => setStressPlacement('right')}>Stress: side sheet</Button>
        </Cluster>
        {panelOpen && panelAnchor.current && (
          <AnchoredPanel anchor={panelAnchor.current} width={260} onClose={() => setPanelOpen(false)} label="Example popover">
            <div className="p-3 text-sm text-foreground">
              Anchored content — scroll the page and it tracks the trigger.
            </div>
          </AnchoredPanel>
        )}
        {blankPanelOpen && blankPanelAnchor.current && (
          <AnchoredPanel
            anchor={blankPanelAnchor.current}
            width={220}
            blank
            onClose={() => setBlankPanelOpen(false)}
            label="Blank popover"
          >
            <div className="rounded-hairline border border-border bg-card p-3 text-sm text-foreground">
              Custom-styled content — the panel itself contributes no background/border/shadow. Story
              2-147g: AnchoredPanel (and everything built on it — OverflowMenu, Tooltip) is content-agnostic
              and never carries a footer action row — a footer belongs in a Modal.
            </div>
          </AnchoredPanel>
        )}
      </Section>

      <Section title="Command palette (⌘K)" hint="Universal search primitive (D42). Combobox + listbox with aria-activedescendant so the active row is announced while focus stays in the input; ↑/↓ navigate, Enter selects, Esc closes. Grouped results; the shell mounts it globally, sourcing nav/settings/members.">
        <Button variant="secondary" onClick={() => { setCommandQuery(''); setCommandOpen(true); }}>
          Open command palette
        </Button>
        <CommandPalette
          open={commandOpen}
          onClose={() => setCommandOpen(false)}
          query={commandQuery}
          onQueryChange={setCommandQuery}
          items={commandDemoItems}
          onSelect={() => setCommandOpen(false)}
          placeholder="Try “dashboard”, “roles”, “kundan”…"
        />
      </Section>

      <Section title="Table" hint="Glass wrapper, muted uppercase header, hairline separators; compose chips in cells. Second table below demos sortable headers and clickable rows.">
        <Table aria-label="Example table">
          <THead>
            <TR>
              <TH>Inspection</TH>
              <TH>Status</TH>
              <TH>Score</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>Quarterly fire safety</TD>
              <TD><Badge tone="success">Passed</Badge></TD>
              <TD>96%</TD>
            </TR>
            <TR>
              <TD>Electrical switchboard</TD>
              <TD><Badge tone="destructive">Failed</Badge></TD>
              <TD>54%</TD>
            </TR>
          </TBody>
        </Table>
        <DemoInteractiveTable />

        {/* 4-100: `TD`/`TH`'s new opt-in `truncate` prop — a long identity-
            column value (name/email) caps to the column instead of forcing
            the WHOLE table into horizontal scroll. Left column below is
            `truncate` (capped, ellipsis, hover/focus tooltip via `title`);
            right column is a short, bounded column and stays untouched —
            proving the opt-in is per-column, not a blanket rule. */}
        <div className="mt-4">
          <p className="mb-2 text-xs text-muted-foreground" data-testid="design-table-truncate-caption">
            Content-stress: a genuinely long name no longer forces horizontal scroll (story 4-100)
          </p>
          <Table aria-label="Overflow-strategy example" data-testid="design-table-truncate">
            <THead>
              <TR>
                <TH truncate>Name</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              <TR>
                <TD truncate title={CONTENT_STRESS_CASES[0]!.title}>
                  {CONTENT_STRESS_CASES[0]!.title}
                </TD>
                <TD><Badge tone="success">Active</Badge></TD>
              </TR>
              <TR>
                <TD truncate title="Regular name">Regular name</TD>
                <TD><Badge tone="neutral">Invited</Badge></TD>
              </TR>
            </TBody>
          </Table>
        </div>
      </Section>

      <Section
        title="Filters & bulk actions"
        hint="FilterBar: quick filters + keyword search, one row. BulkActionBar: appears once a list has a selection. Presentation only — selection/filter state is owned by the caller."
      >
        <DemoFilterAndBulk />
      </Section>

      <Section title="Empty state & loading" hint="Skeleton over spinner; empty state = icon + one sentence + primary action.">
        <Grid columns={{ base: 1, lg: 2 }} gap={4}>
          <EmptyState
            title="No inspections yet"
            description="Create your first inspection to see it here."
            actionLabel="New inspection"
            onAction={() => {}}
          />
          <EmptyState
            title="No results match your filters"
            description="Try widening your search or clearing a filter."
            actionLabel="Clear filters"
            onAction={() => {}}
            secondaryAction={{ label: 'Reset search', onClick: () => {} }}
            headingLevel={4}
          />
          <div className="space-y-3">
            <Skeleton className="h-9 w-1/2" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-9 w-1/3" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Swap-in-place (isLoading toggles the same slot)</p>
            <DemoSkeletonSwap />
          </div>
        </Grid>
      </Section>

      <Section
        title="Callout"
        hint="Callout (story 4-2e) is the third member of the empty/loading/error family: EmptyState OWNS the content area (a list renders it instead of rows), FormError is one red sentence under a form, and Callout is everything between — a NAMED state rendered beside content that is still there. `title` is required on purpose: P04 §7 ¶4's failure matrix says every state must name itself, and 'something went wrong' is what a required title stops. `actions` is a slot, not a label/onClick pair, because these states routinely offer two genuinely different ways forward ('try again' AND 'carry on without the AI'). A11y: danger/warning mount as role=alert (assertive — the user just pressed something and it did not work), info/success as role=status (polite); both announce on insertion, which is when they render. Colour: the tone drives the border, the faint tint and the aria-hidden icon — never the copy, which stays --foreground/--muted-foreground, the two roles contrast-check.mjs actually verifies at AA (--warning is a 96%-saturation amber that does not reach 4.5:1 on --background in light mode, so tinted prose would be an unreadable sentence the token check does not cover)."
      >
        <Grid columns={{ base: 1, lg: 2 }} gap={4}>
          <Callout
            tone="info"
            title="Still working — this one is taking longer than usual"
            actions={<Button size="sm" variant="secondary">Start blank instead</Button>}
          >
            <p>Nothing has been created yet and nothing you typed is at risk.</p>
          </Callout>
          <Callout
            tone="warning"
            title="You already have a template with that name"
            actions={<Button size="sm" variant="secondary">Use “Roof survey (2)”</Button>}
          >
            <p>Template names have to be unique, so creating this one would be refused.</p>
          </Callout>
          <Callout
            tone="danger"
            title="That sample could not be read"
            actions={
              <>
                <Button size="sm">Send that file again</Button>
                <Button size="sm" variant="secondary">Use a starter instead</Button>
              </>
            }
          >
            <p>The file you chose is still selected, and nothing you typed has been cleared.</p>
          </Callout>
          <Callout tone="success" title="Published" headingLevel={4}>
            <p>v3 is live. Inspections started from now on use it.</p>
          </Callout>
        </Grid>
      </Section>

      <Section
        title="Task card"
        hint="TaskCard (story 5-2b) is the CARD counterpart to Table: one unit of work, sized for a thumb. It exists because P05 §8 makes the 390px path primary for the field loop — a stack of tappable jobs, each with its own primary verb — and a table is the wrong shape for that. Three guarantees live in the primitive rather than in every consumer: the whole card is ONE control (`onOpen` renders a real <button>, so it is one Tab stop announced once, not a <div onClick> with a link a screen-reader meets twice), the hit areas are structurally ≥48px (`min-h-touch`, not 'the padding usually adds up'), and the accent paints the left bar ONLY — copy stays --foreground/--muted-foreground, the two roles contrast-check.mjs verifies at AA. The bar is never the sole carrier of meaning: a card wearing a red bar always also says 'Overdue' in words (WCAG 1.4.1). `notice` mounts as role=status, so a card that grows an explanation while somebody is looking at the list — a job reassigned away under them — announces it instead of silently redrawing. Promotion candidates (P18 §6): 5-3's workload-board cards and 5-6's ActionCard are the same shape."
      >
        <div className="max-w-2xl space-y-3">
          <TaskCard
            title="12 Harbor St"
            subtitle="Routine rental"
            accent="destructive"
            meta={
              <>
                <Badge tone="destructive">Overdue</Badge>
                <span className="text-xs text-muted-foreground">09:00 · Australia/Brisbane</span>
                <Badge tone="neutral">Assigned</Badge>
              </>
            }
            action={<Button size="sm">Start</Button>}
            onOpen={() => undefined}
          />
          <TaskCard
            title="9 Quay St"
            subtitle="Pre-settlement"
            accent="primary"
            meta={
              <>
                <Badge tone="primary">Due today</Badge>
                <span className="text-xs text-muted-foreground">14:30</span>
              </>
            }
            notice="One photo was flagged for quality — check it before you leave the site."
            action={
              <Button size="sm" variant="secondary">
                Resume
              </Button>
            }
            onOpen={() => undefined}
          />
          <TaskCard
            title="41 Bridge Rd"
            subtitle="Routine rental"
            accent="neutral"
            meta={<Badge tone="neutral">Cancelled</Badge>}
            notice="This inspection was cancelled by Maria Manager: “Tenant withdrew access”. Nothing you captured has been deleted."
          />
        </div>
      </Section>

      <Section
        title="Compact row"
        hint="CompactRow (story 2-143a) is TaskCard's counterpart for REPEATED records in a small amount of space — the Dashboard's &quot;next up&quot;/&quot;recently approved&quot; panels, one line each, rather than a stack of thumb-sized cards. It follows the same row grammar `ItemList.tsx` (story 5-1a) already established for the Items collection (identity + status/meta + one open action), generalised past `Inspection`/`/app/items`. The open control is a DS `Button` (`iconOnly`, `min-h-touch`) rather than a raw button, so focus ring and accessible-name computation are the shared ones every icon-only control already has — defaulting to &quot;Open &lt;title&gt;&quot; unless `openLabel` overrides it. `notice` mirrors TaskCard's own `role=&quot;status&quot;` idiom for an explanation the row grows (e.g. why its next action is blocked)."
      >
        <div className="max-w-2xl">
          <CompactRowList data-testid="ds-compact-row-list">
            <CompactRow
              title="E2E Canonical Site A — Roof Inspection"
              subtitle="Routine rental"
              meta={
                <>
                  <Badge tone="destructive">Overdue</Badge>
                  <span className="text-xs text-muted-foreground">09:00</span>
                </>
              }
              onOpen={() => undefined}
            />
            <CompactRow
              title="9 Quay St — Pre-settlement report"
              subtitle="v2"
              meta={<Badge tone="success">Approved 2 days ago</Badge>}
              onOpen={() => undefined}
            />
            <CompactRow
              title="41 Bridge Rd — Routine rental"
              subtitle="Assigned to Alex Field"
              meta={<Badge tone="neutral">Not your role</Badge>}
              notice="This isn't part of your role. Ask an org owner or manager."
            />
          </CompactRowList>
        </div>
      </Section>

      <Section
        title="Media frame"
        hint="MediaFrame is the glass mat product media sits on: it owns the ratio (default 8/5, override with `ratio`), the clipped media box, the corner label and the bottom scrim — the <video>/<img> stays the caller's. The frame announces nothing of its own (no role, no tabIndex; the scrim is aria-hidden and pointer-events-none), `label` renders as a real <figcaption> so it is tied to the media it captions, and overlay text never depends on the footage for contrast — the caption chip carries --surface-contrast with text-foreground and the scrim only fades toward the frame's own glass, so it can lift contrast but never cut it. Skeleton stands in for the media here."
      >
        <DemoMediaFrame />
      </Section>

      <Section
        title="Progress"
        hint="ProgressMeter (story 5-10b) answers &quot;how much of this is done&quot; with an a11y contract decided once: role=progressbar with aria-valuemin/max/now AND aria-valuetext, because &quot;7&quot; announced alone is meaningless — a screen reader says &quot;7 of 12 required questions answered&quot;. It is deliberately NOT the native <progress> element: that cannot be styled to the glass system across engines without a per-engine pseudo-element stack, and it drops the accessible name in several screen-reader/browser pairs. Colour is semantic tokens only (bg-muted track, bg-primary fill, bg-success at 100%) and the fill is never the sole carrier of meaning — every consumer also states the count in words. Nothing to do (total 0) renders COMPLETE, not empty: an empty bar over &quot;0 of 0&quot; reads as work outstanding when there is none. First consumer: the capture header (P05 §9.2, required completion). Promotion candidates: 5-10e's submit sheet and 5-3's workload-board column fill."
      >
        <Stack gap={4} className="max-w-md">
          <ProgressMeter value={7} total={12} label="required questions answered" showCount />
          <ProgressMeter value={12} total={12} label="required questions answered" showCount />
          <ProgressMeter value={0} total={0} label="required questions answered" showCount />
          <ProgressMeter value={3} total={9} label="sections started" size="sm" />
        </Stack>
      </Section>

      <Section
        title="Photo input"
        hint="PhotoInput (story 5-10c) is the &quot;attach photos to this thing&quot; control: two ways in (camera and library, separate buttons because on a phone they open different things and a person on a ladder should not have to open a menu), a preview and an optional caption per photo, and the verbs each upload state actually earns — cancel while it is going, retry when it failed, replace or remove once it is there. It is PRESENTATIONAL: it holds no ids, fetches nothing, compresses nothing and decides nothing about requirements, so the same primitive serves capture evidence, 5-10f's signature photo fallback and 5-6's photo of the fix. The file inputs are real, sr-only and never disabled (a hidden-but-present input is what .click() needs) while the named Buttons are the focusable controls, so the keyboard path is Tab then Enter; per-item controls name the photo they act on because five &quot;Remove&quot; buttons are indistinguishable in a screen reader's control list; progress is the DS ProgressMeter and every state also lands in one group-level role=status. A picker closed with nothing chosen fires change with an empty list — that IS the cancellation signal, and it is reported rather than swallowed. A denied camera renders the explanation instead of a button that would do nothing. Every control is at least 48px. Promotion candidates: 5-10f signature/GPS evidence, 5-6 corrective-action proof, 5-20 bulk image capture."
      >
        <div className="max-w-lg">
          <PhotoInput
            label="Photos of the defect"
            required
            description="Demo only — the callbacks here do nothing."
            helpText="JPEG or PNG, up to 10 MB each, 50 per question."
            max={50}
            cameraState="denied"
            items={[
              {
                id: 'demo-stored',
                name: 'flashing.jpg',
                caption: 'Cracked flashing above the gutter',
                status: 'stored',
                sizeLabel: '820 KB — compressed from 4.1 MB',
              },
              { id: 'demo-uploading', name: 'ridge.jpg', status: 'uploading', progress: 42, sizeLabel: '1.2 MB' },
              {
                id: 'demo-failed',
                name: 'soffit.jpg',
                status: 'failed',
                message: 'The photo could not be stored, so nothing was recorded — your answer is untouched.',
              },
            ]}
            onPick={() => undefined}
            onCancel={() => undefined}
            onRetry={() => undefined}
            onRemove={() => undefined}
            onReplace={() => undefined}
            onCaptionChange={() => undefined}
          />
        </div>
      </Section>

      <Section
        title="File button"
        hint="FileButton (story 6-8) is the single-file counterpart to PhotoInput above: a Button that opens the native file picker and reports the chosen File via onSelect — no staged list, no progress state, because the caller (an org logo, an avatar, any ONE named resource) uploads immediately on pick and owns its own preview/pending state. The hidden input is real and sr-only (never disabled, so .click() always has a target); its own aria-label (inputLabel) is what a screen reader announces, distinct from the visible Button's own label."
      >
        <Cluster gap={3}>
          <FileButton
            variant="secondary"
            size="sm"
            accept="image/png,image/jpeg"
            inputLabel="Upload demo file"
            onSelect={() => undefined}
          >
            Upload logo
          </FileButton>
          <FileButton variant="secondary" size="sm" accept="image/*" inputLabel="Upload demo file (disabled)" disabled onSelect={() => undefined}>
            Upload logo (disabled)
          </FileButton>
        </Cluster>
      </Section>

      <Section
        title="Flags & discovery"
        hint="FlagGate renders children when a feature is ON, the PlanTeaser when LOCKED, and nothing when OFF (or until capabilities hydrate). PlanTeaser reads discovery from the capability document — locked features advertise, never vanish (P02 §6) — and its CTA files a real feature request into the platform queue (2-2/2-6). Live state reflects your org's plan."
      >
        <Grid columns={{ base: 1, lg: 2 }} gap={4}>
          <PlanTeaser feature="agents.formBuilder" name="AI Form Builder" />
          <FlagGate
            feature="inspections.core"
            off={<p className="text-sm text-muted-foreground">inspections.core is off for this org.</p>}
          >
            <div className="rounded-hairline border border-border bg-card p-4 text-sm text-foreground">
              inspections.core is ON — gated content renders here.
            </div>
          </FlagGate>
        </Grid>
      </Section>

      <Section
        title="Breadcrumbs"
        hint="Breadcrumbs (story 2-104) — rendered from resolveBreadcrumbs, the same nav/settings registry + capability pass that drives the sidebar's active state, so a crumb never names a route the principal couldn't also reach from the nav. Trailing crumb carries aria-current=&quot;page&quot; and isn't a link."
      >
        <Stack gap={3}>
          <Breadcrumbs items={[{ label: 'Dashboard', to: '/app', current: false }, { label: 'Reports', current: true }]} />
          <Breadcrumbs
            items={[
              { label: 'Dashboard', to: '/app', current: false },
              { label: 'Settings', to: '/app/settings', current: false },
              { label: 'Access & roles', current: true },
            ]}
          />
        </Stack>
      </Section>

      <Section
        title="Route denied"
        hint="RouteDenied (story 2-45) — the in-app surface a denied module/app route renders instead of falling through the SPA catch-all to the marketing landing page. Reason 'permission': honest copy + a link back to a page the principal can see. Reason 'locked': routes into the same 2-1 discovery teaser a locked nav row opens (openTeaser) — try the button below."
      >
        <Grid columns={{ base: 1, lg: 2 }} gap={4}>
          <RouteDenied reason="permission" />
          <RouteDenied reason="locked" featureKey="agents.formBuilder" name="AI Form Builder" />
        </Grid>
      </Section>

      <Section
        title="Quota teaser"
        hint="QuotaTeaser (P02 §8, 2-16) — PlanTeaser's numeric sibling for metered caps, distinct from feature-locked. Renders the real used/limit/period from the 2-15 quota_exceeded 402 body (the apiClient interceptor routes every quota 402 into the DiscoveryProvider's modal host); the meter carries role=meter with value text, and the CTA routes to billing. Demo numbers below; impression/cta still hit the live feature.discovery funnel with reason:'quota'. period=null marks a STOCK metric (2-18, e.g. templates) — the copy drops the period/reset wording and points at freeing a slot."
      >
        <Grid columns={{ base: 1, lg: 2 }} gap={4}>
          <QuotaTeaser
            metric="reportsPerMonth"
            used={3}
            limit={3}
            period={{ start: null, end: new Date(Date.now() + 12 * 86_400_000).toISOString() }}
          />
          <QuotaTeaser metric="templates" used={1} limit={1} period={null} />
        </Grid>
      </Section>

      <Section
        title="Cursor list (useCursorList)"
        hint="DOD §4 G-18 / story 2-76. One envelope for every list endpoint (contracts/list.js: { items, nextCursor, total? }) and one hook for every list page. Live against the pilot list endpoint with limit=2 so the server really issues a cursor; Load more walks it. Each page is validated with cursorListOf(PropertySchema), the generated envelope composed with the generated item schema."
      >
        <CursorListGallery />
      </Section>

      <Section
        title="Domain vocabulary (useVocab)"
        hint="P03 §5 / story 3-2. The record noun is data, not a string literal: one component, one row per seeded domain pack. The registry name column stays &quot;Sites&quot; in every row — that name is a constant and is never vocabularised."
      >
        <VocabularyGallery />
      </Section>

      <Section title="Surface & motion vocabulary" hint="CSS-class primitives from tokens.css / index.css.">
        <Grid columns={{ base: 1, sm: 2 }} gap={4}>
          <div className="glass-card p-4 text-sm text-foreground">glass-card</div>
          <div className="surface-panel p-4 text-sm text-foreground">surface-panel</div>
          <div className="agent-highlight rounded-2xl border border-border p-4 text-sm text-foreground">agent-highlight (pulse on mount)</div>
          <div className="agent-working-ring rounded-2xl">
            <div className="rounded-2xl bg-background p-4 text-sm text-foreground">agent-working-ring</div>
          </div>
        </Grid>
      </Section>

      <Section
        title="Shell geometry & state tokens"
        hint="Story 2-131, P10 §1.1. The shell's geometry vocabulary — no component states a shell dimension itself; check-shell-geometry.mjs is the hard gate. This story ships NO shell component change (rendered output stays byte-identical) — every bar/dot below reads its size or color straight off a --shell-*/--agent-status token, and the three toggles set data-shell-nav/data-shell-agent/data-agent-status on a bounded local demo root, proving the contract is a plain attribute selector rather than anything wired onto the real shell (2-132/2-133's job). Toggle the app theme in the top bar to see --agent-status resolve through --primary/--success/--warning in both light and dark; the compact/touch pair below shows --shell-control-h's pointer:coarse reassignment side by side rather than requiring an actual touch device."
      >
        <ShellGeometryTokensGallery />
      </Section>

      <Section
        title="Tooltip"
        hint="Story 2-132. The visual on-hover/on-focus affordance for a trigger whose accessible name (aria-label) has nowhere to render — first consumer: the sidebar's collapsed icon-only nav rows. The bubble is aria-hidden (the trigger's own aria-label is already the accessible name; a second role=tooltip element would double-announce it) — hover OR Tab to the button below to reveal it. Story 2-147g anatomy note: Tooltip is content-agnostic and decorative-only (aria-hidden, never itself focusable) — it never carries a footer action row, or any interactive content at all. An action belongs in a Modal, never a Tooltip."
      >
        <Inline gap={6} className="p-2">
          <Tooltip label="Dashboard">
            <IconButton
              aria-label="Dashboard"
              variant="ghost"
              className="border border-border bg-muted text-muted-foreground"
              icon={<span aria-hidden="true">D</span>}
            />
          </Tooltip>
          <Tooltip label="Bottom-side example" side="bottom">
            <IconButton
              aria-label="Bottom-side example"
              variant="ghost"
              className="border border-border bg-muted text-muted-foreground"
              icon={<span aria-hidden="true">B</span>}
            />
          </Tooltip>
        </Inline>
      </Section>

      <Section
        title="Shell content-width modes"
        hint="Story 2-131, P10 §1.1. Three named max-width clamps a page selects for itself — no state attribute drives these. One long-content stress example per mode: real prose at --content-reading, a dense multi-column table at --content-operational (the same 72rem AppFrame's contentMaxWidth already defaults to), and an unclamped tile grid at --content-fluid."
      >
        <ShellContentWidthGallery />
      </Section>

      <Section
        title="Visual-language roles — canvas, surface, overlay, hairline"
        hint="Story 2-136, P10 §1.2. Reverses 2-127's 'glass/gradient as default' framing — see tokens.css's own comment (same story) for the full record. .glass-card/.glass/the gradient utilities are UNCHANGED and stay fully available (right pair below); canvas/surface/elevated-overlay/hairline mint NO new token — each is its existing semantic token's own Tailwind utility (bg-background/bg-card/bg-popover/border-border) so a component never reaches for bg-[hsl(var(--…))]. --shadow-elevated is the one genuinely new value. This story ships NO component change; toggle the app theme in the top bar for light/dark."
      >
        <VisualLanguageRoleSurfacesGallery />
      </Section>

      <Section
        title="Visual-language roles — typography, density, focus, status, motion"
        hint="Story 2-136, P10 §1.2. The two dense type rungs (11px/10px) and the wide-tracking eyebrow treatment tokens.css never named, a comfortable/compact density axis (data-density attribute reassignment, same contract shape as §1.1's shell states) plus the 48px touch floor as a hard density-independent minimum, the geometry index.css's .focus-ring already renders, status role names over the existing success/warning/destructive/neutral tokens, and the general-purpose motion timing that only the agent ring had before. The density toggle sets data-density on a bounded local demo root; the touch-target bar demonstrates the coarse-pointer floor side by side rather than requiring a real touch device, the same approach story 2-131 used for --shell-control-h."
      >
        <VisualLanguageRoleDetailGallery />
      </Section>

      <Section
        title="Report render (ReportRender)"
        hint="P06 §4, story 4-78. ONE frontend renderer, everywhere: review/report-detail and the template editor's own live preview both compose this exact component (the reports module's public export) — a template author previewing their layout and a reviewer reading a submitted report run identical rendering logic, only the data differs. Placeholder ReportContent below, one column per sectionHeaderStyle ('filled'|'centered'|'minimal', the template's actual saved 'Report layout' panel choice)."
      >
        <ReportRenderGallery />
      </Section>

      <Section
        title="Report photo-answer thumbnails (ReportInlinePhotoGrid)"
        hint="Story 4-83c, P06 §4. Before this story a photo/`image_gallery`-type answer's `ReportContentRow.value` / `ReportContentSection.tableRows` cell was a bare string — `inspectionAnswers.service.js`'s `render()` helper stringified the answer's raw evidence-id array, so the report editor/preview and the PDF showed a literal id (`6a87b5c26b232400e1781c24`) in place of a photo, in both a plain `<dl>` row and a `kind:'table'` cell. Distinct from the ALREADY-working media-summary GALLERY section (`content.mediaSummary` → `ReportMediaSummaryGrid`, story 4-80/4-81) — this is every OTHER place a photo answer's value appears. `ReportContentRow`/`ReportTableCell` now carry `kind`('text'|'photo-grid')/`photos` additively; `report.service.js` resolves a photo field's evidence ids to thumbnails (reusing `inspectionEvidence.service.js#listForInspection`, story 4-83a's resolver — no second one written) through `MEDIA_KIND_RENDERERS` (`backend/src/config/mediaEvidenceRenderers.js`), a lookup table keyed by the evidence's own `kind` — modeled on `captureEvidence.js`'s `UNSUPPORTED_EVIDENCE` idiom, `photo` the only populated entry today so a later AI-analyzed video/audio evidence kind is a data addition, not a rewrite (the story's own forward-looking constraint). Both `ReportRender.tsx`'s `<dl>` row and `ReportDataTable`'s cell dispatch through a matching `kind → renderer` lookup (`ROW_KIND_RENDERERS`/`CELL_KIND_RENDERERS`), and `pdfExport.service.js` carries the PDF-twin table of the SAME two keys. `ReportInlinePhotoGrid` itself, below, before the composite `Report render`/`Report content toggles` sections further down also show it wired into a real row ('Tap photo', under 'Tap (dripping)') and a real table cell (the fixture-inventory table's 'Notes' column, mixed with plain-text cells) — never a broken `<img>` either way."
      >
        <ReportInlinePhotoGridGallery />
      </Section>

      <Section
        title="Report content toggles (ReportScoreStrip / ReportTableOfContents / ReportFindingsRollup / ReportDisclaimer / ReportMediaSummaryGrid / ReportDataTable)"
        hint="Story 4-81, P06 §4. Before this story the template editor's 11 Content-tab checkboxes were saved, round-tripped state that neither renderer read — every one was a no-op, and `ReportContentSection` carried no `kind`, so a `table`-kind section flattened into the same generic label/value rows every plain section uses (a two-row table rendered as two sections headed 'Table 1'/'Table 2'). `report.service.js#resolveContentToggles` now resolves the SAME panel config the editor itself reads onto `ReportContent.contentToggles`, `ReportContentSection.kind` ('section'|'page'|'table') is now on the wire, and `ReportRender.tsx` gates 9 of the 11: scores -> ReportScoreStrip, tableOfContents -> ReportTableOfContents, flaggedItems/actions -> ReportFindingsRollup, disclaimer -> ReportDisclaimer (renders only when the toggle is on AND the still-null-by-design slot has text), mediaSummary -> ReportMediaSummaryGrid (grid tile size now also reads `style.thumbnailSize`), inspectionResults -> every non-table section, tables -> every `kind:'table'` section as a REAL <table> (ReportDataTable, a print-safe primitive with no glass-card — see its own header comment for why that specifically matters here), and (at the time) pageBreak -> a real break before every `kind:'page'` section plus an on-screen-only 'Page break' divider (print.css hides the divider and keeps the real break for an actual print/PDF) — RETIRED by story 4-110, see `ReportContentSection.pageBreak`'s own comment: that per-section field now drives the break, this toggle no longer does anything. A `contentToggles` that is MISSING (any report assembled before this story) merges against the editor's OWN defaults rather than 'everything off' — otherwise a historical report's main content would silently go blank the next time it renders. The backend PDF renderer (pdfExport.service.js#buildReportHtml) mirrors this exact toggle -> block gating, the `kind` split and the default-merge by hand, the same faithful-twin pattern the a86b338 fidelity fix established for section headers. The other 2 toggles (footer, summary) are deliberately deferred: footer needs a NEW web element (there is no footer on screen today, only the PDF's own chrome), and summary's prose lives inside the Overview section's body alongside Site/Status/Version rows that are not 'the summary' and should not hide with it."
      >
        <ReportContentTogglesGallery />
      </Section>

      <Section
        title="Report Studio — Media Tools (ReportMediaSummaryGrid layout/imageFit/captions/timestamps/grouping)"
        hint="Story 4-119, P06 §4, P10 §5. The report-wide 'Media summary' rollup block's own presentation — layout (grid/list/full-width/appendix), image-fit (cover/contain), caption/timestamp visibility and grouping by question/section. DISTINCT from the per-SECTION `evidencePlacement` (stories 4-116/4-117, which governs where one section's OWN inline photos render) — this is the SEPARATE, whole-report photo rollup at the end of the document. `thumbnailSize` (Style tab) was already wired into this grid before this story (story 4-76/4-81); verified, not re-wired. `groupBy: 'finding'` and `showLocation`/`showAnnotations` round-trip honestly but are disabled in the Media Tools panel and always resolve to their real, working default at render time — no per-photo capture location exists in this data model, photo annotations are a legacy-only mechanism retired story 4-83b, and a photo may be cited by zero/one/several findings with no single honest group. A broken/inaccessible thumbnail (not just a missing one) falls back to the same 'No preview' placeholder, mirroring the letterhead logo's own `onerror` fallback (story 4-107)."
      >
        <ReportMediaSummaryToolsGallery />
      </Section>

      <Section
        title="Report style — expanded (ReportCoverPage / heading scale / accent-derived secondary text)"
        hint="Story 4-82, P06 §4. Extends ReportStyle with three additive fields, trimmed from the story's full candidate list (page-numbering and a watermark/letterhead option were left out — see this story's SESSION-HANDOFF.md entry). coverPage: a new print-safe primitive (ReportCoverPage, components/ui, aria-hidden — every fact it shows is repeated moments later in the report's real semantic header, so it is intentionally invisible to the accessibility tree) rendered before the header, which then reuses the SAME report-page-break class/mechanism a kind:'page' section already uses for its own break — not a second break mechanism. headingScale ('sm'|'md'|'lg') steps SectionHeading's type size around the EXISTING per-sectionHeaderStyle defaults; 'md' is byte-for-byte those pre-4-82 sizes, so a template that never touches the control is unaffected. secondaryTextFromAccent derives a muted/secondary tint from the accent's hue, algorithmically clamped (frontend/src/modules/reports/reportColor.ts) to stay WCAG-AA (>=4.5:1) against a GUARANTEED WHITE background — which is why it only ever applies in the 'print' variant (and the backend PDF, always a white page), never 'screen', whose flat bg-card background is theme-dependent, not the fixed color the derivation could validate against. All three resolve through the same reportLayoutOf null-safe merge and round-trip through the existing Saved styles save/apply mechanism — no second persistence path."
      >
        <ReportStyleExpandedGallery />
      </Section>

      <Section
        title="Report style — font (ReportRender)"
        hint="Story 4-84, P06 §4. `ReportStyle.font` extended additively from a hardcoded 'sans'|'serif' placeholder to a real, curated three-option choice ('sans'|'serif'|'mono', `agents/shared/formSchema.js#REPORT_FONTS`) through the SAME resolution/round-trip path 4-76/4-82 established (`resolveReportStyle`, `saveReportStyle`/`listReportStyles` — no second persistence mechanism). All three are deliberately plain system/generic-font-family CSS stacks, never a new webfont: the backend PDF renderer has no access to the frontend's self-hosted 'Plus Jakarta Sans'/'Outfit' files (`pdfExport.service.js`'s own header comment), so a choice that resolved to a webfont on screen would render a DIFFERENT face in the downloaded PDF — exactly the fidelity gap this story's own done-when ('renders identically in the web preview, print variant, AND the downloaded PDF') rules out. 'mono' steps onto Tailwind's UNMODIFIED default `font-mono` utility (only `sans`/`display` are overridden in `tailwind.config.ts`) and the backend's byte-identical CSS stack under the same key — a class already available, not a new one added. The Style tab's font control (`ReportDesigner.tsx`) also switched from a raw radio `<input>` list to the existing `RadioGroup` design-system primitive (already a11y-passed and galleried above, just not previously used there), each option showing a live sample rendered IN that face."
      >
        <ReportStyleFontGallery />
      </Section>

      <Section
        title="Report style — decoration (section underline width / corner style)"
        hint="Story 4-85, P06 §4. Two more additive ReportStyle fields, same resolution/round-trip path 4-76/4-82/4-84 established (resolveReportStyle, patchReport/saveReportStyle/listReportStyles — no second persistence mechanism). sectionUnderlineWidth ('full'|'text', default 'full') steps the WIDTH of the rule a 'filled' or 'minimal' section heading already draws — 'filled''s tinted bar and 'minimal''s accent border-bottom are both block-level (full container width) with no explicit `display` pre-4-85, so 'full' renders byte-identically to before this story; 'text' adds `inline-block` (both renderers), shrinking either rule to hug only the heading's own text. 'centered' draws no rule either way and is unaffected by the token. surfaceRadius ('rounded'|'sharp', default 'rounded') is a corner-treatment ENUM, not a numeric-pixel picker (same constraint headingScale/thumbnailSize already stay inside) — it steps ONLY the filled/chip surfaces that already hardcoded a 6px radius: the 'filled' heading's tinted bar and the flagged/actions rollup's severity Badges (`ReportFindingsRollup`, `components/ui/Chip.tsx#Badge` gained an opt-in `radius` prop so every OTHER Badge caller app-wide is untouched). An outlined rollup-item card, the score-strip track/fill and the cover-page rule are not 'filled/chip' surfaces and stay exactly as they are. Both tokens' Style-tab controls (`ReportDesigner.tsx`) use the existing `RadioGroup` DS primitive, the same precedent 4-84's font control set."
      >
        <ReportStyleDecorationGallery />
      </Section>

      <Section
        title="Letterhead slot picker (SlotGridPicker / DirectionalPad / ReportLetterhead)"
        hint="Story 4-88b, P06 §4. 4-88a shipped the fixed 7-slot `ReportLetterhead` schema, persistence and the PDF-side render, but deliberately left the editor at a flat enable-list (no active-slot selection, no relocate) and the web preview completely unwired (both documented as this story's own scope in 4-88a's SESSION-HANDOFF entry). Two brand-new DS primitives close the editor gap: `SlotGridPicker` lays 4-88a's seven named slots onto the SAME 2D topology the PDF/web renderers already commit to (a 3-column logo row, a centered title/address stack below it, a 2-column footer row) and marks one cell &quot;active&quot; (aria-pressed) on click; `DirectionalPad` moves the active slot's CONTENT (a full `{enabled, content}` swap with whatever the target slot held, never a silent overwrite — see `letterheadGrid.ts#relocateLetterheadSlot`) to the adjacent named slot in that direction, skipping empty grid positions so all seven slots stay mutually reachable (`findAdjacentSlot`, exported alongside `SlotGridPicker` so the grid and the pad share one adjacency rule, not two that could drift). Both primitives are real `<button>`s throughout — Tab + Enter/Space always operates every cell and every direction; the pad ALSO answers arrow keys once focus lands anywhere inside it. A third new primitive, `ReportLetterhead`, is the actual missing web-preview piece: it renders the header slots (logos row + title + address) by hand-mirroring `pdfExport.service.js`'s `letterheadHeaderHtml` (same gating, same element order), which is what makes the live preview below and a downloaded PDF agree. Footer slots (page number / footer text) stay PDF-only by design — they resolve through Puppeteer's real per-page `footerTemplate` mechanism, and a page-less web preview has no per-page footer to show (documented in `reportPreviewContent.ts` since 4-88a, not re-litigated here). `ReportDesigner.tsx`'s Report-layout panel now composes all three."
      >
        <div className="space-y-6">
          <LetterheadPickerStatesGallery />
          <div className="border-t border-border pt-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Live demo — click a slot (or Tab + Enter/Space) to activate it, then use the directional pad (mouse,
              Tab + Enter/Space, or arrow keys) to relocate its content
            </p>
            <LetterheadPickerLiveDemo />
          </div>
        </div>
      </Section>

      <Section
        title="Paginated report page badge (ReportPageBadge)"
        hint="Story 4-108, P06 §4. The Template editor's 'Print' output preview now runs through Paged.js (a CSS-Paged-Media polyfill, behind the ONE adapter boundary `PagedReportPreview.tsx`, `modules/reports/components/`) instead of one continuous scroll — real page-shaped fragments at the template's configured pageSize, each with its own live page number. `ReportPageBadge` is that number: mounted onto each real page fragment via a React portal (`ReactDOM.createPortal`) once Paged.js finishes laying it out. Real text, not a CSS `content: counter(page)` margin box — generated CSS content is inconsistently exposed to screen readers across browser/AT combinations, and a real `<span>` costs nothing here since the page fragment is genuine DOM sharing this app's own stylesheet (unlike the backend PDF's isolated Puppeteer header/footer frame). Front matter (the cover page) never gets a badge at all — 'front matter does not consume a body page number' is implemented as a real DOM search (`ReportRender.tsx`'s own `data-report-body-start` marker) for which physical page the body actually starts on, not a guess; every page before it is front matter, every page from it onward is body page 1, 2, 3…"
      >
        <Cluster gap={3} align="center">
          <ReportPageBadge pageNumber={1} totalPages={4} />
          <ReportPageBadge pageNumber={2} totalPages={4} />
          <ReportPageBadge pageNumber={4} totalPages={4} />
        </Cluster>
      </Section>

      <Section
        title="Table of contents — dotted-leader page numbers (ReportTableOfContents)"
        hint="Story 4-111, P06 §4. `ReportTocEntry.page` (`contracts/reports.js`) is the section's REAL, resulting physical page number (1-based, cover page included — the same number a PDF viewer's own page indicator shows, distinct on purpose from `ReportPageBadge`'s unrelated front-matter-exempt 'body page' count), resolved server-side by a real Paged.js MEASUREMENT pass (`backend/src/services/pdf/reportTocPageNumbers.service.js`) run once ahead of whichever pipeline actually renders the report — the native single-pass Puppeteer pipeline most templates use, or the `everyContentPage` Paged.js pipeline (story 4-122) — so both a downloaded PDF and the live web view read the SAME number off the SAME frozen `assembledContent`. The number itself is real DOM text, never CSS `content: counter()`/`target-counter()` — the same 'real text, not generated content' rule `ReportPageBadge.tsx`'s own header comment establishes, for the same screen-reader-consistency reason. The dotted LEADER between label and number is purely decorative CSS (`aria-hidden`, a `border-bottom` filling the flex gap) in both renderers — never a literal run of '.' characters, which would wrap unpredictably at arbitrary label lengths. An entry with no `page` (content built before this story, or an unassembled report whose numbers are not resolved yet) renders the label alone, no leader, no guessed number — shown in the second column below. `PagedReportPreview.tsx` (the template editor's own client-side Paged.js preview) is the one caller that cannot know numbers before its OWN pagination pass runs against this exact markup — it renders the SAME dotted-leader row with an empty, hooked number slot (`pendingPageNumbers`, third column below) and fills each slot in via plain DOM text mutation once real layout exists, the same 'real DOM text once real layout exists' rule applied a step later."
      >
        <Grid columns={{ base: 1, lg: 3 }} gap={4}>
          <div className="space-y-2 rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Real numbers (assembled report / downloaded PDF)</p>
            <ReportTableOfContents
              entries={[
                { key: 'overview', label: 'Overview', page: 1 },
                { key: 'exterior', label: 'Exterior condition', page: 2 },
                { key: 'kitchen', label: 'Kitchen', page: 3 },
                { key: 'findings', label: 'Findings and recommended actions', page: 5 },
                { key: 'media-summary', label: 'Media summary', page: 8 },
              ]}
            />
          </div>
          <div className="space-y-2 rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">No numbers yet (unassembled report)</p>
            <ReportTableOfContents
              entries={[
                { key: 'overview', label: 'Overview' },
                { key: 'exterior', label: 'Exterior condition' },
                { key: 'kitchen', label: 'Kitchen' },
              ]}
            />
          </div>
          <div className="space-y-2 rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pending (PagedReportPreview, before its own layout pass fills the slot)</p>
            <ReportTableOfContents
              entries={[
                { key: 'overview', label: 'Overview' },
                { key: 'exterior', label: 'Exterior condition' },
                { key: 'kitchen', label: 'Kitchen' },
              ]}
              pendingPageNumbers
            />
          </div>
        </Grid>
      </Section>

      <Section
        title="Copy field (CopyField)"
        hint="Story 6-1a, P06 §6.1. A readonly value with one-tap copy-to-clipboard — first consumer: the share-link creation dialog's one-time URL (report.service.js never persists the raw token, so this is the ONLY place it is ever shown). readOnly, not disabled, so it stays focusable/selectable for assistive tech; the copy confirmation is both a visible label swap (Copy to Copied) and a polite role=status live-region announcement, and a denied/failed clipboard write leaves the label unchanged rather than claiming a false success (G-27's never-claim-a-false-Saved rule)."
      >
        <div className="max-w-sm">
          <CopyField label="Share link" value="https://app.defectsnap.example/share/9f2b7a1c4e6d8053a1f9c2b7e4d6805391a2c4e6d8053f1a2b7c9e4d6805391a" />
        </div>
      </Section>

      <Section
        title="AppFrame"
        hint="Story 2-127, 2026-08-22 spec §2-3. Shell layout-slot primitive: owns rail width, canvas max-width, and vertical rhythm via three flex columns (rail / canvas / panel) — all in real DOM/source order, so Tab visits rail → canvas → panel exactly as sighted users scan it; no CSS-order trick reshuffles focus. Presentational only (mapping doc §5): it takes rail/panel content as props and has zero knowledge of Sidebar/TopBar/routing — composing those in is AppShell.tsx's job, wired in story 2-128. This demo composes PageHeader (canvas) and RailPanel (panel) to prove the three new primitives work together, bounded to a fixed height so it reads as one example among many Section()s rather than filling the viewport."
      >
        <div className="h-96 overflow-hidden rounded-2xl border border-border">
          <AppFrame
            rail={
              <Stack as="nav" gap={1} className="p-4" aria-label="Example rail navigation">
                {['Dashboard', 'Inspections', 'Reports'].map((label, i) => (
                  <Button key={label} variant={i === 0 ? 'secondary' : 'ghost'} size="sm" block>
                    {label}
                  </Button>
                ))}
              </Stack>
            }
            panel={
              <div className="p-4">
                <RailPanel title="Version history" action={<Button variant="ghost" size="sm">See all</Button>}>
                  <p className="text-sm text-muted-foreground">v3 (current) · v2 · v1</p>
                </RailPanel>
              </div>
            }
          >
            <PageHeader
              headingLevel={2}
              title="13/883 Plenty Road"
              description="Routine inspection · Generated 2 minutes ago"
              chips={<Badge tone="success">Approved</Badge>}
              actions={<Button size="sm">Download</Button>}
            />
            <p className="text-sm text-muted-foreground">Canvas content — the primary reading surface — renders here.</p>
          </AppFrame>
        </div>
      </Section>

      <Section
        title="Header anatomy"
        hint={
          'Story 2-147e, P10 §2. Five header KINDS exist across the tree — this section names all five as ' +
          'one contract for the first time (nobody had before); each is demonstrated where it already lives ' +
          '(the "PageHeader" and "RailPanel" sections below) or, for the two kinds that had no shared name ' +
          'yet, demonstrated inline here.'
        }
      >
        <div className="space-y-6 text-sm">
          <Grid as="dl" columns={{ base: 1, sm: 2 }} gap={4}>
            <div>
              <dt className="font-semibold text-foreground">1 — Shell header (TopBar.tsx)</dt>
              <dd className="mt-1 text-muted-foreground">
                One per app, rendered once by AppShell outside every page's own content: org switcher,
                global search (⌘K), notifications, theme toggle. The ONLY header kind that ever renders
                <code className="mx-1">Breadcrumbs</code> — see the placement rule below. Out of scope to
                change here; not demonstrated standalone since it is shell chrome, not a page-composable
                primitive (the real TopBar is visible in this very page's own header).
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">2 — Page header (PageHeader.tsx)</dt>
              <dd className="mt-1 text-muted-foreground">
                Title + description + chips + actions + optional toolbar. Bare by default (Dashboard,
                Settings, Billing, …) — a collection page (<code>EntityListPage</code>) wraps the identical
                primitive in a flat <code>bg-card</code> surface instead, per P10 §1.3's "list-page header"
                example. Demonstrated in the "PageHeader" section directly below, including the collection
                shape.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">3 — Workspace header (WorkspaceHeader, story 2-147sb)</dt>
              <dd className="mt-1 text-muted-foreground">
                Identity (back-link + title) + two control clusters (tools; save/lifecycle) — the same
                identity-vs-controls split PageHeader uses. Named by 2-147e as a slot contract with no
                component behind it yet; 2-147sb builds the real, reusable <code>WorkspaceHeader</code>{' '}
                (<code>components/ui/Headers.tsx</code>) this section now demonstrates directly, and{' '}
                <code>WorkspaceLayout</code> (see "Executable page layouts" below) composes.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">4 — Context header (ContextHeader, story 2-147sb; extracted from RailPanel.tsx)</dt>
              <dd className="mt-1 text-muted-foreground">
                A rail panel's own title + optional trailing action, heading level 2-4. Already real and
                already consistent (2-147e's own finding) — 2-147sb extracts that shape out of{' '}
                <code>RailPanel.tsx</code>'s render body into an independently reusable{' '}
                <code>ContextHeader</code> component (<code>components/ui/Headers.tsx</code>), which{' '}
                <code>RailPanel</code> now composes instead of duplicating the markup — a refinement,
                byte-identical classes, not a rebuild. Demonstrated in the "RailPanel" section below.
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="font-semibold text-foreground">5 — Section header (SectionHeader, story 2-147sb)</dt>
              <dd className="mt-1 text-muted-foreground">
                An in-page subheading with no card/slot contract of its own.
                <code className="mx-1">text-lg font-semibold text-foreground</code> is the convention
                ALREADY followed by every real consumer checked (BillingPage, DashboardPage,
                PlatformConsolePage, PricingPage, ActivationChecklistCard, NewSubjectDialog and the
                template/feature-request dialogs) — no drift found, just never stated as a rule until 2-147e,
                and never a real component until 2-147sb's <code>SectionHeader</code> (
                <code>components/ui/Headers.tsx</code>), demonstrated inline below (and, self-referentially,
                by this very gallery's own <code>Section()</code> heading, which predates it and stays its
                own inline implementation — see <code>Headers.tsx</code>'s own header comment for why).
              </dd>
            </div>
          </Grid>

          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Breadcrumbs placement rule
            </p>
            <p className="text-muted-foreground">
              Shell header only. <code>Breadcrumbs</code> is composed exactly once in the whole tree, inside{' '}
              <code>TopBar.tsx</code> (verified by reading the wiring, not assumed — one production call
              site). Page / Workspace / Context / Section headers never carry their own crumb trail:{' '}
              <code>resolveBreadcrumbs</code> is a path + nav-registry + capability resolution the shell
              already performs once per navigation — a second copy inside a page header would risk drifting
              from the sidebar's own active-state pass, which reads the same registry.
            </p>
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              3 — Workspace header demo
            </p>
            <WorkspaceHeaderDemo />
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              4 — Context header demo (standalone, outside RailPanel's own card)
            </p>
            <div className="rounded-hairline border border-dashed border-border p-4">
              <ContextHeader
                title="Recent activity"
                action={<Button variant="ghost" size="sm">See all</Button>}
                headingLevel={3}
              />
            </div>
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              5 — Section header demo
            </p>
            <div className="rounded-hairline border border-dashed border-border p-4">
              <SectionHeader
                title="Plan overview"
                description="No card wrapper — SectionHeader renders the exact classes BillingPage.tsx and DashboardPage.tsx already use directly, now named and reusable."
              />
            </div>
          </div>
        </div>
      </Section>

      <Section
        title="PageHeader"
        hint="Story 2-127, 2026-08-22 spec §3.1, §4.2-4.3. Title + supporting metadata + status chips + a right-aligned action cluster, with an optional toolbar row underneath — the one primitive this story's two real consumers (DashboardPage and ReportDetailPage, both wired in story 2-130) both need. Presentational: chips/actions/toolbar are slots filled with existing Badge/Button, never PageHeader's own opinion of what a chip says. headingLevel defaults to 1 (a real page's own `<h1>`) — this demo passes headingLevel=2 so the gallery doesn't grow a second page-level h1. Story 2-147e names this the 'page header' kind (#2 of 5, see 'Header anatomy' above) and adds the third demo below: the flat `bg-card`-wrapped shape `EntityListPage.tsx` now composes for every collection route, proving the SAME PageHeader primitive underlies both the bare and the flat-wrapped camp — only the wrapper differs, never the header contract itself."
      >
        <div className="space-y-6">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Dashboard shape — title + freshness + quick actions
            </p>
            <PageHeader
              headingLevel={2}
              title="Dashboard"
              description="Updated 2 minutes ago"
              actions={
                <>
                  <Button variant="secondary" size="sm">Invite teammate</Button>
                  <Button size="sm">New inspection</Button>
                </>
              }
            />
          </div>
          <div className="border-t border-border pt-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Report detail shape — title + address + status/version chips + toolbar
            </p>
            <PageHeader
              headingLevel={2}
              title="13/883 Plenty Road, South Morang"
              description="Routine inspection · Generated 22 Aug 2026"
              chips={
                <>
                  <Badge tone="success">Approved</Badge>
                  <Badge tone="neutral">v3</Badge>
                </>
              }
              actions={<Button size="sm">Download</Button>}
              toolbar={
                <>
                  <Button variant="ghost" size="sm">Print</Button>
                  <Button variant="ghost" size="sm">Open</Button>
                </>
              }
            />
          </div>
          <div className="border-t border-border pt-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Collection shape (story 2-147e) — same PageHeader, wrapped in a flat `bg-card` surface
              (`EntityListPage.tsx`'s own composition; P10 §1.3 names "a list-page header" as this role's
              canonical example)
            </p>
            <div className="rounded-hairline border border-border bg-card p-6">
              <PageHeader
                headingLevel={2}
                title="Sites"
                description="Every property your org inspects."
                actions={<Button size="sm">New site</Button>}
              />
            </div>
          </div>
        </div>
      </Section>

      <Section
        title="RailPanel"
        hint={
          'Story 2-127, 2026-08-22 spec §3.1, §4.3-4.4. Compact side-column card for CONTINUOUS workspace ' +
          'context alongside a page’s main content — reserved for cases that actually warrant staying on ' +
          'screen the whole time (P10 §2’s disclosure rule, story 2-140), not a default for any secondary ' +
          'information. `ReportDetailPage`’s own "Share access" panel below is the real, current consumer. ' +
          '"Version history" moved OUT of RailPanel in story 2-140 — that content is occasional management, ' +
          'not continuous context, so it is now `VersionPanel` (its own Section below), which owns its own card ' +
          'rather than being wrapped in this one. Content-agnostic: title + optional header action + a children ' +
          'slot — what renders inside is each consumer’s own composition. Visual treatment deliberately ' +
          'breaks from `.glass-card` (kept, unused here): flat `--card` surface, hairline `--border`, small ' +
          '`--radius-sm` corners. Story 2-147e names this the "context header" kind (#4 of 5, see "Header ' +
          'anatomy" above) — already real and already consistent, cited there as the worked example rather ' +
          'than rebuilt.'
        }
      >
        <Grid columns={{ base: 1, sm: 2 }} gap={4}>
          <RailPanel title="Summary" action={<Button variant="ghost" size="sm">See all</Button>}>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">Continuous context</span>
                <Badge tone="success">Always visible</Badge>
              </div>
              <p className="text-muted-foreground">
                A person consults this WHILE working the main content — not a drill-down they open and close.
              </p>
            </div>
          </RailPanel>
          <RailPanel title="Share access" action={<Button variant="ghost" size="sm">New link</Button>}>
            <div className="space-y-3 text-sm">
              <div className="rounded-hairline border border-border p-3">
                <div className="flex items-center justify-between">
                  <Badge tone="success">Active</Badge>
                  <Button variant="ghost" size="sm">Revoke</Button>
                </div>
                <p className="mt-2 text-muted-foreground">External viewer · created by Kundan Ray</p>
                <p className="mt-1 text-xs text-muted-foreground">Expires 30 Aug 2026 · 4 opens</p>
              </div>
            </div>
          </RailPanel>
        </Grid>
      </Section>

      <Section
        title="VersionPanel"
        hint={
          'Controlled cross-module version history (story 2-140, P10 §2.4) — REPLACES the RailPanel-wrapped ' +
          '`VersionHistory` that used to live in the reports module: owns its own card and heading rather ' +
          'than being wrapped in RailPanel again (avoiding the nested-card composition this story exists to ' +
          'remove). Status vocabulary (draft/published/approved/superseded/archived) is generic — the caller ' +
          'resolves its own lifecycle to this closed set plus a display label, same "primitive holds no ' +
          'module-specific transition rules" contract as StatusChip/StatusStepper. One real consumer today: ' +
          '`ReportDetailPage`.'
        }
      >
        <VersionPanel
          title="Version history"
          hint="Every version is kept. Older versions are read-only — a change to a report creates a new version."
          selectedId={3}
          rows={[
            {
              id: 3,
              label: 'v3',
              status: 'draft',
              statusLabel: 'Draft',
              isCurrent: true,
              meta: ['Q3 fire safety inspection', 'Generated 22 Aug 2026'],
              action: (
                <Button variant="secondary" size="sm" disabled aria-label="View version 3">
                  Viewing
                </Button>
              ),
            },
            {
              id: 2,
              label: 'v2',
              status: 'approved',
              statusLabel: 'Approved',
              meta: ['Q3 fire safety inspection', 'Generated 15 Aug 2026'],
              lockedReason: "Approved 16 Aug 2026 — locked: an approved version's PDF cannot be changed.",
              action: (
                <Button variant="secondary" size="sm" aria-label="View version 2">
                  View
                </Button>
              ),
            },
            {
              id: 1,
              label: 'v1',
              status: 'superseded',
              statusLabel: 'Superseded',
              meta: ['Q3 fire safety inspection', 'Generated 2 Aug 2026'],
              action: (
                <Button variant="secondary" size="sm" aria-label="View version 1">
                  View
                </Button>
              ),
            },
          ]}
        />
      </Section>

      <Section
        title="StatusChip"
        hint={
          'The shared lifecycle-state renderer (story 2-141, P10 §2.2/§2.4) — "lifecycle renderer for ' +
          'templates/inspections/actions… state must not rely on colour alone (WCAG 1.4.1), and the ' +
          'primitive holds no module-specific transition rules — modules pass their states in." Its closed ' +
          'vocabulary is the SAME five-tone set `Badge` already defines (primary/success/warning/' +
          'destructive/neutral) — every module already converges its own richer status enum onto one of ' +
          'these five today (TemplateCard, InspectionLifecycleBar, ReviewDetailPage, VersionPanel, ' +
          'ShareLinksPanel each had their own hand-rolled copy of exactly this map). What StatusChip adds ' +
          'structurally is a FIXED icon per tone plus the existing `weight` (subtle/bold) axis, so two ' +
          'different states are never distinguishable by hue alone — verify by squinting or viewing the row ' +
          'below under grayscale: five icons (spinning-arrows / check / triangle / cross / minus-circle) stay ' +
          'distinguishable with the colour removed. `label` is always the accessible name; `value` carries ' +
          'the module’s own raw status string for `data-status` only, never for styling. Real consumers: ' +
          'TemplateCard (draft/in_review/published/archived), InspectionLifecycleBar and ReviewDetailPage ' +
          '(the shared nine-state inspection lifecycle — this fixed a real bug where every non-terminal ' +
          'state rendered identically), VersionPanel (draft/published/approved/superseded/archived), ' +
          'ShareLinksPanel (active/expired/revoked).'
        }
      >
        <div className="space-y-4">
          <Cluster gap={2} data-testid="statuschip-gallery-row">
            <StatusChip tone="neutral" label="Draft" value="draft" />
            <StatusChip tone="primary" label="Assigned" value="assigned" />
            <StatusChip tone="primary" weight="bold" label="In progress" value="in_progress" />
            <StatusChip tone="warning" weight="bold" label="In review" value="in_review" />
            <StatusChip tone="success" label="Approved" value="approved" />
            <StatusChip tone="warning" label="Revoked" value="revoked" />
            <StatusChip tone="neutral" weight="bold" label="Archived" value="archived" />
            <StatusChip tone="destructive" label="Cancelled" value="cancelled" />
          </Cluster>
          <p className="text-xs text-muted-foreground">
            Weight breaks same-tone ties (Assigned/In progress both `primary`; In review/Revoked both `warning`)
            so every pair here differs by more than hue even before the label is read — the exact scheme
            `InspectionLifecycleBar.tsx`&apos;s `STATE_WEIGHT` map now uses live, not a gallery-only flourish.
            Every chip also carries a distinct icon per tone, matching the same &quot;shape, not just hue&quot; discipline
            `StatusStepper` (below) and `VersionPanel`&apos;s Archived row already use.
          </p>
        </div>
      </Section>

      <Section
        title="StatusStepper"
        hint={
          'The shared state-machine renderer (story 2-141, P10 §2.2/§2.4) — "stepper enforces state machine, ' +
          'disabled steps tooltip the reason." A RENDERER, not a machine of its own: every step is exactly ' +
          'one of four states (complete/current/upcoming/blocked) the caller resolves, never derived here. A ' +
          '`blocked` step’s `reason` is REQUIRED and renders as real text under the step (never a tooltip ' +
          'alone — the same discipline `InspectionLifecycleBar`’s disabled actions already established), ' +
          'plus `aria-describedby` so it reaches assistive tech on focus, not only on hover. Story 2-147k gave ' +
          'this its first real consumer: `InspectionLifecycleBar`’s `capture` variant, replacing the flat ' +
          'blocked-action wall with this ordered-stage view in `CapturePage`’s inspector panel — the same ' +
          'position `AgentPlanCard`/`OptionCard` shipped from before a second consumer existed, now realized. ' +
          'That mount is a fixed-width sidebar column, not a full page width, so it first tried a ' +
          '`layout=\'horizontal\'` mode (always a single scrollable row) — direct, live user feedback then ' +
          'asked for the panel\'s own unused VERTICAL space instead: `layout=\'vertical\'` (the real consumer ' +
          'today) is always a single COLUMN, dot-and-continuous-line — the chronology/history-list shape ' +
          '`docs/design/sitewise-ux-reference.html`’s own `.swa-version-panel`/`.swa-version-row` already ' +
          'establishes, carried into a React component for the first time here. `horizontal` stays published ' +
          '(below) — a real, demoed, a11y-passed mode without a live consumer today, the same position this ' +
          'primitive itself shipped from before 2-147k. The default `layout=\'stacked\'` (first demo) is ' +
          'byte-for-byte the original story-2-141 rendering — additive only, zero risk to it. `StepMarker` ' +
          '(the per-state circle sub-component) is exported alongside this primitive so `SectionNav` ' +
          '(`modules/capture/components/`) can reuse the identical marker for its own vertical timeline rail ' +
          '— one shared design language across the capture panel\'s rail AND its inspector panel, not two ' +
          'look-alikes drifting apart.'
        }
      >
        <div className="max-w-2xl space-y-6">
          <StatusStepper
            aria-label="Example inspection stage sequence"
            data-testid="statusstepper-demo"
            steps={[
              { id: 'draft', label: 'Draft', state: 'complete' },
              { id: 'assigned', label: 'Assigned', state: 'complete' },
              { id: 'in_progress', label: 'In progress', state: 'current' },
              { id: 'review', label: 'Review', state: 'upcoming' },
              {
                id: 'approved',
                label: 'Approved',
                state: 'blocked',
                reason: 'Two unresolved AI findings must be decided before this inspection can be approved.',
              },
            ]}
          />
          <Cluster gap={8} align="start">
            <div>
              <p className="mb-2 text-xs uppercase tracking-eyebrow text-muted-foreground">
                layout=&quot;vertical&quot; — the real consumer (InspectionLifecycleBar&apos;s capture panel, 22rem)
              </p>
              <div className="w-80 rounded-lg border border-border p-3">
                <StatusStepper
                  aria-label="Example inspection stage sequence, vertical"
                  data-testid="statusstepper-vertical-demo"
                  layout="vertical"
                  steps={[
                    { id: 'draft', label: 'Draft', state: 'complete' },
                    { id: 'scheduled', label: 'Scheduled', state: 'complete' },
                    { id: 'assigned', label: 'Assigned', state: 'complete' },
                    { id: 'in_progress', label: 'In progress', state: 'current' },
                    { id: 'submitted', label: 'Submitted', state: 'upcoming' },
                    { id: 'in_review', label: 'Review', state: 'upcoming' },
                    { id: 'approved', label: 'Approved', state: 'upcoming' },
                    { id: 'delivered', label: 'Delivered', state: 'upcoming' },
                  ]}
                />
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs uppercase tracking-eyebrow text-muted-foreground">
                layout=&quot;horizontal&quot; — published, no live consumer today
              </p>
              <div className="w-80 rounded-lg border border-border p-3">
                <StatusStepper
                  aria-label="Example inspection stage sequence, horizontal"
                  data-testid="statusstepper-horizontal-demo"
                  layout="horizontal"
                  steps={[
                    { id: 'draft', label: 'Draft', state: 'complete' },
                    { id: 'scheduled', label: 'Scheduled', state: 'complete' },
                    { id: 'assigned', label: 'Assigned', state: 'complete' },
                    { id: 'in_progress', label: 'In progress', state: 'current' },
                    { id: 'submitted', label: 'Submitted', state: 'upcoming' },
                    { id: 'in_review', label: 'Review', state: 'upcoming' },
                    { id: 'approved', label: 'Approved', state: 'upcoming' },
                    { id: 'delivered', label: 'Delivered', state: 'upcoming' },
                  ]}
                />
              </div>
            </div>
          </Cluster>
        </div>
      </Section>

      <Section
        title="Layout recipes"
        hint={
          'Story 2-147f, P10 §1.1/§2. Six documented compositions over 2-131\'s geometry tokens — one per real ' +
          'registered layout family (frontend/src/lib/registries/layoutFamilies.ts, story 2-134, the file ' +
          'check-architecture-boundaries.mjs enforces every route against). Collection and Workspace are shown ' +
          'by cross-reference — "PageHeader"\'s Collection-shape demo + "Shell content-width modes"\'s ' +
          '--content-operational table, and "AppFrame" + "Header anatomy"\'s Workspace-header demo, above — ' +
          'rather than rebuilt a second time; Dashboard, Workflow, Administration and External get a fresh demo ' +
          'below since nothing already in this page shows their shape. A naming note first: P16 §6.1\'s own lane ' +
          'prose mentions a "detail" page in passing, but the real closed set of six has no such key — a single ' +
          'record\'s own view (ReportDetailPage, ReviewDetailPage, the Site and Inspection detail routes) is ' +
          'registered `workflow` by name (story 2-146b\'s own code comment: "a single Report\'s own view... is a ' +
          'record moving through state, not a second list-composition family"). This story writes the Workflow ' +
          'recipe against that real registration rather than inventing a phantom seventh family, which would ' +
          'contradict the registry 2-148 is about to widen enforcement over.'
        }
      >
        <div className="space-y-8 text-sm">
          <Grid as="dl" columns={{ base: 1, sm: 2 }} gap={6}>
            <div>
              <dt className="font-semibold text-foreground">1 — Dashboard</dt>
              <dd className="mt-1 space-y-1 text-muted-foreground">
                <p>Purpose: the at-a-glance landing composition — aggregate cards, activation checklist, recent-activity feed.</p>
                <p>
                  Width: inherits <code>--content-operational</code> via <code>AppShell</code>'s own top-level{' '}
                  <code>AppFrame</code> — <code>DashboardPage.tsx</code> has no <code>AppFrame</code>/<code>max-w-*</code>/
                  <code>mx-auto</code> of its own (confirmed by reading the file).
                </p>
                <p>Header: Page header (kind #2), bare — story 2-130.</p>
                <p>
                  Responsive: no rail/panel; a flat <code>space-y-6</code> stack of self-contained blocks, each
                  reflowing on its own (the KPI grid: <code>grid gap-4 md:grid-cols-4</code> → one column under{' '}
                  <code>md</code>).
                </p>
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">2 — Collection</dt>
              <dd className="mt-1 space-y-1 text-muted-foreground">
                <p>Purpose: search/filter/sort/select over a set of records, URL-owned state, row actions.</p>
                <p>
                  Width: same shell-default inheritance — <code>EntityListPage.tsx</code> (the family's own
                  template, re-confirmed live for this story) still has no <code>AppFrame</code>/width override
                  of its own; 2-147e's header change only touched the wrapper, not the outer <code>space-y-6</code>{' '}
                  structure.
                </p>
                <p>
                  Header: Page header (kind #2), wrapped in a flat <code>bg-card</code> surface (P10 §1.3 names "a
                  list-page header" as this treatment's canonical example).
                </p>
                <p>
                  Responsive: the filter bar (<code>flex flex-wrap</code>) wraps at narrow widths; the table/grid
                  content is each consumer's own responsibility — the template's <code>children</code> slot exists
                  exactly for a custom grid at this seam.
                </p>
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="font-semibold text-foreground">3 — Workflow</dt>
              <dd className="mt-1 space-y-1 text-muted-foreground">
                <p>Purpose: a record moving through an explicit lifecycle — transitions, blockers and their consequences visible before confirmation.</p>
                <p>
                  Width: inherits the shell default; neither real consumer clamps narrower (confirmed:{' '}
                  <code>ReportDetailPage.tsx</code>, <code>ReviewDetailPage.tsx</code> — no <code>AppFrame</code>/
                  <code>max-w-*</code> of their own).
                </p>
                <p>
                  Header: <strong>unconverged across the family's own two real shapes</strong> — <code>ReportDetailPage.tsx</code>{' '}
                  composes Page header (kind #2, chips + toolbar); <code>ReviewDetailPage.tsx</code> hand-rolls its
                  own <code>&lt;header&gt;&lt;h1&gt;</code>, never wired to <code>PageHeader</code> — a real, named
                  drift, the same class of finding 2-147e recorded for <code>EntityListPage</code>'s pre-story
                  header. Left unconverged here; no production route migrates in this story.
                </p>
                <p>
                  Panel: two real shapes — (a) <code>RailPanel</code>/<code>VersionPanel</code> occasional-management
                  content beside the record, in a plain (non-<code>AppFrame</code>) grid (
                  <code>ReportDetailPage.tsx</code>'s <code>grid gap-5 lg:grid-cols-[1fr,320px]</code>); (b) two panes
                  of the SAME record with no side panel at all (<code>ReviewDetailPage.tsx</code>'s{' '}
                  <code>hidden lg:grid lg:grid-cols-2</code> split).
                </p>
                <p>
                  Responsive: shape (a) stacks the panel below the content under <code>lg</code> (the grid's own
                  single breakpoint); shape (b) swaps entirely to a <code>Tabs</code> primitive under <code>lg</code>{' '}
                  rather than stacking (<code>ReviewDetailPage.tsx</code>'s own <code>lg:hidden</code> Tabs block) —
                  a materially different collapse strategy the two shapes do not share.
                </p>
                <p>
                  <strong>The nested-AppFrame-vs-plain-grid call, made explicit:</strong> <code>AppFrame</code>'s
                  three-column contract (persistent rail + width-clamped canvas + panel) is built for a{' '}
                  <em>workspace</em> — a tool rail beside a canvas someone actively edits. Neither workflow shape
                  has a persistent left rail; both are a single record's own view plus at most one secondary column
                  of occasional or parallel content. Composing <code>AppFrame</code> here would add a rail slot
                  neither shape uses, for no gain over the plain grid already in place — <code>RailPanel</code>-in-
                  a-plain-grid (or no panel at all) is the correct, simpler recipe for this family; <code>AppFrame</code>{' '}
                  stays reserved for Workspace.
                </p>
                <p>
                  <strong>A genuine token gap, named rather than papered over:</strong> 2-131 minted{' '}
                  <code>--content-reading</code>/<code>-operational</code>/<code>-fluid</code> (canvas width),{' '}
                  <code>--shell-agent-w</code> (agent drawer width) and <code>--panel-gap</code> (spacing between
                  stacked panel sections) — but no dedicated "side panel width" token. <code>AppFrame</code>'s own{' '}
                  <code>panelWidth</code> prop defaults to a literal <code>'20rem'</code> (its own comment: "the
                  width the report-rail share-link cards are sized for"), and <code>ReportDetailPage.tsx</code>'s
                  ungoverned <code>320px</code> grid column reaches for the same rough proportion by coincidence,
                  not by a shared name. The demo below therefore uses plain fractional columns rather than
                  pretending a token traces a width neither real consumer's number actually comes from.
                </p>
                <div className="pt-2">
                  <WorkflowRecipeDemo />
                </div>
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">4 — Workspace</dt>
              <dd className="mt-1 space-y-1 text-muted-foreground">
                <p>Purpose: tool-rail → canvas → contextual-inspector authoring/editing (e.g. a template or report editor).</p>
                <p>
                  Width: <code>AppFrame</code>'s <code>contentMaxWidth</code>, per consumer —{' '}
                  <code>TemplateEditorPage.tsx</code> overrides to <code>var(--content-fluid)</code> (a
                  canvas-filling editor); <code>CapturePage.tsx</code> takes the shell's own outer clamp (
                  <code>padded=&#123;false&#125;</code>, nests <code>AppFrame</code> for rail/panel only, not a
                  second canvas clamp) — both real, both already token-sourced.
                </p>
                <p>Header: Workspace header (kind #3) — identity + tools cluster + save/lifecycle cluster; demonstrated inline in "Header anatomy" above.</p>
                <p>
                  Responsive: <code>AppFrame</code>'s <code>railClassName</code>/<code>panelClassName</code> (both{' '}
                  <code>"hidden lg:block"</code> on <code>CapturePage.tsx</code>) drop the rail/panel columns
                  entirely below <code>lg</code> rather than squeezing them, each with a named mobile equivalent
                  (a bottom-sheet <code>SectionNav</code>, an inline lifecycle bar) — demonstrated in "AppFrame"
                  above rather than rebuilt here.
                </p>
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">5 — Administration</dt>
              <dd className="mt-1 space-y-1 text-muted-foreground">
                <p>Purpose: authenticated org/platform governance surfaces — settings, access/roles, billing, platform console.</p>
                <p>Width: same shell-default inheritance (<code>OrgAccessPage.tsx</code> has no <code>AppFrame</code>/width override of its own).</p>
                <p>Header: Page header (kind #2), bare — same reasoning as Dashboard (no adjacent list to pair the header with).</p>
                <p>
                  Responsive/panel: a paired form/summary grid — <strong>a genuine, live finding</strong>:{' '}
                  <code>OrgAccessPage.tsx</code>'s two real instances (<code>lg:grid-cols-[1fr_20rem]</code>,{' '}
                  <code>lg:grid-cols-[18rem_1fr]</code>) are literal, un-tokenized arbitrary values — exactly the
                  class of violation <code>check-shell-geometry.mjs</code> forbids in its six closed files, but has
                  no jurisdiction over here (a page, outside <code>CLOSED_FILES</code>). Named, not migrated — 2-148's
                  tree-wide widening is the story that will catch it.
                </p>
                <div className="pt-2">
                  <AdministrationRecipeDemo />
                </div>
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">6 — External</dt>
              <dd className="mt-1 space-y-1 text-muted-foreground">
                <p>Purpose: unauthenticated or minimal-chrome surfaces — marketing, auth, shared/public report views.</p>
                <p>
                  Width: no shell inheritance at all — these routes render outside <code>AppShell</code> entirely, so
                  there is no outer <code>AppFrame</code> clamp. Currently literal, not token-sourced:{' '}
                  <code>PublicLayout.tsx</code>'s <code>max-w-6xl</code>, <code>SharedReportPage.tsx</code>'s{' '}
                  <code>max-w-3xl</code>/<code>max-w-md</code> — neither reaches for a <code>--content-*</code> token
                  (confirmed by grep: zero real consumers of <code>--content-reading</code> or{' '}
                  <code>--content-operational</code> outside <code>tokens.css</code> and this gallery — the same
                  "still theoretical" finding 2-131 itself recorded for the density token; only{' '}
                  <code>--content-fluid</code> has a real consumer, <code>TemplateEditorPage.tsx</code>).
                </p>
                <p>Header: no page/workspace header kind applies — each screen hand-rolls a minimal identity row; not converged here.</p>
                <p>Responsive: single column, centered, no rail/panel — the simplest recipe by construction.</p>
                <div className="pt-2">
                  <ExternalRecipeDemo />
                </div>
              </dd>
            </div>
          </Grid>

          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1 — Dashboard demo</p>
            <DashboardRecipeDemo />
          </div>
        </div>
      </Section>

      <GallerySection
        title="Executable page layouts"
        hint={
          'Story 2-147sb, P10 §2.6. Replaces the "Layout recipes" Section\'s documentation-only demos above ' +
          'with the real, importable DashboardLayout/CollectionLayout/WorkflowLayout/WorkspaceLayout/' +
          'AdministrationLayout/ExternalLayout components (components/ui/PageLayouts.tsx) — 2-147f\'s own ' +
          'scope explicitly stopped at documentation ("not in scope: migrating any specific page"); this is ' +
          'the recipe as a component a page can now actually import. Each demo below composes a real content-' +
          'stress case (long titles/descriptions from the CONTENT_STRESS_CASES fixture, story 2-137; a 4-action ' +
          'HeaderActions cluster on the Administration demo to prove its own overflow collapse) rather than a ' +
          'lorem-ipsum happy path, matching this lane\'s content-stress discipline (2-147a/2-147k). Every layout ' +
          'is built from 2-147sa\'s Container/Stack/Split/Cluster/Grid primitives internally and composes the ' +
          'right header kind for its family — PageHeader (Dashboard/Collection/Workflow/Administration) or the ' +
          'new WorkspaceHeader (Workspace); External hand-rolls its own minimal identity row via a plain slot, ' +
          'per P10 §2.6 ("no page/workspace header kind applies"). Every width traces to a 2-131 --content-* ' +
          'token through Container\'s own width prop (WorkspaceLayout resolves the same token map through ' +
          'Layout.tsx\'s newly-exported CONTAINER_WIDTH_VAR, feeding AppFrame\'s contentMaxWidth) — zero new ' +
          'arbitrary-value geometry. Not a page migration (2-147w*\'s job) — gallery-only.'
        }
      >
        <div className="space-y-10">
          <ExampleFrame label="1 — DashboardLayout — long freshness line + a 3-action HeaderActions cluster">
            <DashboardLayoutDemo />
          </ExampleFrame>
          <ExampleFrame label="2 — CollectionLayout — bg-card-wrapped PageHeader + filter row + long-address table rows + footer pager">
            <CollectionLayoutDemo />
          </ExampleFrame>
          <ExampleFrame label="3 — WorkflowLayout — long title/address, chips + toolbar, Split-based RailPanel (shape a)">
            <WorkflowLayoutDemo />
          </ExampleFrame>
          <ExampleFrame label="4 — WorkspaceLayout — WorkspaceHeader + AppFrame rail/canvas/inspector, bounded to a fixed height">
            <WorkspaceLayoutDemo />
          </ExampleFrame>
          <ExampleFrame label="5 — AdministrationLayout — long title, 4 actions overflowing HeaderActions' 3-visible budget, Split panel">
            <AdministrationLayoutDemo />
          </ExampleFrame>
          <ExampleFrame label="6 — ExternalLayout — no shell inheritance, --content-reading width, long title">
            <ExternalLayoutDemo />
          </ExampleFrame>
        </div>
      </GallerySection>

      <GallerySection
        title="Surface and card compositions"
        hint={
          'Story 2-147ta, P10 §2.7 row 5. Closes the row\'s own named gap ("Flat surface / card wrapper — ' +
          'Named but not yet built ... no standalone Surface/Card file exists in components/ui today") — the ' +
          'direct successor to 2-147sb\'s layouts/headers above. `Surface` formalizes P10 §1.3\'s four surface ' +
          'roles (bare/flat/elevated/glass) into a real component instead of every consumer re-deriving ' +
          '`rounded-hairline border border-border bg-card` (or `bg-popover`+`shadow-elevated`, or ' +
          '`.glass-card`) by hand — measured at 44 files carrying that exact hand-rolled pattern tree-wide ' +
          '(SESSION-HANDOFF.md records the full count). `Card`/`CardHeader`/`CardBody`/`CardFooter` are the ' +
          'slot family built on `Surface`; every action area composes 2-147r\'s `CardActions`, never a raw ' +
          '`Button` list. `MetricCard` and `EntityCard` are the two named card SHAPES composed OF `Card` — ' +
          '`TaskCard` (its own Section above) is the third shape, now composing `Surface` directly instead of ' +
          're-deriving its own border/bg/radius. `KpiCard` (components/dashboard, Dashboard-scoped — its own ' +
          'rule-of-three scoping already settled by 2-147d, not re-litigated here) now composes `MetricCard` ' +
          'internally for the same reason. `EntityCard` gets the full long-content/loading/error/locked/' +
          'responsive `StateMatrix` treatment — the same non-layout-family worked precedent ' +
          '`GalleryScaffoldingProofDemo` (2-147p, "Gallery scaffolding" Section) already established for ' +
          '`Button`, applied here to a composite.'
        }
      >
        <div className="space-y-8">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Surface — the four P10 §1.3 visual roles</h3>
            <div className="mt-2">
              <SurfaceVariantsDemo />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Card slot family — CardHeader (badges + trailing CardActions) / CardBody / CardFooter (divided,
              CardActions)
            </h3>
            <div className="mt-2 max-w-lg">
              <CardSlotFamilyDemo />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground">MetricCard — composed of Card</h3>
            <div className="mt-2">
              <MetricCardDemo />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground">
              EntityCard — states (long-content / loading / error / locked / responsive)
            </h3>
            <div className="mt-2">
              <EntityCardStatesDemo />
            </div>
          </div>
        </div>
      </GallerySection>

      <GallerySection
        title="Collection compositions"
        hint={
          "Story 2-147tb, P10 §2.7 row 7. `Table`/`CompactRow`/`CompactRowList`/`DataList`/`FilterBar`/" +
          "`SegmentedControl` (each demoed in their own Section above) were already canonical per 2-147o's " +
          "own matrix — this story's real, named gap was 'Pagination' and 'result-count text' alone " +
          "('no dedicated Pagination component exists... result-count text is ad hoc per page today'). " +
          "`LoadMorePager` closes both: one component, replacing seven independently hand-rolled copies of " +
          "the identical 'Load more' + aria-live 'Showing N of M' footer (SitesPage/ReportsListPage/" +
          "TemplateLibraryPage/ItemsListPage/ActivityPage/ReviewQueuePage/InspectionTypesPage — all seven " +
          "migrated onto it this story, see SESSION-HANDOFF.md). The worked example below composes every " +
          "converged piece over one collection: FilterBar (keyword + status), SegmentedControl (view mode), " +
          "CompactRow/CompactRowList with RowActions per row (2-139's 'operational work' rows shape — " +
          "one identity, a status pill, a meta line, row actions, never a raw Button), and LoadMorePager. " +
          "Switching the SegmentedControl to Cards renders the SAME records as 2-147ta's Card family — proof " +
          "the two families agree rather than contradict: these records carry no wrapping description/chips/" +
          "imagery, so Rows is 2-139's own correct default here, Cards is offered only to show the two " +
          "families interoperate over identical data, not because this content justifies a second view."
        }
      >
        <div className="space-y-8">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              LoadMorePager — idle / loading / exhausted (no dedicated Pagination component existed before this story)
            </h3>
            <div className="mt-2">
              <LoadMorePagerStatesDemo />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Worked example — FilterBar + SegmentedControl + CompactRow/RowActions (list) or Card (cards) + LoadMorePager
            </h3>
            <div className="mt-2">
              <CollectionCompositionDemo />
            </div>
          </div>
        </div>
      </GallerySection>

      <GallerySection
        title="Forms and feedback compositions"
        hint={
          "Story 2-147ua, P10 §2.3/§2.7 row 3, DOD §4 G-26. Extends the existing G-26 " +
          "`Form`/`FormField`/`FormError`/`FormSubmit` primitive (see the 'Forms' Section above for the " +
          "static/dynamic regime proof) — this story converges the FEEDBACK half onto it rather than " +
          "building a second form system, per its own explicit instruction. Most of what its own summary " +
          "names (banners, callouts, skeletons, empty/error/locked/upgrade states) were already canonical " +
          "per 2-147o's matrix and already gallery-demonstrated in their own Sections above — EmptyState " +
          "('Empty state & loading'), Skeleton (same Section), Callout ('Callout'), SaveStateIndicator " +
          "(below), PermissionGate/RouteDenied and FlagGate/PlanTeaser/QuotaTeaser (the access-gate " +
          "Sections) — cited here, not rebuilt. The real, narrower gap: a STRUCTURAL fix for a crowding " +
          "class direct user feedback found and hand-fixed twice before this story existed " +
          "(`SectionNav.tsx`'s row count beside its label, `TemplatePublishBar.tsx`'s status description " +
          "beside its chip) — now baked into `FormField`/`FormError` themselves (`min-w-0` on every field " +
          "wrapper so a long label/description/error can never force a flex/grid sibling to shrink, " +
          "`break-words` so an unbroken long token wraps instead of overflowing) — plus widening " +
          "`FormActions` (2-147r) adoption from its one proof-of-concept consumer to four more real forms " +
          "(`SubjectForm`, `InspectionTypeForm`, `ReassignInspectionDialog`, `RequestChangesDialog` — each " +
          "previously hand-rolling its own Cancel/Submit `<div>`)."
        }
      >
        <div className="space-y-8">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Content-stress proof — long label, always-invalid long validation message, at 390/768/1024px
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              The control and the Cancel/Save row hold their own width at every frame — neither compresses
              to make room for the wrapping label/error text above them.
            </p>
            <div className="mt-2">
              <FormCrowdingStressDemo />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Loading / error / locked / loaded — one form, the four states it actually renders through
            </h3>
            <div className="mt-2">
              <FormsFeedbackStatesDemo />
            </div>
          </div>
        </div>
      </GallerySection>

      <Section
        title="SaveStateIndicator"
        hint={
          'Extracted from two real, independently-evolved consumers — `TemplateEditorPage.tsx` (story 4-2d) ' +
          'and `CaptureHeader.tsx` (stories 5-10b/5-10d) — each hand-typing an almost-identical `role=status ' +
          'aria-live=polite data-state={kind}` block (story 2-141). `SaveStateKind` is the UNION of both real ' +
          'autosave hooks’ own `SaveState` types and nothing more — `idle | saving | saved | queued | ' +
          'conflict | failed`. There is no `offline` member: backlog finding 5-29 (no outbox this release), ' +
          'and `useAnswerAutosave.ts`’s own header comment is explicit that `queued` is deliberately NOT ' +
          'named offline (“the ops are held, not synced… DefectSnap is not fully offline yet”). The ' +
          'copy stays 100% caller-supplied (a revision number here, a change count there); this component owns ' +
          'only the ARIA contract and the per-kind icon/tone (WCAG 1.4.1).'
        }
      >
        <Stack gap={3}>
          <SaveStateIndicator kind="idle" data-testid="savestate-idle">Nothing to save</SaveStateIndicator>
          <SaveStateIndicator kind="saving" data-testid="savestate-saving">Saving…</SaveStateIndicator>
          <SaveStateIndicator kind="saved" data-testid="savestate-saved">Saved 2 minutes ago · revision 4</SaveStateIndicator>
          <SaveStateIndicator kind="queued" data-testid="savestate-queued">2 changes waiting to save</SaveStateIndicator>
          <SaveStateIndicator kind="conflict" data-testid="savestate-conflict">
            1 answer needs your decision — someone else changed it too
          </SaveStateIndicator>
          <SaveStateIndicator kind="failed" data-testid="savestate-failed">Not saved — the server refused this write</SaveStateIndicator>
        </Stack>
      </Section>

      <Section
        title="AgentPlanCard"
        hint={
          'The agent workspace’s proposed-plan/approval card (story 2-144, P10 §2.4) — EXTRACTED from ' +
          '`AgentPanel.tsx`’s own hand-rolled approval markup (“the existing approval markup IS the ' +
          'specification”) rather than designed fresh, the same position `StatusStepper` shipped from before a ' +
          'second consumer existed. No domain imports, no gateway calls of its own — `decide`/`resolveApproval`/ ' +
          '`honestApprovalOutcome` all stay in `AgentPanel.tsx`; this card only renders the headline, the ' +
          'batched-plan step list (with the friendly `$N.path` step-reference phrasing — the raw token stays a ' +
          'tooltip only), the flat single-tool arg list, and the pending-vs-resolved footer. Pending renders real ' +
          '`Button`s (not a hand-rolled `btn-glass` pair); a resolved outcome renders a `StatusChip` (WCAG 1.4.1 ' +
          '— icon + label, never colour alone) alongside the honest headline sentence, with any raw error demoted ' +
          'to a collapsible “Technical detail”. Approve/Decline below are live — click through a real ' +
          'pending → decided transition.'
        }
      >
        <AgentPlanCardDemo />
      </Section>

      <Section
        title="TeamTree"
        hint={
          'The org/role hierarchy primitive (story 2-146d, P10 §2.2/§2.4) — a nested checkbox tree ' +
          '(region→branch→team) built from the flat `{id, name, kind, parentTeamId}` shape `lib/api/access.gen.ts` ' +
          'already returns. Every ancestor’s checked/indeterminate state is DERIVED from its descendant leaves, ' +
          'never stored, so it cannot drift from what is actually checked — check a team under a branch and the ' +
          'branch (and its region) render indeterminate immediately. Keyboard: every checkbox is a real, always- ' +
          'focusable `<input>` (Tab reaches every row), plus ArrowUp/ArrowDown/Home/End move a roving-tabindex ' +
          '“active” row across the whole tree, the WAI-ARIA treeview pattern. Long names (2-137’s content rule) ' +
          'truncate on a `min-w-0` flex child rather than overflowing the row, with the full name still reachable ' +
          'via `title`. `selectionMode="single"` is OrgAccessPage’s own parent-team-picker mode — same component, ' +
          'exclusive selection instead of a cascading multi-select.'
        }
      >
        <TeamTreeDemo />
      </Section>

      <Section
        title="Overflow menu (OverflowMenu)"
        hint="Story 2-137, P10 §2. A region normally has ONE primary action; secondary actions stay quiet; infrequent actions collapse into an overflow menu rather than wrapping onto a second row. Built on AnchoredPanel (the existing anchored-popover primitive — portal, viewport-flip, scroll-track, Esc/backdrop-close, focus return), not a second positioning mechanism: AnchoredPanel gained one new `role` prop (defaulting to `dialog`, unchanged for every prior consumer) so this primitive's panel can render as a real `role=&quot;menu&quot;` of `role=&quot;menuitem&quot;` rows instead. Roving tabindex + arrow keys (Home/End to the ends) move between enabled items; Tab closes the menu rather than trapping focus, since AnchoredPanel never traps Tab; a disabled action stays visible with its reason instead of disappearing. Story 2-147g anatomy note: OverflowMenu, like AnchoredPanel underneath it, is content-agnostic and never carries a footer action row — a menu's rows ARE its actions; a separate sticky action row belongs in a Modal, never a popover."
      >
        <OverflowMenuDemo />
      </Section>

      <GallerySection
        title="Navigation and disclosure compositions"
        hint={
          'Story 2-147ub, P10 §2.5/§2.7. Converges the seven navigation/disclosure surfaces this story ' +
          'names onto the primitives already demonstrated above and 2-147l\'s own Drawer decision — no new ' +
          'component was built. Each rule states the EXACT desktop-vs-mobile change (or explicit "no ' +
          'change"), grounded in the real source read for this story rather than left to per-page judgment, ' +
          'matching §2.4\'s card-vs-row and §2.5\'s side-sheet-vs-modal written-test discipline. The full ' +
          'prose for each rule (with its own citations) lives in P10 §2.5; this Section is the ' +
          'gallery-demonstrated half of the same rule.'
        }
      >
        <div className="space-y-6">
          <Grid as="dl" columns={{ base: 1, sm: 2 }} gap={6}>
            <div>
              <dt className="font-semibold text-foreground">1 — Sidebar rows (Sidebar.tsx)</dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                Desktop/tablet: <code>expanded</code> (icons+labels) or <code>collapsed</code> (icons-only,
                tablet default) by explicit user toggle — <code>data-shell-nav</code>, P10 §1.1, demoed in the
                "Shell geometry &amp; state tokens" Section above. Below <code>sm</code>:{' '}
                <code>overlay</code> — the same off-canvas drawer (<code>-translate-x-full sm:translate-x-0</code>)
                opened by TopBar's hamburger, not a second nav shape. Real-viewport (<code>sm:</code>)
                driven — verify by resizing the actual browser window, not a <code>ResponsivePreview</code>{' '}
                frame (rule 7 below explains why that frame can't trigger it).
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">2 — Breadcrumbs (Breadcrumbs.tsx)</dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                Identical component/props at every width — no truncation, no middle-collapse. The trail
                wraps onto extra lines (<code>flex flex-wrap</code>) rather than clipping, inside TopBar's{' '}
                <code>min-w-0</code> identity column so it never steals width from the fixed-width icon
                cluster. Wrapping is driven by the container's own width, so the{' '}
                <code>ResponsivePreview</code> demo below genuinely triggers it (stress-length crumb, all
                three simulated widths).
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">3 — Tabs (Tabs.tsx)</dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                Identical component/props at every width. The tablist scrolls horizontally (
                <code>overflow-x-auto</code>, each tab <code>shrink-0</code> unless <code>fill</code>) rather
                than collapsing into a <code>&lt;select&gt;</code> or wrapping — story 2-147c. No second
                "mobile tabs" shape exists — a select-based fallback would break the WAI-ARIA tablist
                contract at exactly the width it's needed most. Demonstrated below with six long-labeled
                tabs at all three widths.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">4 — Overflow menus (OverflowMenu.tsx)</dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                Does not itself change at mobile — it IS the disclosure mechanism the six action hosts
                (2-147r, "Action hosts" Section above) fall back to once their own <code>maxVisible</code>{' '}
                budget is exceeded, a prop-driven collapse orthogonal to viewport width (can trigger on a
                wide desktop toolbar with many actions, or never trigger on a two-action mobile row).{' '}
                <code>OverflowMenu</code>'s own render — trigger, panel, keyboard contract — is
                breakpoint-agnostic everywhere. See the "Overflow menu" Section above.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">5 — Dialogs (Modal placement="center")</dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                Identical at every width — centered panel, backdrop scrim, <code>size</code> caps max-width
                only. No repositioning at any breakpoint: a dialog is a dialog everywhere. See the
                "Overlays" Section above.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">
                6 — Drawers / sheets (Modal placement="right" / "bottom")
              </dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                <strong className="text-foreground">This is 2-147l's Drawer</strong> —{' '}
                <code>placement="right"</code>, reused verbatim, not reinvented. Side sheet: right-anchored
                at every width, capped width + left-rounded corners at <code>sm</code>+, edge-to-edge (still
                right-anchored, never repositions) below it. Bottom sheet: pinned to the bottom edge with a
                grab handle below <code>sm</code>, RECENTERS to a normal centered dialog at <code>sm</code>+
                — the one placement that repositions across the breakpoint. See the "Overlays" Section above
                (narrow the real browser window below 640px to see both dock and recentre).
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">7 — Inspectors (RailPanel)</dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                <code>RailPanel</code> itself never changes shape at any width — no responsive className
                exists in <code>RailPanel.tsx</code>. The responsive change lives one level up, in{' '}
                <code>WorkflowLayout</code>'s <code>Split</code>-based panel slot (
                <code>lg:grid-cols-split-*</code>): stacks BELOW the record content in a single column below{' '}
                <code>lg</code>, a genuine second column at <code>lg</code>+ (<code>ReportDetailPage.tsx</code>
                's own <code>grid gap-5 lg:grid-cols-[1fr,320px]</code>). Real-<code>lg:</code>-driven, same
                as rule 1 — the "Grid" Section above already documents why a fixed-width{' '}
                <code>ResponsivePreview</code> frame cannot trigger a Tailwind <code>lg:</code> rule (it
                reads the real browser viewport, not the frame's own width), so this is verified by resize,
                not simulated here. Deliberately NOT a bottom sheet or a second drawer at narrow width — §2.5
                reserves a permanent inspector for "continuous workspace context a person consults WHILE
                working the main content"; collapsing it into a dismissable overlay at mobile would
                contradict the exact reason it is a permanent inspector rather than a side sheet.
              </dd>
            </div>
          </Grid>

          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              2 — Breadcrumbs: a stress-length crumb wraps, never clips or truncates
            </p>
            <ResponsivePreview>
              <Breadcrumbs
                items={[
                  { label: 'Dashboard', to: '/app', current: false },
                  { label: 'Reports', to: '/app/reports', current: false },
                  { label: CONTENT_STRESS_CASES[0]!.title, current: true },
                ]}
              />
            </ResponsivePreview>
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              3 — Tabs: six long-labeled tabs scroll horizontally rather than clip or wrap
            </p>
            <ResponsivePreview>
              <NavDisclosureTabsStressDemo />
            </ResponsivePreview>
          </div>

          <p className="border-t border-border pt-4 text-sm text-muted-foreground">
            <strong className="text-foreground">Drawer precedent, reused.</strong> Every dialog/drawer/sheet/
            side-panel need this story surveyed resolves to one of <code>Modal</code>'s three existing
            placements (rules 5–6 above) or to <code>RailPanel</code> staying inline (rule 7) — no new
            overlay component was built, and none was found necessary. <code>2-147l</code>'s finding stands:{' '}
            <code>Modal placement="right"</code> is the Drawer, full stop.
          </p>
        </div>
      </GallerySection>

      <Section
        title="Missing value"
        hint="Story 2-137, P10 §2. The one explicit rendering for &quot;this field has no value&quot; — an em dash, not the word 'None'/'N/A' (those read as an answer; the dash reads as a placeholder nothing filled in), with an accessible name that names the FIELD ('Due date not provided') so several missing values in a row don't announce as an indistinguishable string of dashes. Composed by `DataList` (story 2-138) rather than reimplemented there."
      >
        <div className="max-w-sm space-y-1">
          {MISSING_VALUE_FIELDS.map((f) => (
            <Cluster
              key={f.key}
              gap={3}
              justify="between"
              className="border-b border-border/60 py-2 last:border-b-0"
            >
              <span className="min-w-0 text-sm text-muted-foreground">{f.label}</span>
              <MissingValue label={f.label} />
            </Cluster>
          ))}
        </div>
      </Section>

      <Section
        title="Data list (DataList)"
        hint="Story 2-138, P10 §2.4. Pure label/value presentation — a real `dl`, no fetching, no module knowledge, no domain nouns. Composes MissingValue (story 2-137) for an absent value and CopyField (story 6-1a, `showLabel={false}` — DataList's own `dt` is the visible label) for a `copyable` item, rather than reimplementing either. `density` ('comfortable'/'compact') and `columns` (1-3, `sm:` and up — always a single stacked column below that) are spacing/layout only, reusing 2-136's type scale."
      >
        <div className="space-y-6">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Comfortable · 2 columns · present, missing and copyable values
            </p>
            <DataList
              density="comfortable"
              columns={2}
              data-testid="ds-datalist-comfortable"
              items={[
                { label: 'Type', value: 'E2E Canonical Inspection Type' },
                { label: 'Inspection status', value: 'completed' },
                { label: 'Due date', value: undefined },
                { label: 'Assigned to', value: null },
                { label: 'Report link', value: CONTENT_STRESS_CASES[0]!.url, copyable: true },
                { label: 'Report id', value: CONTENT_STRESS_CASES[0]!.identifier, breakAll: true },
              ]}
            />
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Compact · 1 column · a rail-width summary
            </p>
            <div className="max-w-xs">
              <DataList
                density="compact"
                columns={1}
                data-testid="ds-datalist-compact"
                items={MISSING_VALUE_FIELDS.map((f) => ({ key: f.key, label: f.label, value: undefined }))}
              />
            </div>
          </div>
        </div>
      </Section>

      <Section
        title="Content-stress matrix"
        hint="Story 2-137, P10 §2 — the canonical fixture (frontend/src/lib/contentStressMatrix.ts; story 2-134 adopts it as the shared e2e fixture) for the content rules this story establishes: long titles/descriptions, addresses, unbroken identifiers (`break-all`, not plain prose wrapping), URLs (never raw — always through CopyField) and localization expansion (German compound nouns run 30-200% longer than the English source). Every text-bearing flex/grid child below carries `min-w-0`. Verified at 1920/1440/1024/768/390px and 200% zoom with no clipped/unreachable content and no page-level horizontal overflow — the dashed 390px box re-renders the same content bounded to that exact width so the claim is visible here, not only asserted."
      >
        <ContentStressMatrixGallery />
      </Section>

      <Section
        title="Promotion candidates"
        hint="Module-local components watched under the rule of three (P18 §6) — reviewed at each wave boundary, promoted here when reuse appears."
      >
        <Table aria-label="Promotion candidates">
          <THead>
            <TR>
              <TH>Component</TH>
              <TH>Lives in</TH>
              <TH>Promotion note</TH>
            </TR>
          </THead>
          <TBody>
            {PROMOTION_CANDIDATES.map((c) => (
              <TR key={c.name}>
                <TD className="font-semibold">{c.name}</TD>
                <TD><code className="text-xs text-muted-foreground">{c.home}</code></TD>
                <TD className="text-muted-foreground">{c.note}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Section>

      <GallerySection
        title="Gallery scaffolding (self-hosting proof)"
        hint={
          'Story 2-147p, P10 §4. The gallery\'s OWN presentation layer — GalleryPage (this page\'s own outer ' +
          'wrapper, replacing the previously hand-built <div className="space-y-6"><header>…), GallerySection ' +
          '(this box), ExampleFrame, VariantGrid, StateMatrix and ResponsivePreview ' +
          '(frontend/src/components/gallery/) — demonstrated together against one real, already-canonical ' +
          'primitive (Button) as the worked proof this story\'s own done-when requires, rather than shipped as ' +
          'six components with no real consumer. StateMatrix below renders the exact five-state set P10 §2.6/' +
          '§4.2 name (long-content/loading/error/locked/responsive), built from LAYOUT_FAMILY_STATE_SET\'s own ' +
          'canonical order. Not in scope (2-147vb\'s job): migrating the page\'s other 57 Section()s onto this ' +
          'scaffolding — those keep using the pre-existing local Section() helper above, unchanged.'
        }
      >
        <GalleryScaffoldingProofDemo />
      </GallerySection>

      <GallerySection
        title="Unified interactive-control geometry"
        hint={
          'Story 2-147qa, P10 §1.1/§2.7. Button, IconButton, Input/Select and SegmentedControl converged onto ' +
          'ONE height/density scale (tokens.css --control-h-sm/--control-h-md, the latter an alias of the ' +
          'existing --shell-control-h, via h-control-sm/h-control) instead of each deriving its own height ' +
          "from padding + line-height — 2-147n's census found four different rendered heights across these " +
          "controls at HEAD (Button 28/36px, IconButton 36px, Input/Select ~24/28px, SegmentedControl one " +
          'hardcoded ~32px with no size prop). Grep-able proof: frontend/scripts/check-control-geometry.mjs ' +
          '(same allowlist-scan pattern as check-shell-geometry.mjs). Loading now has one shared contract ' +
          '(disable + aria-busy + spinner in the icon/adornment slot where one exists) across Button, ' +
          'IconButton and Input; Select documents the one real platform exception (no spinner glyph — a ' +
          'native <select> has no child-content slot to put one in). OverflowMenu (the menu-trigger) and ' +
          'SlotGridPicker/DirectionalPad (the spatial-grid picker family) were investigated and are cited, ' +
          'not re-built — see this Section\'s own demo component header.'
        }
      >
        <ControlGeometryProofDemo />
      </GallerySection>

      <GallerySection
        title="Typography, iconography and state primitives"
        hint={
          'Story 2-147qb, P10 §2.7 rows 1/4. Typography/icons/dividers have NO wrapper primitive by §2.7\'s own ' +
          'citation — Tailwind\'s stock scale, @heroicons/react directly, border-border(-strong)/divide-y — so ' +
          'converging them meant finding and fixing nine real call sites hand-rolling their own arbitrary ' +
          'tracking value (0.12em/0.18em/0.2em) beside 19 already-correct tracking-eyebrow consumers, not building a new component (that would ' +
          'reopen a tie §2.7 already closed). Badge/StatusChip/StatusStepper/ProgressMeter/SaveStateIndicator were ' +
          'already the canonical row-4 primitives (stories 2-141/2-146b converged status presentation earlier in ' +
          'this chain) — this story\'s real find was five independent hand-rolled `rounded-full` spans reproducing ' +
          "Badge's own visual signature with no radius option to reach for; three converged onto a new " +
          'radius="pill" (AgentPanel/TemplateCard/TemplateEditorPage), two left as documented formBuilder ' +
          'specialist-renderer debt. ProgressMeter gained a `tone` override for the one real inverted-semantics ' +
          "case found (UsageMeter.tsx's quota gauge, dead code with zero consumers, converged anyway since a " +
          'tree-wide grep does not care about the import graph). Grep-able proof: ' +
          'frontend/scripts/check-typography-state-convergence.mjs (eyebrow-tracking + raw-<svg> icon-system + ' +
          'Badge-pill positive-proof, same allowlist-scan pattern as check-shell/control-geometry.mjs).'
        }
      >
        <TypographyStatePrimitivesProofDemo />
      </GallerySection>

      <GallerySection
        title="Semantic actions and governed action hosts"
        hint={
          'Story 2-147r, P10 §2.7 row 2. Six action-host components — HeaderActions, CardActions, ' +
          'RowActions, ToolbarActions, FormActions, DialogActions (components/ui/ActionHosts.tsx) — built ' +
          "over 2-147qa's converged Button/IconButton control-height contract. A caller supplies an " +
          'ActionDescriptor[] (WHAT an action is — label, handler, primary/secondary/ghost/destructive per ' +
          "P10 §1.3/story 2-137's own action-hierarchy vocabulary) and the host decides HOW it renders/" +
          'groups/collapses — product code no longer picks variant, size or wrapping itself. `variant` ' +
          "defaults to `'secondary'` (not Button's own `'primary'` default) so an unlabelled action never " +
          "silently becomes a second primary in one region. Overflow is a FIXED maxVisible budget per host " +
          "(RowActions=2, HeaderActions/CardActions/FormActions/DialogActions=3, ToolbarActions=4) — " +
          'exceeding it collapses trailing, non-`pinned` actions into a real `OverflowMenu` (story 2-137), ' +
          "not a second popover mechanism. Zero ReactNode escape hatches on any of the six — see " +
          'ActionHosts.tsx\'s own header comment for the full contract. Six real proof-of-concept wirings ' +
          '(not a page migration — 2-147w* does that): HeaderActions → OrgAccessPage\'s Invite member/' +
          'Convert to organization header actions; CardActions → AgentPlanCard\'s Approve/Decline footer; ' +
          "RowActions → ShareLinksPanel's per-row Revoke; ToolbarActions → ReportDetailPage's Download PDF/" +
          'Regenerate/Print/Open inspection row (the exact toolbar shape PageHeader.tsx\'s own header ' +
          'comment already named); FormActions → CreateShareLinkDialog\'s Cancel/Create link row; ' +
          "DialogActions → ShareLinksPanel's RevokeConfirmDialog footer (composes WITH ModalFooter, not " +
          'instead of it). Grep-able proof: frontend/scripts/check-action-host-governance.mjs.'
        }
      >
        <ActionHostsProofDemo />
      </GallerySection>

      <GallerySection
        title="Layout composition primitives"
        hint={
          'Story 2-147sa, P10 §2.6/§2.7 row 6. Seven internal layout primitives (components/ui/Layout.tsx) — ' +
          'Container, Stack, Inline, Cluster, Grid, Split, Section — the closed set every layout/page/composite ' +
          "in this chain builds from from this point forward (2-147sb's six named page layouts, next, are built " +
          'directly on top of these; 2-147xb starts rejecting new bespoke flex/grid layout decisions made outside ' +
          "this file). Divider is deliberately NOT an eighth primitive here — P10 §2.7 row 1 already settled that " +
          'responsibility twice (2-147o\'s own contract, independently re-confirmed by 2-147qb\'s measurement) as ' +
          '"applied directly" (border-border / border-border-strong / divide-y) — building a wrapper would reopen ' +
          "a tie this lane already closed. Inline vs Cluster is grounded in real usage, not guessed: Inline is a " +
          "single unbreakable pair that never wraps (Breadcrumbs.tsx's per-crumb icon+label, " +
          'SaveStateIndicator.tsx); Cluster is a group of independent items that wraps as a whole once the row ' +
          "runs out of space (PageHeader.tsx's chips/actions/toolbar rows, BulkActionBar.tsx). Every gap/space " +
          "value is Tailwind's own spacing scale, spent only via static className lookup maps (never " +
          "`gap-${n}` interpolation, which Tailwind's static scanner cannot see) — the same real values already " +
          "in use tree-wide (PageHeader.tsx gap-2/gap-4, DashboardPage.tsx space-y-6/space-y-4, OrgAccessPage.tsx " +
          "gap-6). Container wraps 2-131's --content-reading/-operational/-fluid tokens verbatim, read via style " +
          "(never restated — the check-shell-geometry.mjs 'reading a token is not a violation' carve-out). " +
          "Split's ratio vocabulary is proportional (fr-unit, real named tailwind.config.ts grid-cols-split-* " +
          "utilities, never an arbitrary-value grid-cols-[...] bracket) — deliberately not a fixed pixel/rem " +
          "secondary-pane width, since no --panel-w token exists yet (P10 §2.6's own named gap, not closed here). " +
          "Section formalizes P10 §2.6's bare-<h2> 'Section header' kind (#5) — no card/slot styling of its own, " +
          "that's 2-147ta's separately-named Surface/Card gap. Zero new arbitrary-value geometry: " +
          'check-shell-geometry.mjs / check-no-arbitrary-token-values.mjs both re-run clean after this file was ' +
          'added.'
        }
      >
        <LayoutPrimitivesProofDemo />
      </GallerySection>
    </GalleryPage>
  );
}
